/**
 * 체리쉬 컵 — 토너먼트 페이지 (2026-09-18).
 *
 * 배틀홈 배너를 누르면 열리는 **방**이다. 상시 탭으로 두지 않는다 — 상단 탭바는 4칸이
 * 한계라(patch-cherish-tabs.py) 다섯 번째 고정 탭은 모바일에서 잘린다. 대회가 열릴 때만
 * 배너가 뜨고, 누르면 이 방이 생기고, 닫을 수 있다.
 *
 * 화면은 **두 벌, 데이터는 한 벌**이다:
 *   PC     — 아래에서 위로 올라가는 세로 대진표 (CSS grid)
 *   모바일 — 라운드 단위 세로 피드 (상단 칩으로 라운드 이동, 내 경기 최상단 고정)
 * 브래킷을 축소해 폰에 넣으면 글자가 6px 이 된다. 줄이는 게 아니라 다시 구성한다.
 *
 * B Midnight Arena: 배경·트로피·기존 해설자 원화를 독립 자산으로 배치한다.
 * 대진표·이름·해설은 실제 DOM 이며 이미지에 합성하지 않는다.
 * 이 테마는 가독성을 위해 고정 팔레트를 사용한다(회차 palette 는 이 패널에 적용하지 않음).
 *
 * ⚠️ `async/await` 를 쓰지 않는다 (이 포크 클라 규약). fetch 는 `.then()` 체인으로.
 * ⚠️ 시드 번호·관전 인원수·상대 라인업은 서버가 아예 안 내려준다. 화면에서도 만들지 말 것.
 */
import preact from "../js/lib/preact";
import { PS, PSRoom, type RoomID, type RoomOptions } from "./client-main";
import { PSPanelWrapper, PSRoomPanel } from "./panels";
import { BattleSound, type BattleBGM } from "./battle-sound";
import { CwlRouletteBox, CWROUL_CSS, type CwlRoulette, type CwlHop } from "./cherish-roulette";

const CWT_API = 'https://cherish-world.com/api/showdown/tournament';

/** 대회가 도는 동안은 초 단위로 본다. 라운드 전환·호출 카운트다운이 여기에 실린다. */
const CWT_POLL_RUNNING = 2000;
/** 접수 중·종료 뒤에는 느리게. */
const CWT_POLL_IDLE = 6000;
/**
 * **열린 대회가 없을 때.** 이 화면이 사는 시간의 대부분이 평시이고, 그때 트래픽이 0에
 * 가까워야 한다 (2026-09-19 — 대회가 하나도 없는데 분당 124회가 나갔다).
 */
const CWT_POLL_NONE = 60000;
/** 내 상태(`/me`)는 대부분 **내 조작**으로만 바뀐다 — 조작한 쪽에서 직접 다시 부른다. */
const CWT_ME_POLL = 10000;
/** 공개 연출: 대회명이 뜨고 첫 카드가 나오기까지. */
const CWT_REVEAL_LEAD_MS = 2500;
/** 담임 얼굴·이름 — 배틀 패널(복기 코치 말풍선)이 쓰는 것과 **같은 자산**을 가리킨다. */
/** 사건 이름 — 대진표·명장면에 그대로 쓴다. */
const CWT_EVENT_KO: { [key: string]: string } = {
	crit: '급소',
	super: '효과가 굉장했다',
	faint: '쓰러짐',
	'last-stand': '막판',
	comeback: '뒤집힘',
};

const CWT_TEACHERS: { [key: string]: { name: string, img: string, portrait: string, role: string } } = {
	cherry: { name: '체리', img: '/cherish/ui/coach-cherry.png?v=3', portrait: '/cherish/tour/midnight/cherry.png', role: '대회 진행' },
	berry: { name: '베리', img: '/cherish/ui/coach-berry.png?v=2', portrait: '/cherish/tour/midnight/berry.png', role: '선수 응원' },
	lychee: { name: '리치', img: '/cherish/ui/coach-lychee.png?v=1', portrait: '/cherish/tour/midnight/lychee.png', role: '경기 분석' },
};
type CwtRevealCue = 'entrance' | 'land' | 'complete' | 'spin' | 'select' | 'hop' | 'rank' | 'win' | 'lose' | 'count' | 'go';
/** 모든 대회에 공통으로 쓰는 확정 효과음. 등록 상태나 대회 설정에 의존하지 않는다. */
const CWT_REVEAL_SOUNDS: Record<CwtRevealCue, string> = {
	entrance: '/audio/cherish/tournament-reveal-entrance-e3-v1.mp3',
	land: '/audio/cherish/tournament-reveal-land-l1-quiet-v1.mp3',
	complete: '/audio/cherish/tournament-reveal-complete-c13-v1.mp3',
	spin: '/audio/cherish/tournament-roulette-spin-v2.mp3',
	select: '/audio/cherish/tournament-roulette-select-v1.mp3',
	hop: '/audio/cherish/tournament-roulette-hop-v1.mp3',
	rank: '/audio/cherish/tournament-roulette-rank-v1.mp3',
	win: '/audio/cherish/tournament-roulette-win-v1.mp3',
	lose: '/audio/cherish/tournament-roulette-lose-v1.mp3',
	/** 룰렛 마감 뒤 3·2·1 (`build-tournament-countdown.py` 합성) · 0 = 멈추기 시작. */
	count: '/audio/cherish/tournament-roulette-count-v1.mp3',
	go: '/audio/cherish/tournament-roulette-go-v1.mp3',

};
/** 공개 연출: 마지막 카드 뒤 vs 점화 + 카운트다운에 남겨두는 시간. */
const CWT_REVEAL_TAIL_MS = 4500;
/** 같은 페이지에서 결과 탭을 다시 열어도 시상 효과를 반복하지 않는다. */
const CWT_PRESENTED_AWARDS = new Set<string>();

/** 받침 유무로 조사를 고른다 — 한글이 아니면(영문 별명 등) 받침 없는 쪽. */
function cwsJosa(word: string, withFinal: string, without: string): string {
	const ch = word.charCodeAt(word.length - 1);
	if (ch < 0xac00 || ch > 0xd7a3) return without;
	return (ch - 0xac00) % 28 ? withFinal : without;
}

interface CwtCard {
	entrantId: string;
	name: string;
	avatar: string | null;
	/** 공개 입장과 우승 팝업의 전신 아바타. 익명 참가자는 null. */
	body?: string | null;
	/** 공개 입장 전용 지정 파트너. 비공개 출전 명단과 별개다. */
	partner?: { name: string; sprite: string; role: 'partner' | 'signature' } | null;
	anonymous: boolean;
	usedMons: string[] | null;
	stamps: number;
	/** 새싹컵 — 이 선수가 데려온 아이 (매 판 반드시 나간다). 뽑기가 공개로 진행돼 감출 정보가 아니다. */
	borrowed?: { name: string; sprite: string; lender: string } | null;
}
/** 새싹컵 뽑기판의 아이 카드. 능력치 숫자는 서버가 안 보낸다. */
interface CwsMon {
	id: string;
	name: string;
	speciesKo: string;
	speciesId: string;
	sprite: string;
	shiny: boolean;
	typesKo: string[];
	abilityKo: string;
	itemKo: string | null;
	movesKo: string[];
	inTeam: boolean;
}
/** 새싹컵 뽑기판 (마감 뒤 빌려주기·뽑기 동안만). 모두에게 같은 내용이다. */
/** 마왕토벌전 (2026-10-12) — 서버 `RaidBoardView`. 두 팀 명단·이월 상태·지금 판·창 시계. */
interface CwrMon { name: string | null; sprite: string | null; hp: number; status: string | null; fainted: boolean; seen: boolean }
interface CwrCard {
	entrantId: string;
	name: string;
	avatar: string | null;
	anonymous: boolean;
	alive: boolean;
	leader: boolean;
	votes: number;
	hand: boolean;
	wins: number;
	standing: boolean;
	mons: CwrMon[];
	monsLeft: number;
}
interface CwrBoard {
	phase: 'reveal' | 'vote' | 'pick' | 'flip' | 'battle' | 'done';
	secLeft: number | null;
	voteSec: number;
	pickSec: number;
	flipSec: number;
	battleNo: number;
	winner: 'boss' | 'raider' | null;
	standing: { boss: string | null; raider: string | null };
	next: { boss: string | null; raider: string | null };
	bosses: CwrCard[];
	raiders: CwrCard[];
	bossesLeft: number;
	raidersLeft: number;
	/** 회차 마릿수 (2026-11-01) — 마왕 3·토벌대 1 또는 마왕 6·토벌대 2. 옛 서버 응답에는 없다(3·1 로 본다). */
	sizes?: CwrSizes;
}
interface CwrSizes { boss: number; raider: number }
const CWR_SIZES_DEFAULT: CwrSizes = { boss: 3, raider: 1 };
/** 마릿수 한글 — 「세 마리」·「두 마리」. 화면 문구가 회차 마릿수를 따라간다. */
function cwrCountKo(n: number): string {
	return (['', '한', '두', '세', '네', '다섯', '여섯'][n] || String(n)) + ' 마리';
}
/** `/me` 의 마왕토벌전 칸 — 내 팀·리더·내 선택·같은 팀의 손든 아이 (서버 `RaidMeView`). */
interface CwrMe {
	team: 'boss' | 'raider';
	leader: boolean;
	vote: string | null;
	hand: string | null;
	pick: string | null;
	/** 2마리판 (2026-11-01) — 손든·고른 아이 전부(앞이 선봉). 옛 서버 응답에는 없다. */
	hands?: string[];
	picks?: string[];
	raiderSize?: number;
	needNext: boolean;
	next: string | null;
	fighter: boolean;
	/** 공개 단계에 내가 이번 판 주자인가 (2026-11-01) — 'boss' 면 선봉을 바꿀 수 있다. 옛 서버 응답에는 없다. */
	flipFighter?: 'boss' | 'raider' | null;
	mons: { id: string; name: string; sprite: string; hp: number; status: string | null; fainted: boolean }[];
	/** 지금 지명되면 나갈 내 아이 (고른 아이 → 손든 아이 → 서 있던 아이 → 첫 아이). */
	intent?: CwrIntent | null;
	/** 2마리판 — 지명되면 나갈 아이 전부(앞이 선봉). 옛 서버 응답에는 없다(`intent` 하나로 본다). */
	intents?: CwrIntent[];
	/** 같은 팀 — 출전 예정 아이(`intent`)와 손든 아이는 여기서만 보인다. */
	mates: { entrantId: string; name: string; alive: boolean; standing: boolean; hand: { id: string; name: string; sprite: string } | null; intent?: CwrIntent | null; intents?: CwrIntent[] }[];
}
interface CwrIntent { id: string; name: string; sprite: string; source: 'pick' | 'hand' | 'default' }
/** 빙글빙글컵 (2026-10-13) — 서버 `SwissBoardView`. 탈락 없는 순위표: 승·패·상대 전적(만난 상대들의 승수 합)·이번 라운드 상태. */
interface CwxRow {
	entrantId: string;
	name: string;
	avatar: string | null;
	anonymous: boolean;
	wins: number;
	losses: number;
	byes: number;
	/** 상대 승수 합 — 화면은 「상대 전적」. 같은 승수의 순서를 가른다. */
	opp: number;
	rank: number;
	/** 두 번 연속 무응답·강제 제외 — 더 안 뛴다 (순위표엔 남는다). */
	excluded: boolean;
	state: 'ready' | 'calling' | 'live' | 'done' | 'bye' | 'idle';
	opponent: string | null;
	won: boolean | null;
}
interface CwxBoard {
	rounds: number;
	round: number | null;
	final: boolean;
	finalFormat: 'six' | 'solo' | null;
	finalists: [string, string] | null;
	runnerUp: string | null;
	standings: CwxRow[];
}
const CWR_STATUS_KO: { [k: string]: string } = { par: '마비', brn: '화상', psn: '독', tox: '맹독', slp: '잠듦', frz: '얼음' };
/** 선택·투표 창 동안 `/me` 를 더 자주 본다 — 리더의 지명·같은 팀의 손들기가 2초 안에 보여야 창이 쓸모 있다. */
const CWR_ME_POLL_FAST = 2500;

interface CwsBoard {
	phase: 'lend' | 'pick';
	secLeft: number | null;
	turnSec: number;
	lenders: { entrantId: string; name: string; avatar: string | null; anonymous: boolean; registered: boolean; mon: CwsMon | null; takenBy: string[] }[];
	players: { entrantId: string; name: string; avatar: string | null; anonymous: boolean; pickOrder: number; picked: string | null; out: boolean }[];
	turn: string | null;
	/** 도움 통화방 — 빌려준 사람에게 묻는 중인 요청 (2026-10-08). 옛 서버 응답에는 없다. */
	voiceAsks?: { playerId: string; lenderId: string; secLeft: number }[];
	/** 모두 골랐는데 도움 통화방 답을 기다리느라 대진 공개를 미루는 중 (2026-10-09). 옛 서버 응답에는 없다. */
	voiceWait?: boolean;
}
/** 도움 통화방 — `/me` 의 내 요청. 출전자에겐 결과까지, 빌려준 사람에겐 묻는 중인 것만. */
interface CwsVoiceMine {
	role: 'player' | 'lender';
	stage: 'player' | 'lender' | 'moving' | 'moved' | 'declined' | 'missed' | 'failed';
	name: string;
	secLeft: number;
}
interface CwtMatch {
	round: number;
	slot: number;
	state: string;
	p1: CwtCard | null;
	p2: CwtCard | null;
	winner: string | null;
	score: [number, number];
	roomId: string | null;
	callSecLeft: number | null;
	/** 진행 중인 판의 남은 마리 수 [p1, p2]. 관전 그림자가 없으면 null. */
	alive: [number, number] | null;
	lastStand: boolean;
	events: string[];
	/** 명장면 3선 — 끝난 칸에만. */
	highlights: { turn: number; events: string[]; hpLost: number }[] | null;
	/**
	 * 승부 예측 (4강부터, 2026-09-23). 창이 없던 칸은 null.
	 * 서버는 **배당 배수만** 준다 — 건 사람 수·금액은 안 온다. 화면에서도 만들지 말 것.
	 */
	bet?: { state: string; secLeft: number | null; odds: [number, number] | null } | null;
}
interface CwtView {
  resultBannerHidden?: boolean;
	/** 서버에 놓인 클라 번들 스탬프 (2026-10-14). 내 스크립트의 `?v=` 와 다르면 옛 번들이다 — 새로고침 띠. 옛 서버 응답에는 없다. */
	clientStamp?: string | null;
  /** `loop` 는 라운드 곡(2026-10-15). 없거나 false 면 한 번 재생. */
  bracketMusic?: { title: string; url: string; loop?: boolean } | null;
	id: string;
	title: string;
	status: string;
	/** 화면이 무엇을 그릴지는 `status` 가 아니라 이걸로 정한다 (서버가 굳힌 판정). */
	phase: string;
	/** 지금 국면이 끝나기까지 남은 초. 로컬 시계를 여기에 맞춘다. */
	phaseSecLeft: number | null;
	/** 공개 연출 총 길이(초). 카드 하나당 간격은 이 값과 인원으로 계산한다. */
	revealSec: number;
	/** 공개 연출 등장 순서(엔트런트 id) — 모든 화면이 같은 순서를 본다. */
	revealOrder: string[] | null;
	rehearsal: boolean;
	/** 이 대회가 전 유저의 자동매칭을 잠그고 있나 (2026-10-27) — 홈의 대회 카드가 본다. 옛 서버 응답에는 없다(안 그린다). */
	matchLocked?: boolean;
	bracketSize: number | null;
	currentRound: number | null;
	startsAt: string | null;
	signupCount: number | null;
	intermissionSecLeft: number | null;
	palette: string[] | null;
	matches: CwtMatch[];
	champion: CwtCard | null;
	talk: { teacher: string; text: string; moment: string; roomid: string | null; at: number }[];
	/** 지금 예측을 받는 칸 (한 번에 하나). 없으면 null. */
	betOpen?: { round: number; slot: number; secLeft: number } | null;
	/** 'sprout' = 새싹컵 · 'raid' = 마왕토벌전 · 'swiss' = 빙글빙글컵. 옛 서버 응답에는 없다(일반으로 본다). */
	mode?: 'normal' | 'sprout' | 'raid' | 'swiss';
	/** 새싹컵 뽑기판 — 마감 뒤 빌려주기(`lend`)·뽑기(`pick`) 동안만. */
	sprout?: CwsBoard | null;
	/** 마왕토벌전 판 — 진행·종료 동안만. 대진표 대신 이걸 그린다. */
	raid?: CwrBoard | null;
	/** 마왕토벌전 마릿수 (2026-11-01) — 접수 화면부터 문구가 이걸 본다. 마왕토벌전이 아니거나 옛 서버면 없다. */
	raidSizes?: CwrSizes | null;
	/** 빙글빙글컵 순위표 (2026-10-13) — 진행·종료 동안만. 대진표 대신 이걸 그린다. `matches` 는 이번 라운드 칸으로 같이 온다. */
	swiss?: CwxBoard | null;
	/** 모집 마감 뒤 운영진 검토 중 (2026-10-12) — 신청·취소가 닫혔다. 옛 서버 응답에는 없다(접수 중으로 본다). */
	signupClosed?: boolean;
	/** 종료 순위표 + 리본 (2026-10-14). 끝난 대회만. 옛 서버 응답에는 없다. */
	standings?: CwtStanding[] | null;
	/** 리본 적립이 확정됐나 (운영진 「정산 확정」). false 면 「확인 뒤 지급돼요」. */
	ribbonsSettled?: boolean | null;
	/** 행운의 자리 룰렛 — 룰렛을 켠 회차가 끝난 뒤에만. 마감 전엔 `result` 가 null. */
	roulette?: CwlRoulette | null;
}
/** 종료 순위표 한 줄. */
interface CwtStanding {
	entrantId: string;
	name: string;
	avatar: string | null;
	anonymous: boolean;
	isBot: boolean;
	rank: number | null;
	wins: number;
	ribbons: number;
}
// 룰렛 칸·카드·결과·상태 타입과 CWL_* 상수는 공용 cherish-roulette.tsx 로 옮겼다 (2026-11-02).
/** 리본 상점 응답 (`GET /showdown/ribbons`). */
interface CwlRibbons {
	wallet: { balance: number; earned: number };
	boxes: { tier: number; ko: string; price: number; desc: string }[];
	ledger: { delta: number; note: string; at: string }[];
	recentBoxes: { tier: number; ko: string; label: string | null; at: string }[];
}
interface CwtMyBet {
	round: number;
	slot: number;
	side: 'p1' | 'p2';
	amount: number;
	/** 'open' 정산 전 | 'won' 적중 | 'lost' 빗나감 | 'refunded' 환불 */
	status: string;
	payout: number | null;
}
interface CwtMe {
	tournament: { id: string, status: string, title: string } | null;
	/** 승부 예측 — 내 것만. 회차에서 껐으면 null. */
	bet?: { stakes: number[]; eligible: boolean; reason: string | null; mine: CwtMyBet[] } | null;
	entrant: {
		entrantId: string;
		displayName: string;
		anonymous: boolean;
		status: string;
		entry: string[] | null;
		/** 엔트리 6칸의 이름·그림 (내 것만 온다). 새싹컵의 데려온 아이는 `borrowed`. */
		entryCards: { id: string, name: string, sprite: string, borrowed?: boolean }[] | null;
		lineup: string[] | null;
	} | null;
	/** 새싹컵 — 내 역할·빌려준/데려온 아이·내 차례에 고를 수 있는 빌려준 사람 id. */
	sprout?: {
		role: 'player' | 'lender';
		pickOrder: number | null;
		lent: CwsMon | null;
		borrowed: CwsMon | null;
		pickable: string[] | null;
		shared: boolean;
		voice?: CwsVoiceMine | null;
	} | null;
	/** 마왕토벌전 — 내 팀·리더·선택·같은 팀 손든 아이. */
	raid?: CwrMe | null;
	/** 리본 지갑 (2026-10-14). 옛 서버 응답에는 없다. */
	ribbons?: { balance: number; earned: number } | null;
	/** 행운의 자리 룰렛에서 내가 민 숫자 (안 밀었으면 null). */
	rouletteClick?: number | null;
}

/** 대회 상태를 받아 가는 화면 하나. */
interface CwtSub {
	fn: () => void;
	/** 이 화면이 바라는 주기(ms). 국면마다 달라지므로 값이 아니라 함수다. */
	want: () => number;
}

/**
 * 대회 상태를 **한 통로로만** 물어본다 (2026-09-19).
 *
 * 전에는 배너·관전 띠·대진표 패널이 저마다 타이머를 돌렸다. 그중 띠는 **배틀방마다
 * 하나씩** 붙는다 — 방을 여덟 개 열어 둔 사람 하나가 `GET /showdown/tournament` 를
 * 분당 124회 쐈고, 그 사람의 바구니(60/분)가 그대로 터졌다. **대회가 하나도 없는
 * 시간에도** 그랬다. 화면 수가 요청 수를 곱하는 구조라 주기만 늘려서는 못 막는다.
 *
 * 그래서 요청은 여기 하나로 모은다:
 *   - 주기는 구독자가 바라는 값 중 **가장 짧은 것**. 화면이 몇 개든 통로는 하나다.
 *   - **열린 대회가 없으면 1분**으로 내려간다. 평시에 트래픽이 사라진다.
 *   - 탭이 숨어 있으면 아예 안 쏜다. 돌아오는 순간 한 번 받는다.
 *
 * ⚠️ 레이트 리밋을 키워서 푸는 문제가 아니다. 60/분은 안전핀이고, 새는 쪽이 여기다.
 */
/**
 * 이 탭이 쥔 대회 패널 번들의 스탬프 — `<script src="js/cherish-tournament.js?v=…">` 의 `v`. 못 찾으면 null.
 *
 * 배포가 api 를 먼저 재시작하고 클라를 나중에 빌드하면(2026-10-14 00:39→00:42) 점검 새로고침이 옛 번들을 받는다.
 * 그 탭은 새 api 응답을 옛 코드로 그리다 깨진다(「0.25강」). 서버가 내려 주는 `clientStamp` 와 이 값이 다르면
 * 「새 버전이 있어요」 띠를 띄워 유저가 스스로 새로고침하게 한다 — 순서를 틀려도 복구되는 안전핀이다.
 */
function cwtOwnStamp(): string | null {
	if (typeof document === 'undefined' || typeof document.querySelector !== 'function') return null;
	const el = document.querySelector('script[src*="cherish-tournament.js"]');
	const src = el ? el.getAttribute('src') || '' : '';
	const m = /[?&]v=([0-9a-z]+)/.exec(src);
	return m ? m[1]! : null;
}

class CwtFeed {
	view: CwtView | null = null;
	loaded = false;
	failed = false;
	/** 서버 번들이 내 번들보다 새것이다 — 새로고침 띠. 서버가 스탬프를 모르거나(null) 내 것을 못 찾으면 false. */
	stale = false;
	subs: CwtSub[] = [];
	timer: number | null = null;
	inflight = false;
	/** 429·5xx 를 맞은 뒤 다시 물어볼 로컬 시각. 맞고도 같은 주기로 두들기면 계속 맞는다. */
	backoffUntil = 0;

	subscribe(sub: CwtSub) {
		this.subs.push(sub);
		if (this.loaded) sub.fn();
		if (!this.loaded || this.subs.length === 1) this.load();
		else this.schedule();
	}
	/** 구독표 자체로 지운다 — `fn` 으로 지우면 감싼 화살표가 안 맞아 **영영 안 지워진다**. */
	unsubscribe(sub: CwtSub) {
		this.subs = this.subs.filter(s => s !== sub);
		if (!this.subs.length && this.timer !== null) {
			clearTimeout(this.timer);
			this.timer = null;
		}
	}
	/** 신청·라인업처럼 내가 방금 바꾼 것은 기다리지 않는다. */
	refresh() {
		this.load();
	}
	/** 서버 번들 스탬프와 내 것을 견준다. 한 번 stale 이면 계속 stale — 서버 캐시(60초)가 옛값을 잠깐 돌려줘도 띠가 깜빡이지 않게. */
	noteStamp(view: CwtView | null) {
		const server = view?.clientStamp ?? null;
		const mine = cwtOwnStamp();
		if (server && mine && server !== mine) this.stale = true;
	}
	/** 지금 눌러야 할 주기. 열린 대회가 없으면 누가 뭘 바라든 1분이다. */
	interval(): number {
		const wait = Math.max(0, this.backoffUntil - Date.now());
		if (!this.view) return Math.max(CWT_POLL_NONE, wait);
		let ms = CWT_POLL_NONE;
		for (const sub of this.subs) {
			const want = sub.want();
			if (want < ms) ms = want;
		}
		return Math.max(ms, CWT_POLL_RUNNING, wait);
	}
	schedule() {
		if (this.timer !== null) clearTimeout(this.timer);
		this.timer = null;
		if (!this.subs.length) return;
		this.timer = setTimeout(() => this.load(), this.interval()) as unknown as number;
	}
	load() {
		if (this.timer !== null) clearTimeout(this.timer);
		this.timer = null;
		// 이미 날아간 요청이 있으면 그 응답이 다음 타이머를 건다
		if (!this.subs.length || this.inflight) return;
		// 탭이 숨어 있으면 쏘지 않는다 — 안 보는 화면이 남의 요청 몫을 먹는다
		if (typeof document !== 'undefined' && document.hidden) {
			this.schedule();
			return;
		}
		this.inflight = true;
		fetch(CWT_API, { credentials: 'include' })
			.then(r => {
				// ⚠️ **200 이 아니면 화면을 바꾸지 않는다.** 429·401·5xx 를 「data: null」과 같이
				//    다루면 대회가 멀쩡히 도는 중에 「지금은 열린 대회가 없어요」가 뜬다
				//    (2026-09-19 리허설에서 유저가 겪음). 원인은 폴링 과다였는데 증상은 엉뚱하게
				//    「대회 없음」이라 화면만 보고는 원인을 영영 못 찾는다. 열린 대회가 없다는 말은
				//    **서버가 200 으로 그렇게 답했을 때만** 한다.
				if (!r.ok) {
					const sec = parseInt(r.headers.get('retry-after') || '', 10);
					const back = r.status === 429 ? Math.max(sec > 0 ? sec : 0, 30) : 5;
					this.backoffUntil = Date.now() + back * 1000;
					this.failed = true;
					return null;
				}
				this.backoffUntil = 0;
				return r.json();
			})
			.then(json => {
				if (json === null) return;   // 위에서 처리했다 (직전 화면을 그대로 둔다)
				if (json?.ok) {
					this.view = (json.data as CwtView | null);
					this.failed = false;
					this.noteStamp(this.view);
				} else {
					// 본 적 있는 대회는 지우지 않는다
					this.failed = true;
				}
			})
			.catch(() => {
				this.failed = true;
			})
			.then(() => {
				this.loaded = true;
				this.inflight = false;
				for (const sub of this.subs.slice()) sub.fn();
				this.schedule();
			});
	}
}

export const cwtFeed = new CwtFeed();

// 탭이 돌아오면 밀린 상태를 한 번 받아 온다 (숨어 있는 동안은 안 쏜다)
if (typeof document !== 'undefined') {
	document.addEventListener('visibilitychange', () => {
		if (!document.hidden) cwtFeed.refresh();
	});
}

class CherishTourRoom extends PSRoom {
	override readonly classType: string = 'cherishtour';
	// eslint-disable-next-line no-useless-constructor
	constructor(options: RoomOptions) {
		super(options);
	}
}

/** 배틀과 같은 BGM 중재기를 사용한다. 별도 폴링/별도 탭 간 채널은 만들지 않는다. */
export class CwtMusicPlayer extends preact.Component<{ room: PSRoom, music: CwtView['bracketMusic'], showEffects: boolean }> {
	/** 대진표 음악은 기본으로 튼다 (2026-09-28 사용자 결정) — 끄기는 공통 음량 슬라이더로 한다. */
	enabled = true;
	effectsEnabled = true;
	effects: { [cue: string]: HTMLAudioElement } = {};
	error = '';
	url = '';
	sound: HTMLAudioElement | undefined;
	bgm: BattleBGM | undefined;
	subscriptions: { unsubscribe: () => void }[] = [];
	alive = false;
	override componentDidMount() {
		this.alive = true;
		this.subscriptions.push(PS.subscribe(this.onContext), PS.prefs.subscribe(this.onContext));
		document.addEventListener('visibilitychange', this.onContext);
		window.addEventListener('pagehide', this.onPageHide);
		this.onContext();
	}
	override componentDidUpdate() { if (this.syncAudio()) this.forceUpdate(); this.syncEffects(); }
	override componentWillUnmount() {
		this.alive = false;
		for (const sub of this.subscriptions) sub.unsubscribe();
		document.removeEventListener('visibilitychange', this.onContext);
		window.removeEventListener('pagehide', this.onPageHide);
		this.release();
	}
	allowed() {
		if (document.hidden || !PS.isVisible(this.props.room)) return false;
		// 두 패널을 함께 보는 환경에서도 배틀이 보이면 배틀 음악에 양보한다.
		for (const id in PS.rooms) {
			const room = PS.rooms[id as RoomID];
			if (room?.classType === 'battle' && PS.isVisible(room)) return false;
		}
		return true;
	}
	onPageHide = () => { this.bgm?.pause(); this.stopEffects(); };
	onContext = () => { if (this.alive) { this.syncAudio(); this.syncEffects(); this.forceUpdate(); } };
	onSound = () => { if (this.alive) this.forceUpdate(); };
	onError = () => {
		this.error = '음악을 불러오지 못했어요.';
		this.enabled = false;
		this.release();
		this.onSound();
	};
	onMetadata = () => {
		if (!this.sound || this.bgm) return;
		const duration = this.sound.duration;
		if (!isFinite(duration) || duration <= 1) { this.onError(); return; }
		// 접수·소개곡은 한 번만 재생한다. BattleBGM의 공통 음량/탭 중재는 사용하되
		// 루프 타이머는 곡 길이보다 충분히 뒤에 두고 ended에서 멈춘다.
		// 라운드 곡(loop)은 요소 자체 반복 — ended 가 안 오니 끝에서 멈추는 길을 안 탄다.
		this.sound.loop = !!this.props.music?.loop;
		this.bgm = BattleSound.loadBgm(this.url, 0, duration * 1000 + 3_600_000);
		this.bgm.sound = this.sound;
		this.onContext();
	};
	onEnded = () => {
		if (!this.bgm || !this.sound) return;
		this.enabled = false;
		this.bgm.pause();
		this.onSound();
	};
	release() {
		this.stopEffects();
		if (this.sound) {
			this.sound.removeEventListener('loadedmetadata', this.onMetadata);
			this.sound.removeEventListener('error', this.onError);
			this.sound.removeEventListener('playing', this.onSound);
			this.sound.removeEventListener('pause', this.onSound);
			this.sound.removeEventListener('ended', this.onEnded);
		}
		this.bgm?.destroy();
		if (this.bgm) { clearTimeout(this.bgm.timer); this.bgm.timer = undefined; }
		this.bgm = undefined;
		if (this.sound) { this.sound.pause(); this.sound.removeAttribute('src'); this.sound.load(); }
		this.sound = undefined;
	}
	stopEffects() {
		for (const cue in this.effects) {
			const sound = this.effects[cue]!;
			sound.pause(); sound.removeAttribute('src'); sound.load();
		}
		this.effects = {};
	}
	syncEffects() {
		if (!this.props.showEffects || !this.effectsEnabled || !this.allowed() || PS.prefs.mute || BattleSound.yieldedToOtherTab) {
			if (Object.keys(this.effects).length) this.stopEffects();
			return;
		}
		for (const cue of Object.keys(CWT_REVEAL_SOUNDS) as CwtRevealCue[]) {
			if (this.effects[cue]) continue;
			const sound = document.createElement('audio');
			sound.preload = 'auto';
			sound.src = CWT_REVEAL_SOUNDS[cue];
			sound.load();
			this.effects[cue] = sound;
		}
	}
	stopEffect(cue: CwtRevealCue) {
		const sound = this.effects[cue];
		if (sound) { sound.pause(); sound.currentTime = 0; }
	}
	playEffect(cue: CwtRevealCue) {
		if (!this.props.showEffects || !this.effectsEnabled || !this.allowed() || PS.prefs.mute || BattleSound.yieldedToOtherTab) return;
		this.syncEffects();
		const sound = this.effects[cue];
		if (!sound) return;
		sound.volume = Math.min(1, Math.max(0, PS.prefs.effectvolume / 100 * (cue === 'spin' ? 0.28 : ['select', 'hop', 'rank', 'win', 'lose', 'count', 'go'].includes(cue) ? 0.65 : 1)));
		if (cue === 'spin' && !sound.paused) return;
		sound.loop = cue === 'spin';
		sound.pause(); sound.currentTime = 0;
		void sound.play().catch(() => {});
	}
	syncAudio(): boolean {
		const url = this.props.music?.url || '';
		const changed = url !== this.url;
		if (changed) { this.release(); this.url = url; this.error = ''; this.enabled = true; }
		if (!url || !this.enabled || !this.allowed()) {
			if (this.bgm?.isPlaying) this.bgm.pause();
			return changed;
		}
		if (!this.sound) {
			// BattleSound.getSound는 URL 앞에 배틀 호스트를 붙인다. API의 절대 URL은 직접 지정한다.
			this.sound = document.createElement('audio');
			this.sound.crossOrigin = 'use-credentials';
			this.sound.src = url;
			this.sound.addEventListener('loadedmetadata', this.onMetadata);
			this.sound.addEventListener('error', this.onError);
			this.sound.addEventListener('playing', this.onSound);
			this.sound.addEventListener('pause', this.onSound);
			this.sound.addEventListener('ended', this.onEnded);
			if (this.sound.readyState >= 1) this.onMetadata();
			else { this.sound.preload = 'metadata'; this.sound.load(); }
		}
		if (this.bgm && !this.bgm.isPlaying) {
			// 중재기의 최신 곡 우선순위로 복귀한다. 다른 배틀 객체는 변경하지 않는다.
			BattleSound.deleteBgm(this.bgm);
			BattleSound.bgm.push(this.bgm);
			this.bgm.resume();
		}
		return changed;
	}
	volume = (e: Event) => { PS.prefs.set('musicvolume', Number((e.currentTarget as HTMLInputElement).value)); };
	toggleEffects = () => {
		this.effectsEnabled = !this.effectsEnabled;
		if (this.effectsEnabled) BattleSound.claimBgmTab?.();
		this.syncEffects();
		this.forceUpdate();
	};
	override render() {
		if (!this.props.music && !this.props.showEffects) return null;
		const playing = !!this.bgm?.isActuallyPlaying;
		return <aside class="cwt-music" aria-label="대진표 소리">
			{this.props.music ? <>
			<span class="cwt-music-label">ARENA MUSIC</span>
			<strong title={this.props.music.title}>{this.props.music.title}</strong>
			<div class="cwt-music-actions">
				<span>{PS.prefs.mute ? '전체 음소거 중' : this.enabled && !playing ? '재생 대기' : playing ? '반복 재생' : '음악 듣기'}</span>
			</div>
			{PS.prefs.mute ? <button type="button" onClick={() => PS.prefs.set('mute', false)}>전체 음소거 해제</button> : null}
			<label title="배틀 음악과 같은 음량 설정을 사용해요">음악 음량 (공통) <input type="range" min="0" max="100" value={PS.prefs.musicvolume} onInput={this.volume} aria-label="음악 음량 (배틀과 공통)" /></label>
			{this.error ? <small role="status">{this.error}</small> : null}
			</> : null}
			{this.props.showEffects ? <button type="button" onClick={this.toggleEffects} aria-label={this.effectsEnabled ? '대진 공개 효과음 끄기' : '대진 공개 효과음 켜기'}>
				효과음 {this.effectsEnabled ? '켜짐' : '꺼짐'}
			</button> : null}
		</aside>;
	}
}

export class CherishTourPanel extends PSRoomPanel<CherishTourRoom> {
	static readonly id = 'cherishtour';
	static readonly routes = ['cherish-tour'];
	static readonly Model = CherishTourRoom;
	static readonly title = '체리쉬 컵';

	view: CwtView | null = null;
	me: CwtMe | null = null;
	loaded = false;
	failed = false;
	busy = false;
	/** 모바일 라운드 피드에서 보고 있는 라운드. null = 진행 중인 라운드를 따라간다. */
	viewRound: number | null = null;
	anonWanted = false;
	/** 라인업 편집 중 고른 개체 (엔트리 안에서만). */
	picking: string[] | null = null;
	lineupDialog: HTMLDialogElement | null = null;
	setLineupDialog = (el: HTMLDialogElement | null) => { this.lineupDialog = el; };
	handleOpenLineup = () => { this.lineupDialog?.showModal(); };
	handleCloseLineup = () => { if (!this.busy) this.lineupDialog?.close(); };
	handleCancelLineup = (e: Event) => { if (this.busy) e.preventDefault(); };
	/**
	 * 창이 닫히면(닫기·Esc·배틀 방이 앞으로 와서 가려짐) 덜 고른 선택은 버린다. 남겨 두면 다시 열었을 때
	 * 고른 아이가 눌린 채로 보여 「바꿔 놨다」고 믿는다 — 2026-10-25 56회, 확정 전에 판이 열려 창이 닫혔고
	 * 경기 뒤 그 선택을 보고 적용된 줄 알았다. 다 고르면 그 자리에서 저장되므로 버릴 것은 덜 고른 것뿐이다.
	 */
	handleLineupClosed = () => { if (!this.busy && this.picking) { this.picking = null; this.forceUpdate(); } };
	/** 이번 라운드 판이 이미 열린 뒤에 저장했다 — 그 라운드 동안 「다음 판부터」를 띄운다 (서버 `appliesNext`). */
	lineupNextRound: number | null = null;
	/** `/me` 타이머 (대회 상태는 공용 통로가 가져온다). */
	timer: number | null = null;
	/** 공개 연출·인터미션 동안만 도는 **로컬** 애니 시계 — 요청과 무관하다. */
	animTimer: number | null = null;
	bracketStage: HTMLElement | null = null;
	revealFlight: Animation | null = null;
	revealFlightKey = '';
	musicPlayer: CwtMusicPlayer | null = null;
	setMusicPlayer = (player: CwtMusicPlayer | null) => { this.musicPlayer = player; };
	revealSoundKey = '';
	setBracketStage = (el: HTMLElement | null) => { this.bracketStage = el; };
	/** 지금 국면이 끝나는 로컬 시각 (서버가 준 남은 초를 옮겨 담는다). */
	phaseEndsAt = 0;
	/** 마지막으로 본 국면. */
	phaseKind = '';
	/** 이미 자리에 꽂힌 카드 키. */
	seen: { [key: string]: boolean } = {};
	/** 방금 꽂힌 카드 키 → 꽂힌 시각. */
	fresh: { [key: string]: number } = {};
	seenReady = false;
	/** `/me` 를 가져와 둔 대회 id — 대회가 바뀌면 다시 가져온다. */
	meFor: string | null = null;
	awardKey = '';
	/** 우승 팝업을 띄운 대회 키 — 닫으면 비운다. 시상 효과와 같은 시점에 한 번 뜬다. */
	popupKey = '';
	/** 승부 예측 — 고른 편·판돈. 창이 다른 칸으로 넘어가면 비운다. */
	betSide: 'p1' | 'p2' | null = null;
	betAmount: number | null = null;
	/** 지금 고르고 있는 창의 칸 (`round-slot`). */
	betKey = '';
	/** 예측 마감 로컬 시각 — 서버가 준 남은 초를 옮겨 담는다(폴링 사이에도 초가 흐른다). */
	betEndsAt = 0;

	// ---- 행운의 자리 룰렛 — 상태·연출은 공용 <CwlRouletteBox> 가 든다 (cherish-roulette.tsx, 2026-11-02) ----

	// ---- 리본 상점 (2026-10-14) ----
	ribbons: CwlRibbons | null = null;
	ribbonsLoading = false;
	boxBusy = false;
	/** 방금 연 상자 — 결과 카드. */
	boxResult: { tier: number; ko: string; label: string; icon: string | null; kind: string; at: number } | null = null;

	// ---- 새싹컵 (2026-10-06) ----
	/** 빌려줄 수 있는 내 아이 — 빌려주기 단계에 들어설 때 한 번 받는다(요청은 그 한 번뿐). */
	lendOptions: CwsMon[] | null = null;
	lendLoading = false;
	/** 빌려주기 화면에서 고른 아이 (확정 전). */
	lendChoice: string | null = null;
	lendFilter = '';
	/** 뽑기 — 내 차례에 고른 빌려준 사람 (확정 전). */
	pickChoice: string | null = null;
	/** 방금 누가 무엇을 데려갔나 — 띠 한 줄과 카드 반짝임. */
	sproutNews: { key: string; text: string; sprite: string | null; lenderId: string; at: number } | null = null;
	/** 직전에 본 「누가 누구 것을 데려갔나」. 첫 로드에는 소식을 만들지 않는다. */
	sproutPicked: { [playerId: string]: string | null } | null = null;
	/** 지금 단계 키 — 단계가 바뀌면 내 상태를 다시 받는다. */
	sproutPhaseKey = '';
	/** 뽑기판을 처음 그린 시각 — 카드가 다 뒤집히기 전에는 도장·잠금 표시를 늦게 띄운다. */
	sproutFlipAt = 0;
	/**
	 * 도움 통화방 팝업 (2026-10-08). role 'player' = 내가 데려간 쪽, 'lender' = 내가 빌려준 쪽.
	 * stage: 'player' 내게 묻는 중 · 'waiting' 「예」 뒤 주인의 답을 기다림 · 'lender' 수락·거부를 묻는 중 ·
	 *        'moving' 방을 여는 중 · 'moved'/'declined'/'missed'/'failed' 결과 한 줄(몇 초 뒤 스스로 닫힘).
	 * `until` 이 지나면 닫힌다 — 묻는 창은 서버 마감과 같은 순간이고, 결과 줄은 보여 주는 시간이다.
	 */
	voice: { role: 'player' | 'lender'; stage: string; name: string; until: number; key: string } | null = null;
	/** 이미 답한 요청 키 — 공개판에 한 바퀴쯤 더 남아 있어도 다시 띄우지 않는다. */
	voiceDone: { [key: string]: true } = {};
	/** 「예」를 누른 시각 · 그 요청이 공개판에 실린 걸 봤나 · 결과를 /me 로 받았나(한 번만 묻는다). */
	voiceYesAt = 0;
	voiceSeenOnBoard = false;
	voiceResultAsked = false;

	override componentDidMount() {
		cwtFeed.subscribe(this.sub);
		this.meTick();
		this.loadRibbons();
		super.componentDidMount?.();
		this.subscribeTo(PS, () => {
			if (!PS.isVisible(this.props.room)) this.lineupDialog?.close();
			if (this.noteAward()) this.forceUpdate();
		});
	}
	override componentDidUpdate() {
		this.syncRevealSounds(); this.syncRevealFlight();
	}
	override componentWillUnmount() {
		this.clearRevealFlight();
		cwtFeed.unsubscribe(this.sub);
		if (this.timer) clearTimeout(this.timer);
		this.timer = null;
		if (this.animTimer) clearInterval(this.animTimer);
		this.animTimer = null;
		super.componentWillUnmount?.();
	}
	clearRevealFlight() {
		this.revealFlight?.cancel();
		this.revealFlight = null;
		this.bracketStage?.classList.remove('is-reveal-flight');
	}
	syncRevealSounds() {
		const view = this.view;
		if (view?.phase !== 'reveal' || document.hidden || !PS.isVisible(this.props.room)) return;
		const order = view.revealOrder || [];
		if (!order.length) return;
		const count = this.revealedCount();
		const key = `${view.id}-${count}`;
		if (key === this.revealSoundKey) return;
		this.revealSoundKey = key;
		const elapsed = this.revealElapsed();
		const interval = Math.max(1, view.revealSec * 1000 - CWT_REVEAL_LEAD_MS - CWT_REVEAL_TAIL_MS) / order.length;
		if (count >= 1 && count <= order.length && elapsed - CWT_REVEAL_LEAD_MS - (count - 1) * interval < 1200) {
			this.musicPlayer?.playEffect('entrance');
		} else if (count > order.length && elapsed - (view.revealSec * 1000 - CWT_REVEAL_TAIL_MS) < 1200) {
			this.musicPlayer?.playEffect('complete');
		}
	}
	/** 현재 입장 카드가 실제 대진 칸까지 이동한다. 위치는 그때의 DOM 에서 재므로 모바일에도 맞는다. */
	syncRevealFlight() {
		const view = this.view;
		const stage = this.bracketStage;
		if (view?.phase !== 'reveal' || !stage || document.hidden || !PS.isVisible(this.props.room)) {
			this.clearRevealFlight();
			return;
		}
		const order = view.revealOrder || [];
		const count = this.revealedCount();
		const entrantId = order[count - 1];
		const key = entrantId ? `${view.id}-${entrantId}` : '';
		if (key !== this.revealFlightKey) this.clearRevealFlight();
		if (!key || key === this.revealFlightKey) return;
		const span = Math.max(1, view.revealSec * 1000 - CWT_REVEAL_LEAD_MS - CWT_REVEAL_TAIL_MS);
		const interval = span / order.length;
		const into = this.revealElapsed() - CWT_REVEAL_LEAD_MS - (count - 1) * interval;
		// 인원이 적어 카드 간격이 길어도 전신과 파트너를 충분히 보여준 뒤 다음 칸에 꽂는다.
		const duration = Math.min(680, Math.max(250, interval * .35));
		const hold = Math.max(0, interval - duration - Math.min(220, interval * .12));
		if (into < hold) return;
		this.revealFlightKey = key;
		if (into > hold + duration || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
		const source = stage.querySelector<HTMLElement>('.cwt-reveal-card');
		const target = stage.querySelector<HTMLElement>(`.cwt-card[data-cwt-entrant="${entrantId}"]`);
		if (!source || !target || !source.animate) return;
		const from = source.getBoundingClientRect();
		const to = target.getBoundingClientRect();
		if (!from.width || !to.width) return;
		const dx = to.left + to.width / 2 - from.left - from.width / 2;
		const dy = to.top + to.height / 2 - from.top - from.height / 2;
		const scale = Math.min(1, Math.max(.25, to.width / from.width));
		stage.classList.add('is-reveal-flight');
		this.revealFlight = source.animate([
			{ transform: 'translate(0, 0) scale(1)', opacity: 1 },
			{ transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0 },
		], { duration, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' });
		this.revealFlight.onfinish = () => this.musicPlayer?.playEffect('land');
	}

	/** 통로가 새 상태를 물어다 줬다. */
	onFeed = () => {
		const previous = this.view;
		this.view = cwtFeed.view;
		this.loaded = cwtFeed.loaded;
		this.failed = cwtFeed.failed;
		const v = this.view;
		this.syncPhaseClock(previous);
		this.phaseKind = v ? v.phase : '';
		this.noteAward();
		this.syncAnim();
		this.noteArrivals();
		this.syncBet();
		this.syncSprout();
		// 대회가 새로 열렸거나 갈렸을 때만 내 상태를 다시 본다
		const id = v ? v.id : null;
		if (id !== this.meFor) {
			this.lineupDialog?.close();
			this.viewRound = null;
			this.picking = null;
			this.meFor = id;
			this.loadMe();
		}
		this.forceUpdate();
	};
	syncPhaseClock(previous: CwtView | null) {
		const v = this.view;
		if (!v || v.phaseSecLeft === null) { this.phaseEndsAt = 0; return; }
		const next = Date.now() + v.phaseSecLeft * 1000;
		// 새싹컵 뽑기는 차례가 넘어갈 때마다 시계가 다시 30초가 된다 — 차례도 같아야 같은 국면이다
		const same = previous?.id === v.id && previous?.phase === v.phase && previous?.currentRound === v.currentRound &&
			(previous?.sprout?.turn ?? null) === (v.sprout?.turn ?? null) &&
			// 마왕토벌전 — 투표·선택·공개 창은 밖에서 보면 전부 '인터미션'이라 창 종류와 판 번호까지 같아야 같은 국면이다
			(previous?.raid?.phase ?? null) === (v.raid?.phase ?? null) && (previous?.raid?.battleNo ?? 0) === (v.raid?.battleNo ?? 0);
		// 폴링 지연으로 공개 시계가 뒤로 가며 같은 입장 카드를 재연하지 않도록 한다.
		this.phaseEndsAt = same && this.phaseEndsAt ? Math.min(this.phaseEndsAt, next) : next;
	}
	noteAward(): boolean {
		const v = this.view;
		if (v?.status !== 'done' || !v.champion) { this.awardKey = ''; return false; }
		const key = `${v.id}-${v.champion.entrantId}`;
		if (this.awardKey === key || CWT_PRESENTED_AWARDS.has(key) || document.hidden || !PS.isVisible(this.props.room)) return false;
		CWT_PRESENTED_AWARDS.add(key);
		this.awardKey = key;
		this.popupKey = v.roulette && v.roulette.phase !== 'wait' ? '' : key;
		return true;
	}
	/** 대진표는 초 단위가 필요하지만, 그 요구가 방 개수만큼 곱해지지는 않는다. */
	sub: CwtSub = {
		fn: () => this.onFeed(),
		// 새싹컵 뽑기는 30초 차례라 진행 중 대회처럼 초 단위로 본다. 룰렛이 도는 동안(결과 전)도 같다 — 민 사람 수·마감이 초 단위다
		want: () => (cwtFeed.view?.status === 'running' || cwtFeed.view?.sprout ||
			(cwtFeed.view?.roulette && cwtFeed.view.roulette.phase !== 'result') ? CWT_POLL_RUNNING : CWT_POLL_IDLE),
	};

	meTick = () => {
		const rp = cwtFeed.view?.raid?.phase;
		const fast = rp === 'vote' || rp === 'pick' || rp === 'flip';
		this.timer = setTimeout(() => {
			this.loadMe();
			this.meTick();
		}, fast ? CWR_ME_POLL_FAST : CWT_ME_POLL) as unknown as number;
	};

	/** 열린 대회가 없으면 **아예 안 부른다** — 평시에 이 경로로 나갈 요청은 없다. */
	loadMe() {
		if (!cwtFeed.view) {
			this.me = null;
			return;
		}
		fetch(`${CWT_API}/me`, { credentials: 'include' })
			.then(r => (r.ok ? r.json() : null))
			.then(json => {
				// 같은 이유로 **실패에 내 상태를 지우지 않는다** — 신청해 둔 사람에게 「신청하기」가
				// 도로 뜨면 두 번 누른다.
				if (!json?.ok) return;
				this.me = (json.data as CwtMe);
				this.syncVoiceFromMe();
				this.forceUpdate();
			})
			.catch(() => undefined);
	}


	/** `onOk` 는 성공 응답의 data 를 받는다 — 뽑기 응답의 도움 통화방 질문처럼 바로 그릴 것이 있을 때. */
	post(path: string, body: unknown, onOk?: (data: any) => void) {
		if (this.busy) return;
		this.busy = true;
		this.forceUpdate();
		fetch(`${CWT_API}${path}`, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body ?? {}),
		})
			.then(r => r.json())
			.then(json => {
				if (!json?.ok) {
					this.busy = false;
					PS.alert(json?.message || json?.error?.message || '잠시 뒤 다시 시도해주세요.');
					this.forceUpdate();
					return;
				}
				if (path === '/lineup' && this.me?.entrant) {
					const saved = ((body as {monIds: string[]}).monIds).slice();
					this.me.entrant.lineup = saved;
					this.lineupNextRound = json.data?.appliesNext ? (this.view?.currentRound ?? null) : null;
					// 3마리는 다 고르면 끝이라 닫는다. 결승 6마리는 순서를 여러 번 고치므로 열어 둔다.
					if (saved.length !== 6) this.lineupDialog?.close();
				}
				if (path === '/pick') this.pickChoice = null;
				if (onOk) onOk(json.data);
				this.busy = false;
				this.picking = null;
				cwtFeed.refresh();
				this.loadMe();
			})
			.catch(() => {
				this.busy = false;
				PS.alert('연결이 불안정해요. 잠시 뒤 다시 시도해주세요.');
				this.forceUpdate();
			});
	}

	/**
	 * 새로 자리에 꽂힌 카드를 기록한다. **첫 로드에서는 아무것도 새것이 아니다** —
	 * 안 그러면 방을 열 때마다 대진표 전체가 한꺼번에 튀어 들어온다.
	 */
	noteArrivals() {
		const view = this.view;
		if (!view) return;
		const keys: string[] = [];
		for (const m of view.matches) {
			if (m.p1) keys.push(`${m.round}-${m.slot}-1-${m.p1.entrantId}`);
			if (m.p2) keys.push(`${m.round}-${m.slot}-2-${m.p2.entrantId}`);
		}
		if (!this.seenReady) {
			for (const k of keys) this.seen[k] = true;
			this.seenReady = true;
			return;
		}
		for (const k of keys) {
			if (!this.seen[k]) {
				this.seen[k] = true;
				this.fresh[k] = Date.now();
			}
		}
	}

	/** 방금 꽂힌 카드인가 (진입 연출 1.2초). */
	isFresh(round: number, slot: number, side: 1 | 2, entrantId: string): boolean {
		const at = this.fresh[`${round}-${slot}-${side}-${entrantId}`];
		return !!at && Date.now() - at < 1200;
	}

	/**
	 * 공개 연출·인터미션 동안만 **로컬 시계**를 돌린다.
	 *
	 * 카드는 25초에 여덟 장이 나오는데 서버 응답은 2초에 한 번이다. 그 사이를 요청으로
	 * 메우면 화면 수만큼 요청이 늘어난다 — 대신 남은 초를 받아 두고 여기서 다시 그린다.
	 * **시계는 공짜고 요청은 아니다.**
	 */
	syncAnim() {
		const wants = this.phaseKind === 'reveal' || this.phaseKind === 'intermission' ||
			this.phaseKind === 'lend' || this.phaseKind === 'pick';
		if (wants && this.animTimer === null) {
			this.animTimer = setInterval(() => { this.forceUpdate(); this.syncAnim(); }, 250) as unknown as number;
		} else if (!wants && this.animTimer !== null) {
			clearInterval(this.animTimer);
			this.animTimer = null;
		}
	}

	/** 공개 연출이 시작된 뒤 흐른 시간(ms). 총 길이는 서버가 준다(`revealSec`). */
	revealElapsed(): number {
		const total = (this.view?.revealSec ?? 0) * 1000;
		if (!total || !this.phaseEndsAt) return 0;
		const left = Math.max(0, this.phaseEndsAt - Date.now());
		return Math.max(0, total - left);
	}

	/**
	 * 지금까지 몇 장이 나왔나 (첫 장이 1). 마지막 장이 지나면 인원수를 넘어서고,
	 * 그때부터 화면은 「곧 시작해요」로 바뀐다.
	 */
	revealedCount(): number {
		const order = this.view?.revealOrder || [];
		if (!order.length) return 0;
		const total = (this.view?.revealSec ?? 0) * 1000;
		const span = Math.max(1, total - CWT_REVEAL_LEAD_MS - CWT_REVEAL_TAIL_MS);
		const elapsed = this.revealElapsed();
		if (elapsed < CWT_REVEAL_LEAD_MS) return 0;
		return Math.floor((elapsed - CWT_REVEAL_LEAD_MS) / (span / order.length)) + 1;
	}

	/**
	 * 이 참가자가 대진표에 나타났나. **공개 연출 중에만** 감춘다 — 그 밖의 국면에서까지
	 * 감추면 대진표가 영영 빈 종이로 남는다.
	 */
	isRevealed(entrantId: string): boolean {
		if (this.phaseKind !== 'reveal') return true;
		const order = this.view?.revealOrder || [];
		if (!order.length) return true;
		const idx = order.indexOf(entrantId);
		if (idx < 0) return true;
		return idx < this.revealedCount();
	}

	// ---- 조각 ----------------------------------------------------------------

	rounds(): number {
		// 빙글빙글컵 — 라운드 수 + 결승 한 칸
		if (this.view?.swiss) return this.view.swiss.rounds + 1;
		const size = this.view?.bracketSize || 0;
		let r = 0;
		let n = size;
		while (n > 1) {
			n /= 2;
			r++;
		}
		return r;
	}

	roundLabel(round: number): string {
		const sw = this.view?.swiss;
		if (sw) return round > sw.rounds ? '결승' : `${round}라운드`;
		const total = this.rounds();
		const left = total - round;
		if (left === 0) return '결승';
		if (left === 1) return '준결승';
		return `${Math.pow(2, left + 1)}강`;
	}

	/**
	 * 선택 창이 받는 마릿수 — 평소 3. 빙글빙글컵 결승(6대6)에 내가 섰으면 6 (엔트리 전부, 순서만 바꾼다).
	 * 서버 `setLineup` 이 같은 판정을 하므로 어긋나면 거절된다.
	 */
	lineupSize(): number {
		const view = this.view;
		const sw = view?.swiss;
		const my = this.myEntrantId();
		if (!view || !sw || !my || !sw.final || sw.finalFormat !== 'six' || view.currentRound !== sw.rounds + 1) return 3;
		return sw.finalists && sw.finalists.indexOf(my) >= 0 ? 6 : 3;
	}

	myEntrantId(): string | null {
		return this.me?.entrant?.entrantId ?? null;
	}

	renderCard(card: CwtCard | null, match: CwtMatch, mine: boolean, side: 1 | 2) {
		if (!card) {
			return <div class="cwt-card is-empty"><span class="cwt-bye">{match.round > 1 && match.state === 'pending' ? '진출자 대기' : '부전승'}</span></div>;
		}
		// 공개 연출 중에는 아직 등장하지 않은 참가자가 빈 종이로 남아 있다
		if (!this.isRevealed(card.entrantId)) {
			return <div class="cwt-card is-hidden" />;
		}
		const won = match.state === 'done' && match.winner === card.entrantId;
		const lost = match.state === 'done' && match.winner !== null && match.winner !== card.entrantId;
		const cls = ['cwt-card'];
		if (won) cls.push('is-won');
		if (lost) cls.push('is-lost');
		if (mine) cls.push('is-mine');
		if (card.anonymous) cls.push('is-anon');
		if (match.round === 1 && this.view?.phase === 'reveal' &&
			(this.view.revealOrder || [])[this.revealedCount() - 1] === card.entrantId) cls.push('is-reveal-current');
		if (this.isFresh(match.round, match.slot, side, card.entrantId)) cls.push('is-new');
		return <div class={cls.join(' ')} data-cwt-entrant={card.entrantId}>
			<span class="cwt-ava">
				{card.avatar && !card.anonymous
					? <img src={card.avatar} alt="" />
					: <span class="cwt-ava-blank" />}
			</span>
			<span class="cwt-name" title={card.name}>{card.name}</span>
			{card.borrowed ? <img class="cwt-card-lent" src={card.borrowed.sprite} alt={`${card.borrowed.lender} 님의 ${card.borrowed.name}`}
				title={`${card.borrowed.lender} 님에게 빌린 ${card.borrowed.name}`} /> : null}
			{mine ? <span class="cwt-mine-label">나</span> : null}
			{card.stamps > 0 && <span class="cwt-stamps">{'●'.repeat(Math.min(card.stamps, 4))}</span>}
			{card.usedMons && card.usedMons.length
				? <span class="cwt-used">{card.usedMons.map(() => <i class="cwt-used-dot" />)}</span>
				: null}
			{won && <span class="cwt-stamp-win" aria-hidden="true" />}
		</div>;
	}

	/**
	 * 칸의 숫자 두 벌.
	 *
	 *  1. **남은 마리 수** (진행 중) — 관전 그림자가 흘려 준 공개 정보. 대진표만 봐도 판세가 보인다.
	 *  2. **세트 스코어** (결승 3판 2선승) — 몇 판을 이겼나.
	 *
	 * 둘을 한 줄에 섞지 않는다. 섞으면 「3-2」가 마리 수인지 판수인지 알 수 없다.
	 */
	renderPips(match: CwtMatch) {
		const rows = [];
		if (match.alive) {
			const dots = (n: number, of: number) => {
				const out = [];
				for (let i = 0; i < of; i++) out.push(<i class={i < n ? 'cwt-pip is-on' : 'cwt-pip'} />);
				return out;
			};
			const cap = Math.max(match.alive[0], match.alive[1], 3);
			rows.push(<span class="cwt-pips" title="남은 포켓몬">
				{dots(match.alive[0], cap)}<b>·</b>{dots(match.alive[1], cap)}
			</span>);
		}
		if (match.score[0] > 0 || match.score[1] > 0) {
			rows.push(<span class="cwt-sets" title="이긴 판수">{match.score[0]} : {match.score[1]}</span>);
		}
		return rows.length ? <>{rows}</> : null;
	}

	renderMatch = (match: CwtMatch) => {
		const my = this.myEntrantId();
		const mine1 = !!my && match.p1?.entrantId === my;
		const mine2 = !!my && match.p2?.entrantId === my;
		const iAmIn = mine1 || mine2;
		return <div
			class={`cwt-match is-${match.state}${iAmIn ? ' is-mine' : ''}${match.lastStand ? ' is-last' : ''}`}
			key={`${match.round}-${match.slot}`}
		>
			{this.renderCard(match.p1, match, mine1, 1)}
			<div class="cwt-mid">
				<span class="cwt-vs" aria-hidden="true" />
				{this.renderPips(match)}
				{match.state === 'live' && match.roomId
					? <button class="cwt-watch" data-room={match.roomId} onClick={this.handleWatch}>{iAmIn ? '내 배틀' : '관전'}</button>
					: null}
				{match.state === 'calling' && match.callSecLeft !== null
					? <span class="cwt-calling">호출 중 {Math.floor(match.callSecLeft / 60)}:{String(match.callSecLeft % 60).padStart(2, '0')}</span>
					: null}
				{match.state === 'live' && match.lastStand
					? <span class="cwt-last">막판</span>
					: null}
				{match.state === 'ready' ? <span class="cwt-wait">곧 시작</span> : null}
				{match.state === 'pending' ? <span class="cwt-wait">대기</span> : null}
			</div>
			{this.renderCard(match.p2, match, mine2, 2)}
		</div>;
	};

	/**
	 * PC 대진표 — **아래에서 위로** 올라간다 (2026-09-19 사용자 결정).
	 *
	 * 맨 아래가 8강, 위로 준결승, 맨 위가 결승이다. 한 라운드가 가로 한 줄이고, 그 줄 안에서
	 * 경기가 나란히 선다. 위 라운드의 칸은 **자기를 먹여 주는 두 칸 위에 걸쳐** 놓이는데,
	 * 그 계산이 `grid-column: slot*span+1 / span span` 이다 (span = 2^(라운드-1)).
	 *
	 * 화면을 뒤집는 건 CSS `column-reverse` 하나다 — DOM 은 1라운드부터 그대로라서 읽는 순서·
	 * 탭 순서가 라운드 순서와 같다. 줄을 거꾸로 만들어 넣으면 그 둘이 어긋난다.
	 */
	renderTree() {
		const view = this.view!;
		const total = this.rounds();
		const lanes = (view.bracketSize || 2) / 2;
		// Array.from 인 이유: 쇼다운 클라의 babel 은 루프 변수를 콜백이 캡처하면 거부한다
		// (throwIfClosureRequired). 화살표 함수 안의 const 는 루프 변수가 아니라 통과한다.
		const rows = Array.from({ length: total }, (_unused, idx) => {
			const r = idx + 1;
			const span = Math.pow(2, r - 1);
			const cells = view.matches
				.filter(m => m.round === r)
				.map(m => <div class="cwt-cell" style={`grid-column:${m.slot * span + 1}/span ${span}`}>
					{this.renderMatch(m)}
				</div>);
			return <div class="cwt-row">
				<h4 class="cwt-row-h">{this.roundLabel(r)}</h4>
				<div class="cwt-row-grid" style={`grid-template-columns:repeat(${lanes},minmax(0,1fr))`}>{cells}</div>
			</div>;
		});
		return <div class="cwt-tree">{rows}</div>;
	}

	/** 모바일 라운드 피드 — 지금 라운드가 기본, 내 경기는 맨 위에 고정. */
	renderFeed() {
		const view = this.view!;
		const total = this.rounds();
		const current = view.status === 'done' ? total : view.currentRound ?? 1;
		const showing = Math.max(1, Math.min(total, this.viewRound ?? current));
		const my = this.myEntrantId();
		const all = view.matches.filter(m => m.round === showing);
		const mineFirst = all.slice().sort((a, b) => {
			const am = my && (a.p1?.entrantId === my || a.p2?.entrantId === my) ? 0 : 1;
			const bm = my && (b.p1?.entrantId === my || b.p2?.entrantId === my) ? 0 : 1;
			return am - bm || a.slot - b.slot;
		});
		const chips = Array.from({ length: total }, (_unused, idx) => {
			const r = idx + 1;
			return (<button
				class={r === showing ? 'cwt-chip is-on' : 'cwt-chip'}
				aria-pressed={r === showing}
				data-round={r}
				onClick={this.handleRound}
			>{this.roundLabel(r)}</button>);
		});
		return <div class="cwt-feed">
			<div class="cwt-chips" aria-label="대진표 라운드 선택">{chips}</div>
			{showing !== current ? <button class="cwt-follow-round" onClick={this.handleFollowRound}>{view.status === 'done' ? '결승 결과 보기' : `${this.roundLabel(current)}으로 돌아가기`}</button> : null}
			<div class="cwt-feed-list">{mineFirst.map(this.renderMatch)}</div>
		</div>;
	}

	/**
	 * 관전 진입. **관전 슬롯은 1개다** — 새 경기를 열면 앞서 보던 관전방을 닫는다.
	 * 배틀방 하나가 스프라이트·애니메이션 장면을 통째로 들고 있어서, 저사양 폰에서는
	 * 세 개만 열려 있어도 버겁다. 내가 뛰는 방은 건드리지 않는다.
	 */
	handleWatch = (e: Event) => {
		const roomid = (e.currentTarget as HTMLElement).getAttribute('data-room');
		if (!roomid) return;
		for (const id in PS.rooms) {
			const other = PS.rooms[id as RoomID];
			// ⚠️ 방 종류 판별은 `classType` 이다 (`type` 이 아니다 — 그걸 보면 아무것도 안 닫힌다)
			if (!other || other.classType !== 'battle' || id === roomid) continue;
			// 내가 참가자인 방은 닫지 않는다 — panel-battle 이 `side` 로 참가 여부를 가른다
			if ((other as any).side) continue;
			PS.leave(id as RoomID);
		}
		PS.join(roomid as RoomID);
	};

	handleRound = (e: Event) => {
		const el = (e.currentTarget as HTMLElement);
		const r = Number(el.getAttribute('data-round'));
		if (r) {
			this.viewRound = r;
			this.forceUpdate();
		}
	};
	handleFollowRound = () => {
		this.viewRound = null;
		this.forceUpdate();
	};

	// ---- 새싹컵 ----------------------------------------------------------------

	/**
	 * 새싹컵 뽑기판을 따라간다. 단계가 바뀌면 내 상태(`/me`)를 한 번 다시 받고, 누가 무엇을
	 * 데려갔는지는 **직전 판과 비교해서** 소식 한 줄로 만든다. 요청을 새로 만들지 않는다 —
	 * 대회 통로가 가져온 판만 본다. 첫 로드는 비교할 직전이 없으니 소식도 없다.
	 */
	syncSprout() {
		const v = this.view;
		const board = v?.sprout ?? null;
		if (!v || !board) {
			this.sproutPicked = null;
			this.sproutPhaseKey = '';
			this.lendOptions = null;
			this.lendChoice = null;
			this.pickChoice = null;
			// 뽑기판이 내려갔다(대진 공개·취소) — 묻던 창은 접고, 결과 한 줄은 제 시간만큼 남겨 둔다
			if (this.voice && (this.voice.stage === 'player' || this.voice.stage === 'lender')) this.voice = null;
			return;
		}
		const phaseKey = `${v.id}-${board.phase}`;
		if (phaseKey !== this.sproutPhaseKey) {
			this.sproutPhaseKey = phaseKey;
			this.pickChoice = null;
			this.sproutFlipAt = Date.now();
			this.loadMe();
		}
		this.syncVoiceFromBoard(board);
		const now: { [playerId: string]: string | null } = {};
		for (const p of board.players) now[p.entrantId] = p.picked;
		const before = this.sproutPicked;
		this.sproutPicked = now;
		if (!before) return;
		let fresh = false;
		// ⚠️ 반복문 안에서 루프 변수를 붙잡는 화살표를 만들지 않는다 — 운영 빌드의 babel 이
		//    「let/const in this block would add a closure」로 통째로 죽는다 (2026-10-06 실측)
		const lenderById: { [id: string]: CwsBoard['lenders'][number] } = {};
		for (const l of board.lenders) lenderById[l.entrantId] = l;
		for (const p of board.players) {
			if (!p.picked || before[p.entrantId] === p.picked) continue;
			fresh = true;
			const lender = lenderById[p.picked];
			const mon = lender?.mon ?? null;
			const what = mon ? `${lender!.name} 님의 ${mon.name}${cwsJosa(mon.name, '을', '를')}` : '함께할 아이를';
			this.sproutNews = {
				key: `${p.entrantId}-${p.picked}`,
				text: `${p.name} 님이 ${what} 데려갔어요`,
				sprite: mon?.sprite ?? null,
				lenderId: p.picked,
				at: Date.now(),
			};
		}
		if (fresh) {
			this.musicPlayer?.playEffect('land');
			// 고를 수 있는 것이 줄었다 — 내 판정(잠금·함께 쓰기)을 새로 받는다
			this.loadMe();
		}
	}

	/** 빌려줄 수 있는 내 아이 — 빌려주기 단계에서 처음 그릴 때 한 번만 받는다. */
	loadLendOptions() {
		if (this.lendLoading || this.lendOptions) return;
		this.lendLoading = true;
		fetch(`${CWT_API}/lend-options`, { credentials: 'include' })
			.then(r => (r.ok ? r.json() : null))
			.then(json => {
				if (!json?.ok) return;
				this.lendOptions = (json.data?.mons || []) as CwsMon[];
				if (!this.lendChoice) this.lendChoice = this.me?.sprout?.lent?.id ?? null;
			})
			.catch(() => undefined)
			.then(() => {
				this.lendLoading = false;
				this.forceUpdate();
			});
	}
	handleLendChoose = (e: Event) => {
		const id = (e.currentTarget as HTMLElement).getAttribute('data-mon');
		if (!id || this.busy) return;
		this.lendChoice = id;
		this.forceUpdate();
	};
	handleLendFilter = (e: Event) => {
		this.lendFilter = (e.currentTarget as HTMLInputElement).value;
		this.forceUpdate();
	};
	handleLend = () => {
		if (this.lendChoice) this.post('/lend', { monId: this.lendChoice });
	};
	handlePickChoose = (e: Event) => {
		const id = (e.currentTarget as HTMLElement).getAttribute('data-lender');
		if (!id || this.busy) return;
		this.pickChoice = this.pickChoice === id ? null : id;
		this.forceUpdate();
	};
	handlePickConfirm = () => {
		if (this.pickChoice) this.post('/pick', { lenderEntrantId: this.pickChoice }, data => this.onVoiceOffer(data?.voice));
	};

	// ---- 도움 통화방 (2026-10-08) -------------------------------------------------
	/*
	 * 데려간 사람에게 「주인에게 도움을 받을까요?」, 「예」면 주인에게 「수락·거부」, 수락이면 봇이 둘만 들어갈 수
	 * 있는 임시 통화방으로 둘을 옮긴다. 서버가 둘 다 음성에 있을 때만 묻는다(함께 쓰기엔 묻지 않는다).
	 *
	 * 받는 길 (새 타이머·새 폴링 없음):
	 *  - 데려간 사람의 질문은 **뽑기 응답**에 실려 온다 → 바로 팝업.
	 *  - 주인의 수락창은 **공개 뽑기판**(`sprout.voiceAsks`, 2초 통로)에 실린다 — `/me` 10초로는 15초 창에 못 맞춘다.
	 *  - 데려간 사람의 결과(이동·거절·무응답)는 공개판에서 요청이 사라진 뒤 `/me` 를 **한 번** 받아 읽는다.
	 *  - 시계는 기존 애니 타이머(뽑기 동안 250ms)가 다시 그린다.
	 */
	onVoiceOffer(offer: { lenderName: string; seconds: number } | null | undefined) {
		const myId = this.myEntrantId();
		if (!offer || !myId) return;
		this.voice = { role: 'player', stage: 'player', name: offer.lenderName, until: Date.now() + offer.seconds * 1000, key: `p-${myId}` };
		this.voiceSeenOnBoard = false;
		this.voiceResultAsked = false;
		this.forceUpdate();
	}
	/** 공개 뽑기판을 보고 — 주인이면 수락창을 띄우고, 데려간 사람이면 내 요청이 사라진 순간을 잡는다. */
	syncVoiceFromBoard(board: CwsBoard) {
		const myId = this.myEntrantId();
		if (!myId) return;
		const asks = board.voiceAsks || [];
		const v = this.voice;
		let forMe: { playerId: string; lenderId: string; secLeft: number } | null = null;
		let mine = false;
		for (const a of asks) {
			if (a.lenderId === myId) forMe = a;
			if (a.playerId === myId) mine = true;
		}
		if (forMe) {
			const key = `l-${forMe.playerId}-${forMe.lenderId}`;
			if (!this.voiceDone[key] && (!v || v.key !== key)) {
				let name = '새싹 선수';
				for (const p of board.players) if (p.entrantId === forMe.playerId) name = p.name;
				this.voice = { role: 'lender', stage: 'lender', name, until: Date.now() + forMe.secLeft * 1000, key };
				this.musicPlayer?.playEffect('land');
			}
		} else if (v && v.role === 'lender' && v.stage === 'lender') {
			// 요청이 사라졌다 — 시한이 지났거나 데려간 사람이 먼저 접었다
			this.voice = null;
		}
		if (v && v.role === 'player' && v.stage === 'waiting') {
			if (mine) this.voiceSeenOnBoard = true;
			// 공개판에 한 번 실린 뒤 사라졌거나, 주인이 2초 안에 답해 실리기도 전에 끝났으면 결과를 받는다
			else if (!this.voiceResultAsked && (this.voiceSeenOnBoard || Date.now() - this.voiceYesAt > 4000)) {
				this.voiceResultAsked = true;
				this.loadMe();
			}
		}
	}
	/** `/me` 를 받았다 — 기다리던 결과를 그리거나, 뽑기 응답을 놓쳤으면(새로고침) 질문을 다시 띄운다. */
	syncVoiceFromMe() {
		const mv = this.me?.sprout?.voice ?? null;
		const myId = this.myEntrantId();
		const v = this.voice;
		if (v && v.role === 'player' && v.stage === 'waiting') {
			if (!mv) {
				// 요청이 통째로 사라졌다(api 재시작 등) — 조용히 닫는다
				if (this.voiceResultAsked) this.voice = null;
			} else if (mv.role === 'player' && mv.stage !== 'player' && mv.stage !== 'lender' && mv.stage !== 'moving') {
				this.voiceResult(mv.stage);
			}
			return;
		}
		if (!v && mv && myId && mv.role === 'player' && mv.stage === 'player' && !this.voiceDone[`p-${myId}`] && mv.secLeft > 0) {
			this.voice = { role: 'player', stage: 'player', name: mv.name, until: Date.now() + mv.secLeft * 1000, key: `p-${myId}` };
		}
	}
	voiceResult(stage: string) {
		const v = this.voice;
		if (!v) return;
		this.voice = { ...v, stage, until: Date.now() + 6000 };
		if (stage === 'moved') this.musicPlayer?.playEffect('complete');
		this.forceUpdate();
	}
	/** 답은 `post` 를 거치지 않는다 — `busy` 가 뽑기 버튼과 얽히고, 통로 새로 고침도 필요 없다. */
	postVoice(answer: 'yes' | 'no', onDone?: (json: any) => void) {
		fetch(`${CWT_API}/voice`, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ answer }),
		})
			.then(r => r.json())
			.then(json => { if (onDone) onDone(json); })
			.catch(() => { if (onDone) onDone(null); });
	}
	handleVoiceYes = () => this.answerVoice(true);
	handleVoiceNo = () => this.answerVoice(false);
	answerVoice(yes: boolean) {
		const v = this.voice;
		if (!v) return;
		if (v.role === 'player' && v.stage === 'player') {
			this.voiceDone[v.key] = true;
			if (!yes) {
				this.voice = null;
				this.forceUpdate();
				this.postVoice('no');
				return;
			}
			this.voice = { ...v, stage: 'waiting', until: Date.now() + 22000 };
			this.voiceYesAt = Date.now();
			this.voiceSeenOnBoard = false;
			this.voiceResultAsked = false;
			this.forceUpdate();
			this.postVoice('yes', json => {
				const cur = this.voice;
				if (!cur || cur.stage !== 'waiting') return;
				if (!json?.ok) { this.voiceResult('failed'); return; }
				const stage = json.data?.stage as string | null;
				if (!stage) { this.voice = null; this.forceUpdate(); return; } // 묻던 게 이미 없다(시한)
				if (stage !== 'lender') this.voiceResult(stage);
			});
			return;
		}
		if (v.role === 'lender' && v.stage === 'lender') {
			this.voiceDone[v.key] = true;
			if (!yes) {
				this.voice = null;
				this.forceUpdate();
				this.postVoice('no');
				return;
			}
			this.voice = { ...v, stage: 'moving', until: Date.now() + 25000 };
			this.forceUpdate();
			this.postVoice('yes', json => {
				const cur = this.voice;
				if (!cur || cur.stage !== 'moving') return;
				const stage = json?.ok ? (json.data?.stage as string | null) : null;
				if (!stage) { this.voiceResult(json?.ok ? 'missed' : 'failed'); return; }
				this.voiceResult(stage === 'moved' ? 'moved' : 'failed');
			});
		}
	}
	closeVoice = () => {
		if (this.voice) this.voiceDone[this.voice.key] = true;
		this.voice = null;
		this.forceUpdate();
	};
	renderVoicePopup() {
		const v = this.voice;
		if (!v) return null;
		const now = Date.now();
		if (v.until <= now) {
			// 묻는 창은 서버 마감과 같은 순간에, 결과 줄은 제 시간이 다 되면 닫힌다
			this.voice = null;
			return null;
		}
		const left = Math.max(0, Math.ceil((v.until - now) / 1000));
		const asking = v.stage === 'player' || v.stage === 'lender';
		const pending = v.stage === 'waiting' || v.stage === 'moving';
		let title = '';
		let body = '';
		if (v.stage === 'player') {
			title = `${v.name} 님에게 도움을 받을까요?`;
			body = `${v.name} 님이 수락하면 두 분만 들어갈 수 있는 통화방으로 함께 이동해요.`;
		} else if (v.stage === 'waiting') {
			title = `${v.name} 님에게 물어보고 있어요`;
			body = '답을 기다리는 동안 뽑기는 그대로 이어져요.';
		} else if (v.stage === 'lender') {
			title = `${v.name} 님이 도움을 요청했어요`;
			body = '수락하면 두 분만 들어갈 수 있는 통화방으로 함께 이동해요.';
		} else if (v.stage === 'moving') {
			title = '통화방을 여는 중이에요';
		} else if (v.stage === 'moved') {
			title = '통화방으로 이동했어요';
			body = '돌아가는 건 편할 때 직접 하면 돼요.';
		} else if (v.stage === 'declined') {
			title = `${v.name} 님이 이번엔 거절했어요`;
		} else if (v.stage === 'missed') {
			title = `${v.name} 님이 답하지 않았어요`;
		} else {
			title = '통화방을 열지 못했어요';
			body = '두 분 모두 통화방에 있어야 해요.';
		}
		return <div class={`cwt-pop cws-voice is-${v.stage}`} role="dialog" aria-modal="true" aria-label="도움 통화방"
			onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Escape' && !asking && !pending) this.closeVoice(); }}>
			<div class="cwt-pop-backdrop" onClick={asking || pending ? undefined : this.closeVoice} />
			<div class="cwt-pop-card cws-voice-card">
				<p class="cwt-pop-eyebrow">SPROUT CUP · VOICE</p>
				<span class="cws-voice-ic" aria-hidden="true">{v.stage === 'moved' ? '\u{1F331}' : '\u{1F3A7}'}</span>
				<h3 class="cws-voice-title">{title}</h3>
				{body ? <p class="cws-voice-body">{body}</p> : null}
				{asking || v.stage === 'waiting' ? <p class={`cws-voice-clock${left <= 5 ? ' is-hurry' : ''}`} aria-live="off">{left}초</p> : null}
				{pending ? <span class="cws-voice-wait" aria-hidden="true"><i /><i /><i /></span> : null}
				{v.stage === 'player' ? <div class="cws-voice-btns">
					<button class="cwt-btn" autoFocus onClick={this.handleVoiceYes}>예, 함께 통화할게요</button>
					<button class="cwt-btn is-ghost" onClick={this.handleVoiceNo}>아니오</button>
				</div> : null}
				{v.stage === 'lender' ? <div class="cws-voice-btns">
					<button class="cwt-btn" autoFocus onClick={this.handleVoiceYes}>수락</button>
					<button class="cwt-btn is-ghost" onClick={this.handleVoiceNo}>거부</button>
				</div> : null}
				{!asking && !pending ? <div class="cws-voice-btns">
					<button class="cwt-btn cwt-pop-close" autoFocus onClick={this.closeVoice}>확인</button>
				</div> : null}
			</div>
		</div>;
	}

	/** 원형 시계 — 남은 비율만큼 테두리가 줄어든다. 5초 아래면 맥박이 빨라진다. */
	renderSproutClock(left: number | null, total: number) {
		const r = 30;
		const c = 2 * Math.PI * r;
		const frac = left === null ? 0 : Math.max(0, Math.min(1, left / Math.max(1, total)));
		const sec = left === null ? null : Math.ceil(left);
		return <div class={`cws-clock${sec !== null && sec <= 5 ? ' is-hurry' : ''}`} role="timer" aria-label={sec === null ? '남은 시간 없음' : `남은 시간 ${sec}초`}>
			<svg viewBox="0 0 72 72" aria-hidden="true">
				<circle class="cws-clock-track" cx="36" cy="36" r={r} />
				<circle class="cws-clock-fill" cx="36" cy="36" r={r} style={`stroke-dasharray:${c};stroke-dashoffset:${c * (1 - frac)}`} />
			</svg>
			<b>{sec === null ? '—' : sec}</b>
			<small>초</small>
		</div>;
	}

	renderSproutAva(avatar: string | null, anonymous: boolean) {
		return <span class="cws-ava">{avatar && !anonymous ? <img src={avatar} alt="" /> : <span class="cwt-ava-blank" />}</span>;
	}

	/** 빌려준 아이 한 장. 빌려주기 동안엔 뒷면, 뽑기가 열리면 한 장씩 뒤집힌다. */
	renderLentCard(l: CwsBoard['lenders'][number], idx: number, board: CwsBoard, myTurn: boolean) {
		const ms = this.me?.sprout ?? null;
		const myId = this.myEntrantId();
		const mine = !!myId && l.entrantId === myId;
		if (board.phase === 'lend' || !l.mon) {
			return <div class={`cws-card is-back${l.registered ? ' is-ready' : ''}${mine ? ' is-mine' : ''}`} key={`${l.entrantId}-back`} style={`--cws-i:${idx}`}>
				<div class="cws-card-inner"><div class="cws-back" aria-hidden="true"><span class="cws-back-mark" /></div></div>
				<div class="cws-card-foot">
					{this.renderSproutAva(l.avatar, l.anonymous)}
					<span class="cws-lender-name">{l.name}</span>
					{l.registered
						? <span class="cws-ready" key="ready">준비 완료</span>
						: <span class="cws-wait" aria-label="고르는 중"><i /><i /><i /></span>}
				</div>
			</div>;
		}
		const mon = l.mon;
		const taken = l.takenBy.length > 0;
		const pickable = myTurn && !!ms?.pickable && ms.pickable.indexOf(l.entrantId) >= 0;
		const blocked = myTurn && !pickable && (!taken || !!ms?.shared);
		const chosen = pickable && this.pickChoice === l.entrantId;
		const just = this.sproutNews?.lenderId === l.entrantId && Date.now() - this.sproutNews.at < 1600;
		const cls = ['cws-card', 'is-face'];
		if (taken) cls.push('is-taken');
		if (pickable) cls.push('is-pickable');
		if (blocked) cls.push('is-blocked');
		if (chosen) cls.push('is-chosen');
		if (just) cls.push('is-just');
		if (mine) cls.push('is-mine');
		const takers = board.players.filter(p => l.takenBy.indexOf(p.entrantId) >= 0);
		return <button type="button" class={cls.join(' ')} key={`${l.entrantId}-face`} style={`--cws-i:${idx}`}
			data-lender={l.entrantId} disabled={!pickable || this.busy} onClick={this.handlePickChoose}
			aria-pressed={pickable ? chosen : undefined}
			aria-label={`${l.name} 님의 ${mon.name}${taken ? `, ${takers.map(t => t.name).join(', ')} 님이 데려감` : ''}${blocked ? ', 같은 종이 겹쳐 고를 수 없음' : ''}`}>
			<div class="cws-card-inner">
				<div class="cws-face">
					<div class="cws-art">
						<span class="cws-art-glow" aria-hidden="true" />
						<img class="cws-sprite" src={mon.sprite} alt="" loading="lazy" />
						{mon.shiny ? <span class="cws-shiny" title="빛나는 아이">✦</span> : null}
					</div>
					<b class="cws-mon-name">{mon.name}</b>
					<span class="cws-species">{mon.speciesKo !== mon.name ? mon.speciesKo : ' '}</span>
					<div class="cws-types">{mon.typesKo.map(t => <span class="cws-type">{t}</span>)}</div>
					<dl class="cws-meta">
						<div><dt>특성</dt><dd>{mon.abilityKo}</dd></div>
						<div><dt>도구</dt><dd>{mon.itemKo || '없음'}</dd></div>
					</dl>
					<ul class="cws-moves">{mon.movesKo.map(m => <li>{m}</li>)}</ul>
					<div class="cws-card-foot">
						{this.renderSproutAva(l.avatar, l.anonymous)}
						<span class="cws-lender-name">{l.name}</span>
						<small>님의 아이</small>
					</div>
				</div>
				<div class="cws-back" aria-hidden="true"><span class="cws-back-mark" /></div>
			</div>
			{taken ? <div class="cws-taken" key={l.takenBy.join(',')}>
				<span class="cws-taken-avas">{takers.map(t => this.renderSproutAva(t.avatar, t.anonymous))}</span>
				<b>{takers.map(t => t.name).join(' · ')}</b>
				<small>{takers.length > 1 ? '함께 써요' : '데려갔어요'}</small>
			</div> : null}
			{blocked ? <em class="cws-block">같은 종이 겹쳐요</em> : null}
			{chosen ? <span class="cws-check" aria-hidden="true">✓</span> : null}
		</button>;
	}

	/** 뽑기 순서 — 최하위부터. 지금 차례는 시간 막대가 줄어든다. */
	renderDraftOrder(board: CwsBoard, frac: number) {
		const myId = this.myEntrantId();
		const monOf = (lenderId: string | null) => board.lenders.find(l => l.entrantId === lenderId)?.mon ?? null;
		return <aside class="cws-order" aria-label="뽑기 순서">
			<div class="cws-order-head"><span class="cwt-stage-eyebrow">DRAFT ORDER</span><b>새싹 선수 {board.players.length}명</b></div>
			<ol>{board.players.map(p => {
				const st = p.out ? 'out' : p.picked ? 'done' : p.entrantId === board.turn ? 'now' : 'wait';
				const mon = monOf(p.picked);
				return <li class={`cws-seat is-${st}${p.entrantId === myId ? ' is-me' : ''}`} key={p.entrantId}>
					<span class="cws-seat-no">{p.pickOrder}</span>
					{this.renderSproutAva(p.avatar, p.anonymous)}
					<span class="cws-seat-name">{p.name}{p.entrantId === myId ? <em class="cwt-mine-label">나</em> : null}</span>
					{st === 'done' && mon ? <img class="cws-seat-mon" src={mon.sprite} alt={mon.name} title={mon.name} key={`${p.entrantId}-${p.picked}`} />
						: st === 'now' ? <span class="cws-seat-tag is-now">고르는 중</span>
						: st === 'out' ? <span class="cws-seat-tag is-out">함께할 아이가 없어요</span>
						: <span class="cws-seat-tag">{board.phase === 'lend' ? '공개 대기' : '대기'}</span>}
					{st === 'now' ? <i class="cws-seat-bar" style={`transform:scaleX(${frac})`} aria-hidden="true" /> : null}
				</li>;
			})}</ol>
		</aside>;
	}

	/** 내 자리 — 빌려주는 사람은 아이 고르기, 출전자는 순서·차례·데려온 아이. 관전자는 없음. */
	renderSproutMine(board: CwsBoard, myTurn: boolean) {
		const ms = this.me?.sprout ?? null;
		if (!ms) return null;
		if (ms.role === 'lender' && board.phase === 'lend') {
			if (!this.lendOptions && !this.lendLoading) this.loadLendOptions();
			const q = this.lendFilter.trim();
			const options = (this.lendOptions || []).filter(m => !q || m.name.indexOf(q) >= 0 || m.speciesKo.indexOf(q) >= 0);
			const chosen = (this.lendOptions || []).find(m => m.id === this.lendChoice) ?? null;
			const same = !!chosen && chosen.id === ms.lent?.id;
			return <section class="cws-mine is-lender" aria-label="빌려줄 아이 고르기" aria-busy={this.busy}>
				<div class="cws-mine-head">
					<div>
						<span class="cwt-stage-eyebrow">YOUR LOAN</span>
						<h5>{ms.lent ? <>{ms.lent.name}{cwsJosa(ms.lent.name, '을', '를')} 빌려줄게요</> : '새싹 선수에게 빌려줄 아이를 골라주세요'}</h5>
						<p>대회 동안만 쓰는 복사본이에요. 내 아이는 그대로 내 곁에 있고, 등원·온실도 평소처럼 할 수 있어요.</p>
					</div>
					{ms.lent ? <span class="cws-mine-badge" key="ok">등록 완료 · 마감 전까지 바꿀 수 있어요</span> : null}
				</div>
				<input class="cws-filter" type="search" placeholder="이름으로 찾기" value={this.lendFilter} onInput={this.handleLendFilter} aria-label="내 포켓몬 이름으로 찾기" />
				<div class="cws-opts">
					{!this.lendOptions ? <p class="cwt-note">내 포켓몬을 불러오고 있어요</p>
						: options.length === 0 ? <p class="cwt-note">찾는 아이가 없어요</p>
						: options.map(m => <button type="button" key={m.id}
							class={`cws-opt${m.id === this.lendChoice ? ' is-on' : ''}${m.id === ms.lent?.id ? ' is-current' : ''}`}
							data-mon={m.id} onClick={this.handleLendChoose} aria-pressed={m.id === this.lendChoice}>
							<img src={m.sprite} alt="" loading="lazy" />
							<b>{m.name}</b>
							<small>{m.typesKo.join(' · ')}</small>
							{m.inTeam ? <em>명단</em> : null}
						</button>)}
				</div>
				<div class="cws-mine-foot">
					<span>{chosen ? <><img src={chosen.sprite} alt="" /><b>{chosen.name}</b> · {chosen.abilityKo}{chosen.itemKo ? ` · ${chosen.itemKo}` : ''}</> : '아이를 고르면 여기에 보여요'}</span>
					<button class="cwt-btn" disabled={!chosen || same || this.busy} onClick={this.handleLend}>
						{this.busy ? '등록 중…' : same ? '이 아이로 등록됨' : ms.lent ? '이 아이로 바꿀게요' : '이 아이를 빌려줄게요'}
					</button>
				</div>
			</section>;
		}
		if (ms.role === 'lender') {
			const taker = board.players.filter(p => p.picked === this.myEntrantId());
			return <section class="cws-mine is-note">
				{ms.lent ? <img class="cws-mine-mon" src={ms.lent.sprite} alt="" /> : null}
				<div><span class="cwt-stage-eyebrow">YOUR LOAN</span>
					<h5>{ms.lent ? `내가 빌려준 ${ms.lent.name}` : '빌려준 아이가 없어요'}</h5>
					<p>{taker.length ? `${taker.map(t => t.name).join(', ')} 님과 함께 출전해요. 응원해 주세요!` : '누가 데려갈지 지켜봐요.'}</p></div>
			</section>;
		}
		// 출전자
		if (board.phase === 'lend') {
			return <section class="cws-mine is-note">
				<div><span class="cwt-stage-eyebrow">YOUR TURN</span>
					<h5>내 뽑기 순서는 {ms.pickOrder}번째예요</h5>
					<p>선배들이 아이를 고르는 중이에요. 뽑기가 시작되면 카드가 한꺼번에 공개돼요.</p></div>
			</section>;
		}
		if (ms.borrowed) {
			return <section class="cws-mine is-done" key="done">
				<img class="cws-mine-mon" src={ms.borrowed.sprite} alt="" />
				<div><span class="cwt-stage-eyebrow">YOUR PARTNER</span>
					<h5>{ms.borrowed.name}{cwsJosa(ms.borrowed.name, '과', '와')} 함께 출전해요</h5>
					<p>매 판 이 아이와 내 아이 두 마리가 나가요. 모두 고르면 대진이 공개돼요.</p></div>
			</section>;
		}
		if (myTurn) {
			const lender = board.lenders.find(l => l.entrantId === this.pickChoice);
			const mon = lender?.mon ?? null;
			return <section class="cws-mine is-turn" key="turn" aria-live="polite">
				<div><span class="cwt-stage-eyebrow">YOUR PICK</span>
					<h5>{mon ? <>{mon.name}{cwsJosa(mon.name, '과', '와')} 함께할까요?</> : '데려올 아이를 골라주세요'}</h5>
					<p>{ms.shared
						? '남은 아이가 없어서 다른 선수가 데려간 아이 중에서 함께 쓸 아이를 골라요.'
						: '시간 안에 고르지 않으면 남은 아이 중 하나가 정해져요.'}</p></div>
				<button class="cwt-btn" disabled={!mon || this.busy} onClick={this.handlePickConfirm}>{this.busy ? '데려오는 중…' : '이 아이와 함께할게요'}</button>
			</section>;
		}
		const ahead = board.players.filter(p => !p.picked && !p.out && p.pickOrder < (ms.pickOrder ?? 0)).length;
		return <section class="cws-mine is-note">
			<div><span class="cwt-stage-eyebrow">YOUR TURN</span>
				<h5>{ahead > 0 ? `내 차례까지 ${ahead}명 남았어요` : '곧 내 차례예요'}</h5>
				<p>마음에 드는 아이를 미리 봐 두세요. 내 차례가 되면 카드를 눌러 고를 수 있어요.</p></div>
		</section>;
	}

	renderSprout() {
		const view = this.view!;
		const board = view.sprout!;
		const myId = this.myEntrantId();
		const myTurn = !!myId && board.turn === myId;
		const left = this.phaseEndsAt ? Math.max(0, (this.phaseEndsAt - Date.now()) / 1000) : null;
		const frac = left === null ? 0 : Math.max(0, Math.min(1, left / Math.max(1, board.turnSec)));
		const turnP = board.players.find(p => p.entrantId === board.turn) ?? null;
		const lend = board.phase === 'lend';
		const ready = board.lenders.filter(l => l.registered).length;
		const news = this.sproutNews && Date.now() - this.sproutNews.at < 4200 ? this.sproutNews : null;
		return <section class={`cws is-${board.phase}${myTurn ? ' is-my-turn' : ''}`} aria-label={lend ? '새싹컵 빌려주기' : '새싹컵 뽑기'}>
			{/*
			  ⚠️ 형제마다 key 를 고정한다. 내 자리 패널이 차례에 따라 종류가 바뀌는데(대기·내 차례·데려옴),
			  key 없이 두면 형제 짝이 어긋나 아래 카드판까지 새로 만들어져 뒤집기 연출이 처음부터 다시 돈다.
			*/}
			<div class="cws-hero" key="hero">
				<div class="cws-hero-copy">
					<span class="cwt-stage-eyebrow">{lend ? 'SPROUT CUP · LENDING' : 'SPROUT CUP · DRAFT'}</span>
					<h4 key={`${board.phase}-${board.turn}`}>{lend ? '선배들이 함께할 아이를 고르고 있어요'
						: myTurn ? '내 차례예요!'
						: turnP ? `${turnP.name} 님이 고르는 중`
						: board.voiceWait ? '도움 통화방 대답을 기다리고 있어요'
						: '곧 대진이 공개돼요'}</h4>
					{/*
					  방금 누가 데려갔는지는 머리 안내문 자리에 잠깐 띄운다. 대회장 바깥 상자가 overflow:hidden 이라
					  화면 아래에 붙이는 sticky 띠는 듣지 않는다 — 늘 보이는 머리가 제일 확실한 자리다.
					*/}
					{news ? <p class="cws-news" key={news.key} role="status">
						{news.sprite ? <img src={news.sprite} alt="" /> : null}<span>{news.text}</span>
					</p> : <p>{lend
						? `빌려줄 아이 ${ready} / ${board.lenders.length} 준비 완료 · 카드는 뽑기가 시작될 때 한꺼번에 공개돼요`
						: '최하위부터 한 명씩, 빌려준 아이 중 하나를 데려가요. 데려온 아이는 매 판 함께 나가요.'}</p>}
				</div>
				{/* 다 고르고 대진 공개를 기다리는 몇 초는 셀 차례가 없다 — 「0」 을 세워 두지 않는다 */}
				{lend || board.turn ? this.renderSproutClock(left, board.turnSec) : null}
			</div>
			<div class="cws-mine-slot" key="mine">{this.renderSproutMine(board, myTurn)}</div>
			<div class="cws-stage" key="stage">
				{/* 카드를 나눠 주는(뒤집는) 동안에는 그 위에 얹는 도장·잠금을 뒤집힌 다음에 띄운다 */}
				<div class={`cws-grid${lend ? ' is-backs' : ''}${!lend && Date.now() - this.sproutFlipAt < board.lenders.length * 280 + 900 ? ' is-dealing' : ''}`}>{board.lenders.map((l, i) => this.renderLentCard(l, i, board, myTurn))}</div>
				{this.renderDraftOrder(board, frac)}
			</div>
		</section>;
	}

	// ---- 마왕토벌전 (2026-10-12) ---------------------------------------------------

	handleRaidVote = (e: Event) => {
		const id = (e.currentTarget as HTMLElement).getAttribute('data-entrant');
		if (id) this.post('/raid/vote', { entrantId: id });
	};
	/** 리더의 지명 — 이미 지명한 사람을 다시 누르면 거둔다. */
	handleRaidName = (e: Event) => {
		const id = (e.currentTarget as HTMLElement).getAttribute('data-entrant');
		if (!id) return;
		this.post('/raid/pick', { entrantId: this.me?.raid?.next === id ? null : id });
	};
	/**
	 * 아이 고르기. 마왕 선봉·1마리판 토벌대는 누른 아이 하나. 2마리판 토벌대(2026-11-01)는 누를 때마다 고른 목록에 넣고 빼며,
	 * 넘치면 먼저 고른 아이가 빠진다. 고른 순서가 출전 순서(앞이 선봉), 모자란 자리는 서버가 명단 앞쪽으로 채운다.
	 */
	handleRaidMon = (e: Event) => {
		const id = (e.currentTarget as HTMLElement).getAttribute('data-mon');
		const me = this.me?.raid;
		if (!id || !me) return;
		const size = me.team === 'raider' ? (me.raiderSize || 1) : 1;
		if (size <= 1) {
			this.post('/raid/pick', { monId: id });
			return;
		}
		const cur = (me.picks || []).slice();
		const at = cur.indexOf(id);
		if (at >= 0) cur.splice(at, 1);
		else {
			cur.push(id);
			while (cur.length > size) cur.shift();
		}
		this.post('/raid/pick', { monIds: cur.length ? cur : null });
	};
	/** 「나 갈래」 토글 — 지금 출전 예정인 아이(들)로 손든다. 이미 들었으면 내린다. */
	handleRaidHand = () => {
		const me = this.me?.raid;
		if (!me) return;
		const ids = this.raidIntents(me).map(x => x.id);
		this.post('/raid/hand', me.hand || !ids.length ? { monIds: null } : { monIds: ids });
	};

	/** 회차 마릿수 — 판이 열리기 전(접수 화면)은 view 의 값, 옛 서버면 3·1. */
	raidSizes(): CwrSizes {
		const v = this.view;
		return (v && (v.raidSizes || (v.raid && v.raid.sizes))) || CWR_SIZES_DEFAULT;
	}
	/** 지명되면 나갈 아이들 — 옛 서버 응답(`intent` 하나)도 같은 모양으로. */
	raidIntents(x: { intent?: CwrIntent | null; intents?: CwrIntent[] }): CwrIntent[] {
		return x.intents && x.intents.length ? x.intents : x.intent ? [x.intent] : [];
	}

	raidPhaseLabel(board: CwrBoard): string {
		switch (board.phase) {
		case 'reveal': return '선수 공개';
		case 'vote': return '리더 투표';
		case 'pick': return '다음 주자 선택';
		case 'flip': return '동시 공개';
		case 'battle': return `${board.battleNo}번째 판`;
		default: return board.winner === 'boss' ? '마왕 승리' : '토벌대 승리';
		}
	}

	/** 아이 칩 — 마왕의 벤치는 한 번 나오기 전까지 물음표. 토벌대는 나왔던 아이만 온다. */
	renderRaidMons(card: CwrCard, team: 'boss' | 'raider') {
		if (!card.mons.length) return null;
		return <div class="cwr-mons">{card.mons.map((m, i) => {
			if (!m.seen) return <span class="cwr-mon is-hidden" key={i} title="아직 나오지 않은 아이"><i>?</i></span>;
			const hp = Math.max(0, Math.min(100, m.hp));
			const tone = m.fainted ? 'out' : hp <= 25 ? 'low' : hp <= 50 ? 'mid' : 'ok';
			return <span class={`cwr-mon is-${tone}`} key={i} title={`${m.name ?? ''} ${m.fainted ? '쓰러짐' : `HP ${hp}%`}${m.status ? ` · ${CWR_STATUS_KO[m.status] ?? m.status}` : ''}`}>
				{m.sprite ? <img src={m.sprite} alt={m.name ?? ''} /> : <i>?</i>}
				<b class="cwr-hp"><i style={`width:${m.fainted ? 0 : hp}%`} /></b>
				{m.status && !m.fainted ? <em class={`cwr-st is-${m.status}`}>{CWR_STATUS_KO[m.status] ?? m.status}</em> : null}
				{m.fainted ? <em class="cwr-st is-out">기절</em> : null}
			</span>;
		})}</div>;
	}

	renderRaidCard(card: CwrCard, team: 'boss' | 'raider', board: CwrBoard) {
		const me = this.me?.raid ?? null;
		const myId = this.myEntrantId();
		const mine = card.entrantId === myId;
		const myTeam = me?.team === team;
		const next = team === 'boss' ? board.next.boss : board.next.raider;
		const canVote = board.phase === 'vote' && myTeam && card.alive && !!myId && this.me?.entrant?.status === 'alive';
		// 리더는 서 있는 사람을 못 바꾼다 — 두 팀 다 (마왕은 2026-11-01 부터). 서 있는 사람이 없을 때만 지명한다.
		const canName = board.phase === 'pick' && myTeam && !!me?.needNext && card.alive && !card.standing;
		const named = me?.next === card.entrantId;
		const cls = ['cwr-card', `is-${team}`];
		if (!card.alive) cls.push('is-out');
		if (card.standing) cls.push('is-standing');
		if (next === card.entrantId) cls.push('is-next');
		if (mine) cls.push('is-mine');
		if (card.leader) cls.push('is-leader');
		if (canVote || canName) cls.push('is-clickable');
		if (named) cls.push('is-named');
		if (me?.vote === card.entrantId && board.phase === 'vote') cls.push('is-voted');
		const mate = me?.mates.find(m => m.entrantId === card.entrantId) ?? null;
		const inner = <>
			{this.renderSproutAva(card.avatar, card.anonymous)}
			<div class="cwr-card-main">
				<div class="cwr-card-head">
					<span class="cwr-name" title={card.name}>{card.name}</span>
					{card.leader ? <em class="cwr-tag is-leader" title="리더">리더</em> : null}
					{mine ? <em class="cwt-mine-label">나</em> : null}
					{card.standing && board.phase !== 'done' ? <em class="cwr-tag is-standing">{team === 'boss' ? '서 있는 마왕' : '도전 중'}</em> : null}
					{next === card.entrantId ? <em class="cwr-tag is-next">다음</em> : null}
					{!card.alive ? <em class="cwr-tag is-out">쓰러짐</em> : null}
				</div>
				<div class="cwr-card-sub">
					{card.wins > 0 ? <span class="cwr-wins" title={team === 'boss' ? `${card.wins}명 격파` : `${card.wins}명 쓰러뜨림`}>{'●'.repeat(Math.min(card.wins, 6))}<small>{card.wins}</small></span> : null}
					{card.votes > 0 ? <span class="cwr-votes">{card.votes}표</span> : null}
					{myTeam && mate && this.raidIntents(mate).length && card.alive && !card.standing
						? ((list: CwrIntent[]) => <span class={`cwr-intent is-${list.every(x => x.source === 'default') ? 'default' : list[0]!.source}${card.hand ? ' is-hand' : ''}`} title={`${card.hand ? '나 갈래 · ' : ''}${list.map(x => x.name).join(' · ')}${list.every(x => x.source === 'default') ? ' (아직 안 골라 기본값)' : ''}`}>
							{card.hand ? <i>✋</i> : null}{list.map(x => <img src={x.sprite} alt="" key={x.id} />)}<small>{list.map(x => x.name).join(' · ')}</small>
						</span>)(this.raidIntents(mate))
						: card.hand && card.alive && !card.standing ? <span class="cwr-hand" title="나 갈래">✋</span> : null}
				</div>
				{this.renderRaidMons(card, team)}
			</div>
			{named ? <span class="cws-check" aria-hidden="true">✓</span> : null}
		</>;
		if (canVote || canName) {
			return <button type="button" class={cls.join(' ')} key={card.entrantId} data-entrant={card.entrantId} disabled={this.busy}
				onClick={canVote ? this.handleRaidVote : this.handleRaidName}
				aria-pressed={canVote ? me?.vote === card.entrantId : named}
				aria-label={`${card.name}${canVote ? '에게 투표' : named ? ' 지명 취소' : ' 다음 주자로 지명'}`}>{inner}</button>;
		}
		return <div class={cls.join(' ')} key={card.entrantId}>{inner}</div>;
	}

	/** 내 자리 — 내 아이 고르기(선봉·출전)·손들기, 리더 안내. 관전자는 없음. 창이 아닐 때도 미리 고른다. */
	renderRaidMine(board: CwrBoard) {
		const me = this.me?.raid ?? null;
		const ent = this.me?.entrant ?? null;
		if (!me || !ent) return null;
		const alive = ent.status === 'alive';
		const teamKo = me.team === 'boss' ? '마왕' : '토벌대';
		if (board.phase === 'vote') {
			return <section class="cws-mine is-turn cwr-mine" aria-label="리더 투표">
				<div><span class="cwt-stage-eyebrow">{teamKo.toUpperCase()} TEAM · VOTE</span>
					<h5>{alive ? '우리 팀 리더에게 투표해 주세요' : '리더 투표가 진행 중이에요'}</h5>
					<p>아래 {teamKo} 명단에서 카드를 누르면 투표돼요. 동점이면 점수가 높은 분이 리더가 돼요. 리더는 매 판 다음 주자를 골라요.{me.vote ? ' 다시 누르면 바꿀 수 있어요.' : ''}</p></div>
			</section>;
		}
		if (!alive || board.phase === 'done' || board.phase === 'reveal') return null;
		const standing = me.team === 'boss' ? board.standing.boss : board.standing.raider;
		const iStand = standing === this.myEntrantId();
		const named = me.next === this.myEntrantId() || board.next.raider === this.myEntrantId() || board.next.boss === this.myEntrantId();
		const picking = board.phase === 'pick';
		const picks = me.mons.filter(m => !m.fainted);
		const intents = this.raidIntents(me);
		const intentIds = intents.map(x => x.id);
		const raiderSize = me.raiderSize || 1;
		const multi = me.team === 'raider' && raiderSize > 1;
		// 서 있는 토벌대원은 싸운 아이로 계속 싸운다 — 고를 게 없다 (2마리판은 살아남은 아이만)
		if (iStand && me.team === 'raider') {
			return picking ? <section class="cws-mine is-turn cwr-mine" aria-label="선택 창"><div>
				<span class="cwt-stage-eyebrow">RAIDER TEAM · PICK</span><h5>다음 마왕을 기다려요</h5>
				<p>{multi ? '살아남은 아이 그대로 다음 마왕을 상대해요. 쓰러진 자리는 채워지지 않고, 체력은 이어져요.' : '한 마리 그대로 다음 마왕을 상대해요. 체력은 이어져요.'}</p></div></section> : null;
		}
		const monRole = me.team === 'boss' ? '선봉' : multi ? '출전할 아이들' : '출전할 아이';
		// 공개 단계 (2026-11-01) — 나갈 마왕은 도전자를 보고 선봉을 바꾼다(누르면 곧 시작), 나갈 도전자는 아이가 굳었다
		const leadOpen = board.phase === 'flip' && me.flipFighter === 'boss';
		const locked = board.phase === 'flip' && me.flipFighter === 'raider';
		const turn = picking || leadOpen;
		let title: string;
		let desc: string;
		if (leadOpen) {
			title = '도전자가 공개됐어요! 선봉을 정해 주세요';
			desc = '아래에서 누르면 그 아이가 선봉으로 나가고 곧 판이 열려요. 지금 선봉 그대로 가려면 그 아이를 한 번 더 누르세요. 안 누르면 시간이 끝날 때 지금 선봉으로 시작해요.';
		} else if (locked) {
			title = '곧 판이 열려요';
			desc = '이번 판에 나갈 아이는 정해졌어요. 마왕이 선봉을 고르는 동안 잠깐 기다려 주세요.';
		} else if (picking && me.needNext) {
			title = '리더님, 다음 주자를 골라 주세요';
			desc = `아래 ${teamKo} 명단에서 카드를 누르면 지명돼요. 카드마다 그 분이 낼 아이가 보여요${me.team === 'raider' ? ' (✋ 는 손든 분)' : ''}. 한 번 나간 분은 쓰러질 때까지 계속 싸워요. 시간 안에 안 고르면 점수가 가장 낮은 분이 나가요.`;
		} else if (picking && (iStand || named)) {
			title = me.team === 'boss' ? (iStand ? '선봉을 정해 주세요' : '다음 마왕으로 지명됐어요!') : '다음 도전자로 지명됐어요!';
			desc = me.team === 'boss' ? '지금 서 있던 아이가 기본 선봉이에요. 바꾸려면 아래에서 누르세요. 체력은 그대로 이어져요.' : `아래에서 고른 ${cwrCountKo(raiderSize)}로 나가요.${multi ? ' 먼저 고른 아이가 선봉이에요.' : ''} 이기면 그 체력 그대로 다음 마왕과 싸워요.`;
		} else if (picking) {
			title = me.team === 'boss' ? '리더가 다음 마왕을 고르고 있어요' : '리더가 다음 도전자를 고르고 있어요';
			desc = me.team === 'boss' ? '지명되면 아래에서 고른 아이가 선봉으로 나가요.' : `아래에서 낼 아이${multi ? ` ${cwrCountKo(raiderSize)}` : ''}를 골라 두세요. 리더 화면에 그 아이가 보여요. 「나 갈래」를 누르면 손이 올라가요.`;
		} else {
			title = '다음 판을 준비해요';
			desc = me.team === 'boss' ? '지명되면 나갈 선봉을 미리 골라 둘 수 있어요.' : '낼 아이를 미리 골라 두면 리더 화면에 보여요. 「나 갈래」로 손도 들 수 있어요.';
		}
		return <section class={`cws-mine cwr-mine${turn ? ' is-turn' : ''}`} aria-label={leadOpen ? '선봉 고르기' : picking ? '선택 창' : '다음 판 준비'}>
			<div>
				<span class="cwt-stage-eyebrow">{teamKo.toUpperCase()} TEAM · {leadOpen ? 'LEAD' : picking ? 'PICK' : 'READY'}</span>
				<h5>{title}</h5>
				<p>{desc}</p>
				{intents.length ? <p class="cwr-intent-line">{intents.map(x => <img src={x.sprite} alt="" key={x.id} />)}<b>{intents.map(x => x.name).join(' · ')}</b><span>{intents.every(x => x.source === 'default') ? `아직 안 골라서 기본 ${monRole}이에요` : `지금 ${monRole}`}</span></p> : null}
			</div>
			{picks.length && !locked ? <div class="cwr-picks" role="group" aria-label={monRole}>
				{picks.map(m => {
					const order = intentIds.indexOf(m.id);
					const on = order >= 0;
					return <button type="button" class={`cwt-slot${on ? ' is-on' : ''}`} key={m.id} data-mon={m.id} disabled={this.busy}
						onClick={this.handleRaidMon} aria-pressed={on}
						title={`${m.name} · HP ${m.hp}%${m.status ? ` · ${CWR_STATUS_KO[m.status] ?? m.status}` : ''}`}>
						<img class="cwt-slot-img" src={m.sprite} alt="" />
						<span class="cwt-slot-name">{m.name}</span>
						<b class="cwr-hp"><i style={`width:${m.hp}%`} /></b>
						{on ? <em class="cwt-slot-order">{multi ? (order === 0 ? '선봉' : `${order + 1}번`) : monRole}</em> : null}
					</button>;
				})}
				{me.team === 'raider' && !iStand ? <button type="button" class={`cwt-btn${me.hand ? '' : ' is-ghost'} cwr-hand-btn`} disabled={this.busy || !intentIds.length} onClick={this.handleRaidHand} aria-pressed={!!me.hand}>
					{me.hand ? '✋ 손들었어요 · 내리기' : '✋ 나 갈래'}
				</button> : null}
			</div> : null}
		</section>;
	}

	/** 가운데 무대 — 지금 판, 또는 창·공개. */
	renderRaidStage(board: CwrBoard) {
		const view = this.view!;
		const byId: { [id: string]: CwrCard } = {};
		for (const c of board.bosses) byId[c.entrantId] = c;
		for (const c of board.raiders) byId[c.entrantId] = c;
		// 공개된 「다음」이 있으면 그 사람이 다음 판의 주자다 — 서 있는 사람(교체 전)을 먼저 보이면 공개 연출이 이전 출전자를
		// 보여 주고 판이 열려서야 바뀐다 (2026-10-13 사용자 신고). 서버는 flip 부터 next 를 내리고, 판이 열리면 standing 으로 옮긴다.
		const bossId = board.next.boss ?? board.standing.boss;
		const raiderId = board.next.raider ?? board.standing.raider;
		const boss = bossId ? byId[bossId] : null;
		const raider = raiderId ? byId[raiderId] : null;
		const match = view.matches.length ? view.matches[view.matches.length - 1] : null;
		const live = match && match.round === board.battleNo && (match.state === 'live' || match.state === 'calling' || match.state === 'ready') ? match : null;
		const left = this.phaseEndsAt ? Math.max(0, (this.phaseEndsAt - Date.now()) / 1000) : null;
		const total = board.phase === 'vote' ? board.voteSec : board.phase === 'pick' ? board.pickSec : board.phase === 'flip' ? board.flipSec : view.revealSec;
		const myId = this.myEntrantId();
		const side = (card: CwrCard | null, team: 'boss' | 'raider', hidden: boolean) => <div class={`cwr-fighter is-${team}${card && card.entrantId === myId ? ' is-mine' : ''}${hidden ? ' is-hidden' : ''}`}>
			<span class="cwt-stage-eyebrow">{team === 'boss' ? 'DEMON KING' : 'CHALLENGER'}</span>
			{hidden || !card
				? <div class="cwr-fighter-card is-unknown"><span class="cws-ava"><span class="cwt-ava-blank" /></span><b>{hidden ? '고르는 중' : '대기'}</b></div>
				: <div class="cwr-fighter-card">{this.renderSproutAva(card.avatar, card.anonymous)}<b>{card.name}</b>
					{card.wins > 0 ? <small>{team === 'boss' ? `${card.wins}명 격파` : `${card.wins}명 쓰러뜨림`}</small> : null}
					{team === 'boss' ? this.renderRaidMons(card, 'boss') : this.renderRaidMons(card, 'raider')}
				</div>}
		</div>;
		const hidden = board.phase === 'pick' || board.phase === 'vote';
		return <section class={`cwr-stage is-${board.phase}`} aria-label="지금 판">
			<div class="cwr-stage-head">
				<span class="cwt-stage-eyebrow">DEMON KING RAID</span>
				<h4 key={`${board.phase}-${board.battleNo}`}>{board.phase === 'battle' ? `${board.battleNo}번째 판` : board.phase === 'flip' ? `${board.battleNo + 1}번째 판, 동시 공개` : board.phase === 'done' ? (board.winner === 'boss' ? '마왕 팀의 승리' : '토벌대의 승리') : this.raidPhaseLabel(board)}</h4>
				<p>마왕 <b>{board.bossesLeft}</b>명 · 토벌대 <b>{board.raidersLeft}</b>명 남음</p>
			</div>
			<div class="cwr-vs">
				{/* 창 동안 서 있는 사람은 그대로 보이고, 아직 안 정해진 자리만 「고르는 중」 (다음 주자는 서버가 공개 전까지 안 내린다) */}
				{side(raider, 'raider', hidden && !raider)}
				<div class="cwr-vs-mid">
					{board.phase === 'vote' || board.phase === 'pick' || board.phase === 'flip' ? this.renderSproutClock(left, total) : <span class="cwt-final-vs" aria-hidden="true">VS</span>}
					{live && live.state === 'live' && live.roomId ? <button class="cwt-btn" data-room={live.roomId} onClick={this.handleWatch}>{live.p1?.entrantId === myId || live.p2?.entrantId === myId ? '내 배틀로' : '관전하기'}</button> : null}
					{live && live.state === 'calling' && live.callSecLeft !== null ? <span class="cwt-calling">호출 중 {live.callSecLeft}초</span> : null}
					{live && live.state === 'ready' ? <span class="cwt-wait">곧 시작</span> : null}
					{live && live.alive ? <span class="cwr-alive">{live.alive[1]} 대 {live.alive[0]}</span> : null}
				</div>
				{side(boss, 'boss', hidden && !boss)}
			</div>
		</section>;
	}

	/** 공개 연출 (마왕토벌전) — 마왕 먼저, 토벌대 뒤. 대진 칸이 없으니 카드만 가운데에 세운다. */
	renderRaidReveal(board: CwrBoard) {
		const view = this.view!;
		const order = view.revealOrder || [];
		const count = this.revealedCount();
		const elapsed = this.revealElapsed();
		const byId: { [id: string]: { card: CwrCard, team: 'boss' | 'raider' } } = {};
		for (const c of board.bosses) byId[c.entrantId] = { card: c, team: 'boss' };
		for (const c of board.raiders) byId[c.entrantId] = { card: c, team: 'raider' };
		const left = this.phaseEndsAt ? Math.max(0, Math.ceil((this.phaseEndsAt - Date.now()) / 1000)) : null;
		if (elapsed < CWT_REVEAL_LEAD_MS) {
			return <div class="cwt-reveal cwr-reveal"><div class="cwt-reveal-title">{view.title}</div><p class="cwt-note">마왕과 토벌대를 소개합니다</p></div>;
		}
		if (order.length && count > order.length) {
			return <div class="cwt-reveal cwr-reveal"><span class="cwt-reveal-kicker">소개 완료</span>
				<div class="cwt-reveal-go" key={left !== null && left <= 3 ? left : 'ready'}>{left !== null && left <= 3 ? left || 'START' : '잠시 후 리더 투표가 열려요'}</div></div>;
		}
		const cur = byId[order[count - 1] || ''];
		if (!cur) return null;
		return <div class="cwt-reveal cwr-reveal">
			<div class={`cwt-reveal-card is-${cur.team}`} key={cur.card.entrantId}>
				<span class="cwt-reveal-kicker">{cur.team === 'boss' ? '마왕 등장' : '토벌대 입장'} {count} / {order.length}</span>
				<div class="cwt-reveal-visual"><div class="cwt-reveal-trainer">{cur.card.avatar && !cur.card.anonymous
					? <img class="cwt-reveal-face" src={cur.card.avatar} alt="" />
					: <span class="cwt-reveal-silhouette" aria-hidden="true">?</span>}</div></div>
				<b>{cur.card.name}</b>
				<small class="cwr-reveal-role">{cur.team === 'boss' ? `${cwrCountKo(this.raidSizes().boss)}로 성을 지켜요` : `${cwrCountKo(this.raidSizes().raider)}로 도전해요`}</small>
			</div>
			<div class="cwt-reveal-progress" role="progressbar" aria-valuemin={0} aria-valuemax={order.length} aria-valuenow={count}><i style={`transform:scaleX(${Math.min(count / order.length, 1)})`} /></div>
		</div>;
	}

	renderRaid() {
		const view = this.view!;
		const board = view.raid!;
		const reveal = board.phase === 'reveal';
		const sizes = this.raidSizes();
		return <section class={`cwr is-${board.phase}${sizes.boss > 3 ? ' is-six' : ''}`} aria-label="마왕토벌전">
			{reveal ? <div class="cwr-reveal-wrap">{this.renderRaidReveal(board)}</div> : <>
				{this.renderRaidStage(board)}
				<div class="cws-mine-slot" key="mine">{this.renderRaidMine(board)}</div>
			</>}
			<div class="cwr-teams">
				<div class="cwr-team is-raider" aria-label="토벌대">
					<div class="cwr-team-head"><span class="cwt-stage-eyebrow">RAIDERS</span><b>토벌대 {board.raidersLeft}<small> / {board.raiders.length}</small></b><p>{cwrCountKo(sizes.raider)}로 도전. 이기면 그 체력 그대로 다음 마왕을 상대해요.</p></div>
					<div class="cwr-list">{board.raiders.filter(c => !reveal || this.isRevealed(c.entrantId)).map(c => this.renderRaidCard(c, 'raider', board))}</div>
				</div>
				<div class="cwr-team is-boss" aria-label="마왕">
					<div class="cwr-team-head"><span class="cwt-stage-eyebrow">DEMON KINGS</span><b>마왕 {board.bossesLeft}<small> / {board.bosses.length}</small></b><p>{cwrCountKo(sizes.boss)}로 버텨요. 체력과 상태이상은 다음 도전자에게 그대로 이어져요.</p></div>
					<div class="cwr-list">{board.bosses.filter(c => !reveal || this.isRevealed(c.entrantId)).map(c => this.renderRaidCard(c, 'boss', board))}</div>
				</div>
			</div>
		</section>;
	}

	/** 끝 — 이긴 팀. 우승자 한 명이 아니라 팀이다. */
	renderRaidCeremony() {
		const view = this.view!;
		const board = view.raid;
		if (!board || view.status !== 'done' || !board.winner) return null;
		const winners = (board.winner === 'boss' ? board.bosses : board.raiders);
		const survivors = winners.filter(c => c.alive);
		const top = winners.slice().sort((a, b) => b.wins - a.wins)[0];
		return <section class="cwt-award is-celebrating cwr-award" aria-label="마왕토벌전 결과">
			{this.renderConfetti()}
			{this.renderPodium(true)}
			<div class="cwt-champ">
				<h4>{view.title} · {board.winner === 'boss' ? '마왕 팀 승리' : '토벌대 승리'}</h4>
				<div class="cwr-award-row">{(survivors.length ? survivors : winners).slice(0, 8).map(c => <div class="cwt-champ-card" key={c.entrantId}>
					{this.renderSproutAva(c.avatar, c.anonymous)}<b>{c.name}</b></div>)}</div>
				<p class="cwt-award-caption">{board.battleNo}판 만에 {board.winner === 'boss' ? '성을 지켰어요' : '마왕을 모두 쓰러뜨렸어요'}{top && top.wins > 0 ? ` · 가장 많이 쓰러뜨린 사람 ${top.name} (${top.wins}명)` : ''}</p>
			</div>
		</section>;
	}

	// ---- 참가·라인업 ----------------------------------------------------------

	renderSignup() {
		const view = this.view!;
		const me = this.me?.entrant ?? null;
		const starts = view.startsAt ? new Date(view.startsAt) : null;
		const scheduled = starts && Number.isFinite(starts.getTime()) ? starts : null;
		const sprout = view.mode === 'sprout';
		const raid = view.mode === 'raid';
		const swiss = view.mode === 'swiss';
		const rs = this.raidSizes();
		return <section class={`cwt-signup cwt-lobby${me ? ' is-registered' : ''}${sprout ? ' is-sprout' : ''}${raid ? ' is-raid' : ''}${swiss ? ' is-swiss' : ''}`} aria-label="대회 참가 접수">
			<div class="cwt-lobby-intro">
				<span class="cwt-stage-eyebrow">{sprout ? 'SPROUT CUP REGISTRATION' : raid ? 'DEMON KING RAID REGISTRATION' : swiss ? 'ROUND ROBIN CUP REGISTRATION' : 'CHERISH CUP REGISTRATION'}</span>
				<h4>{me ? '당신의 무대가 기다립니다' : sprout ? '처음이어도 괜찮아요' : raid ? (rs.raider > 1 ? `${cwrCountKo(rs.raider)}면 충분해요` : '한 마리면 충분해요') : swiss ? '끝까지 다 뛰어요' : '오늘의 무대에 도전하세요'}</h4>
				{sprout
					? <p>선배의 아이와 함께 무대에 올라요.<br />신청이 마감되면 실력 순으로 반을 나눠요.</p>
					: raid
					? <p>점수가 높은 몇 분이 마왕이 되어 {cwrCountKo(rs.boss)}로 버티고,<br />나머지가 {cwrCountKo(rs.raider)}씩 들고 차례로 도전해요.</p>
					: swiss
					? <p>탈락 없이 전원이 매 라운드 동시에 싸워요.<br />마지막엔 1·2위가 여섯 마리로 결승 한 판.</p>
					: <p>여섯 파트너와 함께, 마지막 무대까지.<br />대진은 개막과 함께 공개됩니다.</p>}
				{sprout
					? <div class="cwt-lobby-facts"><span><b>2</b>마리부터 신청</span><span><b>1</b>마리 빌려 출전</span><span><b>1</b>명의 우승자</span></div>
					: raid
					? <div class="cwt-lobby-facts"><span><b>{rs.raider}</b>마리부터 신청</span><span><b>{rs.boss}</b>마리 마왕</span><span><b>1</b>팀의 승리</span></div>
					: swiss
					? <div class="cwt-lobby-facts"><span><b>6</b>마리 등록</span><span><b>3</b>마리씩 전원 동시</span><span><b>6</b>대6 결승</span></div>
					: <div class="cwt-lobby-facts"><span><b>6</b>마리 등록</span><span><b>3</b>마리 출전</span><span><b>1</b>명의 우승자</span></div>}
				<div class="cwt-lobby-time"><span>시작 예정 (한국 시간)</span>{scheduled
					? <time dateTime={scheduled.toISOString()}>{scheduled.toLocaleString('ko-KR', {timeZone:'Asia/Seoul', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit'})}</time>
					: <b>운영진이 곧 안내합니다</b>}</div>
				<img class="cwt-lobby-trophy" src="/cherish/tour/midnight/trophy.png" alt="" />
			</div>
			<div class="cwt-lobby-entry" aria-busy={this.busy}>
				<div class="cwt-entry-heading"><span class="cwt-stage-eyebrow">YOUR ENTRY</span><span class="cwt-entry-status">{me ? '참가 신청 완료' : view.signupClosed ? '접수 마감' : '참가 접수 중'}</span></div>
				<h4 class="cwt-entry-title">{me ? '무대에 오를 준비 완료' : '당신의 자리를 예약하세요'}</h4>
				{me ? <>
				<p class="cwt-signed"><b>{me.displayName}</b><span>{me.anonymous ? '익명으로 참가합니다' : '이 이름으로 참가합니다'}</span></p>
				{sprout ? <p class="cwt-note">
					마감되면 위쪽 절반은 <b>아이 한 마리를 빌려주고</b>, 아래쪽 절반이 그 아이를 데려가 함께 출전해요.
					출전하게 되면 데려온 아이와 「내 포켓몬」 명단의 아이 둘이 매 판 나가요.
				</p> : raid ? <p class="cwt-note">
					마감되면 {rs.boss > 3 ? '6대6' : '1대1'} 점수 순으로 위에서 몇 분이 <b>마왕</b>이 돼요. 마왕은 「내 포켓몬」 명단 <b>앞 {rs.boss}마리</b>가 그대로 잠기니 순서를 봐 두세요.
					토벌대는 명단 안에서 판마다 {cwrCountKo(rs.raider)}를 골라요.
				</p> : swiss ? <p class="cwt-note">
					시작 전까지 「내 포켓몬」에서 <b>명단 6칸</b>을 자유롭게 바꿀 수 있어요.
					시작하는 순간 그 6마리로 잠기고, 라운드마다 그 안에서 3마리를 골라요. 결승에 오르면 <b>6마리 전부</b> 나가요.
				</p> : <p class="cwt-note">
					시작 전까지 「내 포켓몬」에서 <b>명단 6칸</b>을 자유롭게 바꿀 수 있어요.
					시작하는 순간 그 6마리로 잠기고, 그 안에서 판마다 3마리를 골라요.
				</p>}
				{/* 마감(운영진 검토) 뒤에는 취소가 없다 (2026-10-12) — 빠지는 건 운영진이 한다 */}
				{view.signupClosed
					? <p class="cwt-note"><b>접수가 마감됐어요.</b> 곧 시작해요. 배틀 사이트에서 기다려주세요.</p>
					: <button class="cwt-btn is-ghost" disabled={this.busy} onClick={this.handleWithdraw}>신청 취소</button>}
				</> : view.signupClosed ? <>
			<p class="cwt-note"><b>접수가 마감됐어요.</b> 다음 대회에서 만나요.</p>
				</> : <>
			<p class="cwt-note">{sprout
				? <>포켓몬이 <b>2마리</b>만 있어도 참가할 수 있어요. 잘하는 분은 새싹 선수에게 아이를 빌려주는 역할을 맡아요.</>
				: raid
				? <>포켓몬이 <b>{rs.raider}마리</b>만 있어도 참가할 수 있어요. 마왕이 되려면 명단에 {rs.boss}마리가 있어야 해요.</>
				: swiss
				? <>「내 포켓몬」의 <b>명단 6칸</b>으로 참가해요. 아무도 탈락하지 않으니 마지막 라운드까지 함께 뛰어요.</>
				: <>「내 포켓몬」의 <b>명단 6칸</b>으로 참가해요. 대회가 시작되기 전까지 팀을 준비해주세요.</>}
			</p>
			{raid ? <p class="cwt-note is-dim">마왕토벌전은 실명으로만 참가해요. 리더·지명·손들기가 전부 이름으로 오가요.</p>
				: swiss ? <p class="cwt-note is-dim">빙글빙글컵은 실명으로만 참가해요.</p>
				: <label class="cwt-check">
				<input type="checkbox" checked={this.anonWanted} onChange={this.handleAnonToggle} />
				<span>익명으로 참가하기<small>대진표에 가명으로 표시돼요</small></span>
			</label>}
			<button class="cwt-btn" disabled={this.busy} onClick={raid || swiss ? this.handleSignupReal : this.handleSignup}>참여 신청</button>
				</>}
			</div>
		</section>;
	}

	renderLineup() {
		const me = this.me?.entrant ?? null;
		if (!me || me.status !== 'alive' || !me.entry || me.entry.length === 0) return null;
		const picked = this.picking ?? me.lineup ?? [];
		const changed = picked.join(',') !== (me.lineup ?? []).join(',');
		const nextOnly = this.lineupNextRound !== null && this.lineupNextRound === this.view?.currentRound;
		const cards = me.entryCards ?? [];
		const byId: { [id: string]: { name: string, sprite: string, borrowed?: boolean } } = {};
		for (const c of cards) byId[c.id] = c;
		// 빙글빙글컵 결승은 6마리 전부 — 고르는 게 아니라 순서를 정하는 창이다
		const size = this.lineupSize();
		const slots = me.entry.map((id) => {
			const on = picked.indexOf(id) >= 0;
			const order = picked.indexOf(id) + 1;
			const card = byId[id];
			// 새싹컵 — 데려온 아이는 매 판 반드시 나간다. 해제할 수 없게 잠가 둔다(서버도 거절한다)
			const fixed = !!card?.borrowed;
			return <button
				class={`cwt-slot${on ? ' is-on' : ''}${fixed ? ' is-borrowed' : ''}`}
				data-mon={id}
				key={id}
				aria-pressed={on}
				aria-label={`${card?.name || '포켓몬'}${fixed ? ', 데려온 아이라 매 판 함께 나가요' : on ? `, 선택 ${order}번` : ', 선택 안 됨'}`}
				disabled={this.busy || (!on && picked.length >= size) || (fixed && on)}
				onClick={this.handlePick}
			>
				{/* 그림이 아직 안 왔으면 자리만 비워 둔다 — 숫자를 대신 넣으면 그 숫자가 이름처럼 읽힌다 */}
				{card ? <img class="cwt-slot-img" src={card.sprite} alt="" /> : <span class="cwt-slot-img" />}
				<span class="cwt-slot-name">{card ? card.name : '\u00a0'}</span>
				{fixed ? <span class="cwt-slot-tag">데려온 아이</span> : null}
				{on ? <em class="cwt-slot-order">{order}</em> : null}
			</button>;
		});
		return <div class="cwt-team-control">
			<div class="cwt-team-summary">
				<b>내 출전 명단</b>
				<div class="cwt-team-mini">{(me.lineup ?? []).map(id => <span title={byId[id]?.name || '포켓몬'}>
					{byId[id] ? <img src={byId[id].sprite} alt={byId[id].name} /> : <span>포켓몬</span>}
				</span>)}</div>
				<span class="cwt-team-hint">{nextOnly ? '이미 시작한 판이라 다음 판부터 적용돼요' : (me.lineup ?? []).length === size ? (size === 6 ? '결승에 6마리 전부 나가요' : '다음 판에 출전합니다') : `출전할 ${size}마리를 골라주세요`}</span>
				<button class="cwt-btn is-ghost" onClick={this.handleOpenLineup}>{size === 6 ? '순서 변경' : '명단 변경'}</button>
			</div>
			<dialog class="cwt-team-dialog" ref={this.setLineupDialog} onCancel={this.handleCancelLineup} onClose={this.handleLineupClosed} aria-label="출전 포켓몬 선택">
			<section class="cwt-lineup" aria-label="출전 포켓몬 선택" aria-busy={this.busy}>
			<button class="cwt-dialog-close" disabled={this.busy} onClick={this.handleCloseLineup} autoFocus>닫기</button>
			<div class="cwt-lineup-head"><div><span class="cwt-stage-eyebrow">YOUR TEAM</span><h4>{size === 6 ? '결승에 낼 6마리의 순서' : '다음 판에 낼 3마리'}</h4></div><b class="cwt-selection-count">{picked.length}<small> / {size}</small></b></div>
			<p class="cwt-note">
				{size === 6
					? '결승은 여섯 마리 전부 나가요. 순서를 바꾸려면 카드를 눌러 해제한 뒤 원하는 차례로 다시 눌러주세요. 여섯 칸이 다 차면 바로 저장돼요. 이미 시작한 판에는 변경이 적용되지 않아요.'
					: '선택한 카드를 다시 누르면 해제돼요. 세 마리를 다 고르면 바로 저장돼요. 이미 시작한 판에는 변경이 적용되지 않아요.'}
				{cards.some(c => c.borrowed) ? ' 데려온 아이는 매 판 함께 나가요.' : ''}
			</p>
			<div class="cwt-slots">{slots}</div>
			<div class="cwt-lineup-footer"><div role="status"><b>{
				this.busy ? '명단을 저장하고 있어요'
				: picked.length < size ? `${size - picked.length}마리 더 고르면 바로 저장돼요`
				// 다 찼는데 저장본과 다르다 = 방금 저장이 거절됐다. 카드를 다시 누르면 다시 보낸다.
				: changed ? '저장하지 못했어요. 카드를 다시 눌러주세요'
				: nextOnly ? '저장했어요. 이미 시작한 판이라 다음 판부터 적용돼요'
				: '현재 출전 명단'
			}</b><span>상대에게는 선택이 공개되지 않아요</span></div>
			</div>
		</section>
			</dialog>
		</div>;
	}

	/**
	 * 승부 예측 창을 따라간다 (2026-09-23).
	 *
	 * 창은 한 번에 한 칸뿐이다. 다른 칸으로 넘어가면 고르던 편·금액을 비우고 `/me` 를 한 번
	 * 다시 받는다 — 4강 진출 여부(걸 수 있나)는 창이 열리는 시점에 굳어 있기 때문이다.
	 * 마감 시각은 공개 연출 시계와 같은 방식으로 로컬에 옮겨 담고, 뒤로 가지 않게 한다.
	 */
	syncBet() {
		const v = this.view;
		const open = v?.betOpen ?? null;
		const key = v && open ? `${v.id}-${open.round}-${open.slot}` : '';
		if (open) {
			const next = Date.now() + open.secLeft * 1000;
			this.betEndsAt = key === this.betKey && this.betEndsAt ? Math.min(this.betEndsAt, next) : next;
		} else {
			this.betEndsAt = 0;
		}
		if (key !== this.betKey) {
			this.betKey = key;
			this.betSide = null;
			this.betAmount = null;
			if (key) this.loadMe();
		}
	}
	betLabel(round: number, slot: number): string {
		const label = this.roundLabel(round);
		return label === '준결승' ? `준결승 ${slot + 1}` : label;
	}
	handleBetSide = (e: Event) => {
		const side = (e.currentTarget as HTMLElement).getAttribute('data-side');
		if (side !== 'p1' && side !== 'p2') return;
		this.betSide = side;
		this.forceUpdate();
	};
	handleBetAmount = (e: Event) => {
		const amount = Number((e.currentTarget as HTMLElement).getAttribute('data-amount'));
		if (!(amount > 0)) return;
		this.betAmount = amount;
		this.forceUpdate();
	};
	handleBet = () => {
		const open = this.view?.betOpen;
		if (!open || !this.betSide || !this.betAmount) return;
		this.post('/bet', { round: open.round, slot: open.slot, side: this.betSide, amount: this.betAmount });
	};

	/**
	 * 승부 예측 카드 — 최소판 (2026-09-23). 대진표 위 배팅은 대진표 개편 때 옮긴다.
	 *
	 * 두 부분이다: 지금 창이 열린 경기(편 둘 + 배당 + 판돈 칸 + 걸기)와, 이번 대회에 내가 건 것의 결과.
	 * ⚠️ 배당 말고는 아무 숫자도 없다 — 건 사람 수·편별 금액은 서버가 안 준다(인기 서열이 드러난다).
	 */
	renderBet() {
		const view = this.view;
		const info = this.me?.bet ?? null;
		if (!view || !info) return null;
		const open = view.betOpen ?? null;
		const mine = info.mine ?? [];
		const match = open ? view.matches.find(m => m.round === open.round && m.slot === open.slot) ?? null : null;
		const live = !!(match && match.bet && match.p1 && match.p2);
		if (!live && mine.length === 0) return null;

		const nameOn = (round: number, slot: number, side: 'p1' | 'p2') => {
			const m = view.matches.find(x => x.round === round && x.slot === slot);
			const c = m ? (side === 'p1' ? m.p1 : m.p2) : null;
			return c ? c.name : '선수';
		};
		const won = (n: number) => n.toLocaleString('ko-KR');

		let current = null;
		if (live && match && match.bet && match.p1 && match.p2) {
			const left = Math.max(0, Math.ceil((this.betEndsAt - Date.now()) / 1000));
			const clock = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
			const odds = match.bet.odds ?? [0, 0];
			const placed = mine.find(b => b.round === match.round && b.slot === match.slot) ?? null;
			const pickedOdds = this.betSide === 'p1' ? odds[0] : this.betSide === 'p2' ? odds[1] : 0;
			const sides: ('p1' | 'p2')[] = ['p1', 'p2'];
			current = <div class="cwt-bet-now">
				<div class="cwt-bet-head">
					<div><span class="cwt-stage-eyebrow">PREDICTION</span><h4>{this.betLabel(match.round, match.slot)} 승부 예측</h4></div>
					<b class="cwt-bet-clock" role="timer">{left > 0 ? `${clock} 뒤 마감` : '곧 마감돼요'}</b>
				</div>
				<div class="cwt-bet-sides">
					{sides.map((side, i) => {
						const card = side === 'p1' ? match.p1! : match.p2!;
						const cls = `cwt-bet-side${this.betSide === side ? ' is-on' : ''}${placed?.side === side ? ' is-mine' : ''}`;
						return <button
							class={cls}
							key={side}
							data-side={side}
							aria-pressed={this.betSide === side || placed?.side === side}
							disabled={!!placed || !info.eligible || this.busy || left <= 0}
							onClick={this.handleBetSide}
						>
							<span class="cwt-bet-name">{card.name}</span>
							<b class="cwt-bet-odds">×{odds[i].toFixed(2)}</b>
						</button>;
					})}
				</div>
				{placed ? <p class="cwt-note">
					{nameOn(placed.round, placed.slot, placed.side)}에게 {won(placed.amount)}체리를 걸었어요. 배당은 마감 순간 확정돼요.
				</p> : !info.eligible ? <p class="cwt-note">{info.reason || '이번 경기는 예측할 수 없어요.'}</p> : <>
					<div class="cwt-bet-stakes" role="group" aria-label="걸 체리">
						{info.stakes.map(a => <button
							class={this.betAmount === a ? 'cwt-chip is-on' : 'cwt-chip'}
							key={a}
							data-amount={a}
							aria-pressed={this.betAmount === a}
							disabled={this.busy || left <= 0}
							onClick={this.handleBetAmount}
						>{won(a)}</button>)}
					</div>
					<div class="cwt-bet-foot">
						<span class="cwt-note">
							{this.betSide && this.betAmount
								? `맞히면 지금 배당으로 약 ${won(Math.floor(this.betAmount * pickedOdds))}체리. 마감 순간 배당으로 확정돼요.`
								: '한 경기에 한 번, 한쪽에만 걸 수 있어요.'}
						</span>
						<button
							class="cwt-btn"
							disabled={!this.betSide || !this.betAmount || this.busy || left <= 0}
							onClick={this.handleBet}
						>{this.busy ? '거는 중…' : this.betSide && this.betAmount ? `${won(this.betAmount)}체리 걸기` : '편과 금액을 골라주세요'}</button>
					</div>
				</>}
			</div>;
		}

		const resultText = (b: CwtMyBet) => {
			if (b.status === 'won') return `적중 +${won((b.payout ?? 0) - b.amount)}`;
			if (b.status === 'lost') return '빗나감';
			if (b.status === 'refunded') return '환불됨';
			return '경기 중';
		};
		return <section class="cwt-bet" aria-label="승부 예측">
			{current}
			{mine.length ? <ul class="cwt-bet-mine" aria-label="내 예측">
				{mine.map(b => <li key={`${b.round}-${b.slot}`} class={`is-${b.status}`}>
					<span>{this.betLabel(b.round, b.slot)}</span>
					<span>{nameOn(b.round, b.slot, b.side)}, {won(b.amount)}체리</span>
					<b>{resultText(b)}</b>
				</li>)}
			</ul> : null}
		</section>;
	}

	renderMyMatch() {
		const view = this.view;
		const my = this.myEntrantId();
		if (!view || view.status !== 'running' || view.phase === 'reveal' || view.phase === 'intermission' || !my) return null;
		const match = view.matches.find(m => m.round === view.currentRound && (m.p1?.entrantId === my || m.p2?.entrantId === my) && (m.state === 'calling' || m.state === 'live'));
		if (!match) return null;
		return <section class="cwt-my-match" aria-label="내 경기 안내">
			<div><span class="cwt-stage-eyebrow">YOUR MATCH</span><h4>{match.state === 'live' ? '내 경기가 진행 중입니다' : '내 경기 입장을 준비하고 있어요'}</h4>
			<p>{match.state === 'live' ? '배틀 화면으로 돌아가 다음 행동을 선택해주세요.' : '이 화면에서 기다려주세요. 준비되면 배틀이 자동으로 열립니다.'}</p></div>
			{match.state === 'live' && match.roomId ? <button class="cwt-btn" data-room={match.roomId} onClick={this.handleWatch}>내 배틀로 이동</button> : null}
		</section>;
	}

	handleAnonToggle = () => {
		this.anonWanted = !this.anonWanted;
		this.forceUpdate();
	};
	handleSignup = () => this.post('/signup', { anonymous: this.anonWanted });
	/** 마왕토벌전 — 실명 고정 (체크박스가 없다). */
	handleSignupReal = () => this.post('/signup', { anonymous: false });
	handleWithdraw = () => this.post('/withdraw', {});
	/**
	 * 카드를 누를 때마다 고른다 — **정원이 다 차는 순간 저장**한다 (2026-10-25, 확정 버튼 폐지).
	 * 30초 인터미션에 「골라 놓고 확정을 안 누름」이 그대로 옛 파티 출전이 됐다.
	 */
	handlePick = (e: Event) => {
		if (this.busy) return;
		const id = (e.currentTarget as HTMLElement).getAttribute('data-mon');
		if (!id) return;
		const base = this.picking ?? (this.me?.entrant?.lineup ?? []).slice();
		const next = base.slice();
		const at = next.indexOf(id);
		// 데려온 아이는 해제하지 않는다 (새싹컵)
		if (at >= 0 && this.me?.entrant?.entryCards?.some(c => c.id === id && c.borrowed)) return;
		if (at >= 0) next.splice(at, 1);
		else if (next.length < this.lineupSize()) next.push(id);
		else return;
		const saved = this.me?.entrant?.lineup ?? [];
		if (next.length === this.lineupSize()) {
			// 저장본과 같으면(빼고 같은 자리에 다시 넣음) 보낼 것이 없다
			if (next.join(',') === saved.join(',')) { this.picking = null; this.forceUpdate(); return; }
			this.picking = next;
			this.post('/lineup', { monIds: next });
			return;
		}
		this.picking = next;
		this.forceUpdate();
	};

	/**
	 * 공개 연출 오버레이 — 지금 등장하는 카드를 가운데 크게 보여 준다.
	 *
	 * 「카드가 자리로 날아가 꽂힌다」를 진짜 비행으로 만들지 않았다. 가운데에서 크게 떴다가
	 * 사라지고, 같은 순간 대진표의 그 자리가 빈 종이에서 카드로 바뀐다 — 눈에는 같은 일로
	 * 읽히면서 레이아웃 계산에 기대지 않아 어떤 화면 폭에서도 깨지지 않는다.
	 */
	renderReveal() {
		const view = this.view!;
		const order = view.revealOrder || [];
		const count = this.revealedCount();
		const elapsed = this.revealElapsed();
		const byId: { [id: string]: CwtCard } = {};
		for (const m of view.matches) {
			if (m.p1) byId[m.p1.entrantId] = m.p1;
			if (m.p2) byId[m.p2.entrantId] = m.p2;
		}
		// 카운트다운은 **로컬 시계**로 센다 — 서버가 준 남은 초는 2초마다 오므로 그대로 쓰면 건너뛴다
		const left = this.phaseEndsAt ? Math.max(0, Math.ceil((this.phaseEndsAt - Date.now()) / 1000)) : null;
		if (elapsed < CWT_REVEAL_LEAD_MS) {
			return <div class="cwt-reveal">
				<div class="cwt-reveal-title">{view.title}</div>
				<p class="cwt-note">대진을 뽑고 있어요</p>
			</div>;
		}
		if (order.length && count > order.length) {
			return <div class="cwt-reveal">
				<span class="cwt-reveal-kicker">대진 공개 완료</span>
				<div class="cwt-reveal-go" key={left !== null && left <= 3 ? left : 'ready'}>{left !== null && left <= 3 ? left || 'START' : '잠시 후 시작합니다'}</div>
			</div>;
		}
		const cur = byId[order[count - 1] || ''];
		if (!cur) return null;
		return <div class="cwt-reveal">
			<div class="cwt-reveal-card" key={cur.entrantId}>
				<span class="cwt-reveal-kicker">선수 입장 {count} / {order.length}</span>
				<div class="cwt-reveal-visual">
					<div class="cwt-reveal-trainer">{cur.body && !cur.anonymous
						? <img class="cwt-reveal-body" src={cur.body} alt="" />
						: cur.avatar && !cur.anonymous ? <img class="cwt-reveal-face" src={cur.avatar} alt="" />
						: <span class="cwt-reveal-silhouette" aria-hidden="true">?</span>}</div>
					{/* 새싹컵은 지정 파트너 대신 데려온 아이를 세운다 — 익명이어도 뽑기에서 이미 공개된 아이다 */}
					{cur.borrowed ? <div class="cwt-reveal-partner is-borrowed">
						<span>{cur.borrowed.lender} 님의 아이</span><img src={cur.borrowed.sprite} alt="" /><b>{cur.borrowed.name}</b>
					</div> : cur.partner && !cur.anonymous ? <div class="cwt-reveal-partner">
						<span>{cur.partner.role === 'signature' ? '대표 포켓몬' : 'PARTNER'}</span><img src={cur.partner.sprite} alt="" /><b>{cur.partner.name}</b>
					</div> : null}
				</div>
				<b>{cur.name}</b>
			</div>
			<div class="cwt-reveal-progress" role="progressbar" aria-label="참가자 공개" aria-valuemin={0} aria-valuemax={order.length} aria-valuenow={count}>
				<i style={`transform:scaleX(${Math.min(count / order.length, 1)})`} />
			</div>
		</div>;
	}

	renderRoundStage() {
		const view = this.view;
		if (!view || view.status !== 'running' || view.phase === 'reveal' || !view.currentRound) return null;
		const rest = view.phase === 'intermission';
		// 빙글빙글컵 — 결승 칸은 라운드 수 + 1. 포맷은 6대6 (여섯 종이 안 되는 안전핀이면 3대3, 화면은 말하지 않는다)
		const final = view.swiss
			? view.swiss.final && view.currentRound === view.swiss.rounds + 1
			: !!view.bracketSize && view.currentRound === Math.log2(view.bracketSize);
		if (!rest && !final) return null;
		const match = final ? view.matches.find(m => m.round === view.currentRound) : null;
		const left = this.phaseEndsAt ? Math.max(0, Math.ceil((this.phaseEndsAt - Date.now()) / 1000)) : null;
		const label = this.roundLabel(view.currentRound);
		return <section class={`cwt-round-stage${final ? ' is-final' : ''}`} key={`${view.id}-${view.currentRound}-${rest}`} aria-label={rest ? '다음 라운드 준비' : '결승 무대'}>
			<span class="cwt-stage-eyebrow">{final ? (view.swiss?.finalFormat === 'six' ? 'GRAND FINAL · 6 vs 6' : 'GRAND FINAL') : 'NEXT ROUND'}</span>
			<h4>{label}{rest ? `${cwsJosa(label, '을', '를')} 준비합니다` : ' 무대'}</h4>
			{final && match ? <div class="cwt-final-pair">
				{this.renderFinalist(match.p1)}<span class="cwt-final-vs" aria-hidden="true">VS</span>{this.renderFinalist(match.p2)}
			</div> : null}
			{rest && !final && view.swiss ? this.renderSwissNextFoe(view.swiss, view.currentRound) : null}
			{rest ? <div class="cwt-stage-clock"><b>{left === null ? '—' : left > 0 ? `${left}초` : '잠시 후'}</b><span>다음 경기 시작까지</span></div>
				: <p class="cwt-stage-caption">{match?.state === 'live' ? '결승전 진행 중' : '선수 입장을 기다리고 있습니다'}</p>}
		</section>;
	}

	/**
	 * 빙글빙글컵 인터미션 — **다음 상대 카드**. 라운드가 끝나는 순간 서버가 다음 라운드 짝을 이미 짜 두므로(`currentRound` 가
	 * 먼저 오른다) 30초 창 안에 「누구와 붙나·그 사람 전적」을 보고 출전 3마리를 다시 고를 수 있다. 상대의 라인업은 모른다 —
	 * 보이는 건 순위표에 이미 공개된 승패·상대 전적뿐이다. 부전승이면 그 사실을, 제외됐으면 아무것도 그리지 않는다.
	 */
	renderSwissNextFoe(sw: CwxBoard, round: number) {
		const view = this.view!;
		const my = this.myEntrantId();
		if (!my) return null;
		const match = view.matches.find(m => m.round === round && (m.p1?.entrantId === my || m.p2?.entrantId === my));
		if (!match) return null;
		if (match.state === 'bye') {
			return <div class="cwx-next is-bye"><span class="cwt-stage-eyebrow">YOUR ROUND</span><b>이번 라운드는 부전승이에요</b><small>1승이 더해져요. 다른 판을 관전하며 기다려주세요.</small></div>;
		}
		const foe = match.p1?.entrantId === my ? match.p2 : match.p1;
		if (!foe) return null;
		const row = sw.standings.find(r => r.entrantId === foe.entrantId);
		const mine = sw.standings.find(r => r.entrantId === my);
		return <div class="cwx-next" key={`${round}-${foe.entrantId}`}>
			<span class="cwt-stage-eyebrow">YOUR NEXT OPPONENT</span>
			<div class="cwx-next-card">
				<span class="cwt-ava">{foe.avatar && !foe.anonymous ? <img src={foe.avatar} alt="" /> : <span class="cwt-ava-blank" />}</span>
				<div class="cwx-next-who">
					<b>{foe.name}</b>
					{row ? <small>{row.rank}위 · {row.wins}승 {row.losses}패{row.byes ? ' (부전승 포함)' : ''} · 상대 전적 {row.opp}</small> : null}
				</div>
				{mine ? <span class="cwx-next-vs"><em>VS</em><small>나 {mine.wins}승 {mine.losses}패</small></span> : null}
			</div>
			<small class="cwx-next-hint">상대의 출전 명단은 보이지 않아요. 아래 「명단 변경」으로 이번 판에 낼 3마리를 다시 고를 수 있어요.</small>
		</div>;
	}

	renderFinalist(card: CwtCard | null) {
		return <div class="cwt-finalist">
			<span class="cwt-ava">{card?.avatar && !card.anonymous ? <img src={card.avatar} alt="" /> : <span class="cwt-ava-blank" />}</span>
			<b>{card?.name || '진출자 대기'}</b>
		</div>;
	}

	renderPodium(champion = false) {
		return <div class="cwt-podium" aria-hidden="true">
			<div class="cwt-prize"><img src="/cherish/tour/midnight/trophy.png" alt="" /><span>{champion ? 'CHAMPION' : 'CHERISH CUP'}</span></div>
		</div>;
	}

	/** 트로피 아래에 대진표를 표시한다. */
	renderArena(narrow: boolean) {
		const roundsOnly = narrow || (this.view?.bracketSize ?? 8) > 8 || this.props.room.width < 1000;
		return <section class={`cwt-bracket-stage${roundsOnly ? ' is-round-view' : ''}${this.view?.phase === 'reveal' ? ' is-revealing' : ''}`}
			ref={this.setBracketStage} aria-label="토너먼트 대진표">
			{this.renderPodium()}
			{roundsOnly ? this.renderFeed() : this.renderTree()}
			{this.view?.phase === 'reveal' ? this.renderReveal() : null}
		</section>;
	}

	/** 추첨 동안은 우승 정보를 작은 띠에 남겨 숫자 선택을 가리지 않는다. */
	renderRouletteSummary() {
		const v = this.view!;
		const champion = v.champion;
		const winner = champion?.name ?? (v.raid?.winner === 'boss' ? '마왕 팀' : v.raid?.winner === 'raider' ? '토벌대' : null);
		return <section class="cwt-roulette-summary" aria-label="대회 결과 요약">
			<img src="/cherish/tour/midnight/trophy.png" alt="" />
			<div><span>{v.title} {winner ? '우승' : '종료'}</span><b>{winner ?? '대회를 마쳤어요'}</b></div>
			{champion ? <button class="cwt-btn is-ghost" onClick={() => { this.popupKey = `${v.id}-${champion.entrantId}`; this.forceUpdate(); }}>우승 장면 보기</button> : null}
		</section>;
	}
	renderResultRecords(narrow: boolean) {
		const v = this.view!;
		return <details class="cwt-result-records"><summary>대진·순위·승부 예측 보기</summary>
			{this.renderBet()}
			{v.raid ? this.renderRaid() : v.swiss ? this.renderSwiss(narrow) : this.renderArena(narrow)}
		</details>;
	}
	renderResultShop() {
		return this.ribbons ? <details class="cwt-result-records"><summary>리본 상점 열기</summary>{this.renderRibbonShop()}</details> : null;
	}
	renderCeremony() {
		const view = this.view!;
		const champion = view.status === 'done' ? view.champion : null;
		if (!champion) return null;
		const key = `${view.id}-${champion.entrantId}`;
		const celebrate = this.awardKey === key;
		return <section class={`cwt-award${celebrate ? ' is-celebrating' : ''}`} key={key} aria-label="대회 우승자">
			{celebrate ? this.renderConfetti() : null}
			{this.renderPodium(true)}
			<div class="cwt-champ">
				<h4>{view.title} 우승</h4>
				<div class="cwt-champ-card">
					<span class="cwt-ava">{champion.avatar && !champion.anonymous ? <img src={champion.avatar} alt="" /> : <span class="cwt-ava-blank" />}</span>
					<b>{champion.name}</b>
				</div>
				<p class="cwt-award-caption">마지막 무대의 주인공</p>
				{view.swiss ? this.renderSwissPodiumLine(view.swiss) : null}
				<button class="cwt-btn is-ghost cwt-award-replay" onClick={() => { this.popupKey = key; this.forceUpdate(); }}>우승 장면 다시 보기</button>
			</div>
		</section>;
	}

	// ---- 행운의 자리 룰렛 (2026-10-14 · 2026-11-02 공용 컴포넌트로 추출) ----------------------------------
	//
	// 띠·감속·카드 재생·효과음·컨페티는 cherish-roulette.tsx 의 CwlRouletteBox 가 전부 든다. 여기는 대회 재료만 넘긴다:
	// 서버 룰렛 뷰 · 내 엔트런트 id · 서버가 아는 내 클릭(/me) · 밀기 POST · 대회 음악 플레이어 · 바닥줄(리본 지급 표시).

	renderRouletteBox() {
		const view = this.view;
		const r = view?.roulette;
		if (!view || !r) return null;
		const foot = <>
			{view.ribbonsSettled != null && r.result?.hops.some((h: CwlHop) => (h.ribbons ?? 0) > 0) ? <span class={`cwt-standings-note${view.ribbonsSettled ? ' is-paid' : ''}`}>
				{view.ribbonsSettled ? '🎀 리본이 지급됐어요' : '🎀 운영진 확인 뒤 리본이 지급돼요'}
			</span> : null}
			{this.me?.ribbons ? <span>내 리본 <b>{this.me.ribbons.balance}</b>개</span> : null}
		</>;
		return <CwlRouletteBox
			roulette={r}
			viewId={view.id}
			mineEntrantId={this.me?.entrant?.entrantId ?? null}
			serverMine={this.me?.rouletteClick ?? null}
			busy={this.busy}
			roomWidth={this.props.room.width}
			isVisible={() => PS.isVisible(this.props.room)}
			onPick={(n: number) => this.post('/roulette/click', { n }, () => {})}
			playEffect={(name: string) => this.musicPlayer?.playEffect(name as any)}
			stopEffect={(name: string) => this.musicPlayer?.stopEffect(name as any)}
			onSpinStart={() => { this.popupKey = ''; }}
			footExtra={foot}
		/>;
	}
	prizeGlyph(kind: string): string {
		return kind === 'egg' ? '🥚' : kind === 'cherry' ? '🍒' : kind === 'ribbons' ? '🎀' : '✨';
	}

	// ---- 종료 순위표 + 리본 (2026-10-14) ----------------------------------------------

	renderStandings() {
		const view = this.view;
		const rows = view?.standings;
		if (!view || !rows || !rows.length) return null;
		const mine = this.me?.entrant?.entrantId ?? null;
		const any = rows.some(r => r.ribbons > 0);
		return <section class="cwt-standings" aria-label="최종 순위">
			<div class="cwt-standings-head">
				<div><span class="cwt-stage-eyebrow">FINAL STANDINGS</span><h4>최종 순위</h4></div>
				{any ? <span class={`cwt-standings-note${view.ribbonsSettled ? ' is-paid' : ''}`}>
					{view.ribbonsSettled ? '🎀 리본이 지급됐어요' : '🎀 운영진 확인 뒤 리본이 지급돼요'}
				</span> : null}
			</div>
			<ol class="cwt-standings-list">
				{rows.map(r => <li class={`${mine === r.entrantId ? 'is-me' : ''}${r.rank === 1 ? ' is-champ' : ''}`} key={r.entrantId}>
					<span class="cwt-standings-rank">{r.rank === null ? '-' : r.rank === 1 ? '🏆' : `${r.rank}위`}</span>
					<span class="cwt-ava">{r.avatar && !r.anonymous ? <img src={r.avatar} alt="" /> : <span class="cwt-ava-blank" />}</span>
					<b class="cwt-standings-name">{r.name}{mine === r.entrantId ? <small> 나</small> : null}</b>
					<span class="cwt-standings-wins">{r.wins}승</span>
					<span class="cwt-standings-ribbons">{r.ribbons > 0 ? `🎀 +${r.ribbons}` : ''}</span>
				</li>)}
			</ol>
			{this.me?.ribbons ? <p class="cwt-standings-mine">내 리본 <b>{this.me.ribbons.balance}</b>개{view.ribbonsSettled ? '' : ' (이번 대회 몫은 지급 뒤에 더해져요)'}</p> : null}
		</section>;
	}

	// ---- 리본 상점 (2026-10-14) ---------------------------------------------------------
	//
	// 상자는 사면 바로 열린다. 구성품·확률은 공개하지 않고, 개봉 결과만 보여준다.

	loadRibbons() {
		if (this.ribbonsLoading) return;
		this.ribbonsLoading = true;
		fetch(`${CWT_API.replace(/\/tournament$/, '')}/ribbons`, { credentials: 'include' })
			.then(r => (r.ok ? r.json() : null))
			.then(json => {
				if (json?.ok) this.ribbons = json.data as CwlRibbons;
			})
			.catch(() => undefined)
			.then(() => {
				this.ribbonsLoading = false;
				this.forceUpdate();
			});
	}
	openBox(tier: number) {
		if (this.boxBusy) return;
		const box = this.ribbons?.boxes.find(b => b.tier === tier);
		if (!box) return;
		this.boxBusy = true;
		this.boxResult = null;
		this.forceUpdate();
		fetch(`${CWT_API.replace(/\/tournament$/, '')}/ribbons/open`, {
			method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tier }),
		})
			.then(r => r.json())
			.then(json => {
				this.boxBusy = false;
				if (!json?.ok) {
					PS.alert(json?.message || json?.error?.message || '잠시 뒤 다시 시도해주세요.');
					this.forceUpdate();
					return;
				}
				const d = json.data as { outcome: { label: string; kind: string; icon?: string | null }; wallet: { balance: number; earned: number } };
				this.boxResult = { tier, ko: box.ko, label: d.outcome.label, icon: d.outcome.icon ?? null, kind: d.outcome.kind, at: Date.now() };
				if (this.ribbons) this.ribbons.wallet = d.wallet;
				if (this.me?.ribbons) this.me.ribbons = d.wallet;
				this.musicPlayer?.playEffect('land');
				this.forceUpdate();
				this.loadRibbons();
			})
			.catch(() => {
				this.boxBusy = false;
				PS.alert('연결이 불안정해요. 잠시 뒤 다시 시도해주세요.');
				this.forceUpdate();
			});
	}
	renderRibbonShop() {
		const data = this.ribbons;
		if (!data) return null;
		const balance = data.wallet.balance;
		const res = this.boxResult;
		return <section class="cwl-shop" aria-label="리본 상점">
			<div class="cwl-shop-head">
				<div><span class="cwt-stage-eyebrow">THE RIBBON COLLECTION</span><h4>리본 상점</h4><p class="cwl-shop-tagline">함께한 순간을, 작은 선물로.</p></div>
				<div class="cwl-wallet"><small>내 리본</small><b>{balance}</b><span>개</span>{data.wallet.earned > balance ? <em>지금까지 {data.wallet.earned}개 모았어요</em> : null}</div>
			</div>
			<p class="cwl-shop-note">대회에서 모은 리본으로 선물을 열어보세요. 리본을 사용하면 바로 개봉돼요.</p>
			{res ? <div class={`cwl-box-result is-tier-${res.tier}`} key={res.at} role="status">
				{res.icon ? <img src={res.icon} alt="" /> : <span class="cwl-prize-glyph">{this.prizeGlyph(res.kind)}</span>}
				<div><small>{res.ko}에서</small><b>{res.label}</b><span>{res.kind === 'egg' ? '「내 포켓몬」의 알에서 확인할 수 있어요' : res.kind === 'special_item' ? '「내 포켓몬 → 인벤토리」의 특별한 물건에 들어갔어요' : res.kind === 'ribbons' ? '리본으로 돌아왔어요' : '체리 지갑에 들어갔어요'}</span></div>
				<button class="cwt-btn is-ghost" onClick={() => { this.boxResult = null; this.forceUpdate(); }}>닫기</button>
			</div> : null}
			<div class="cwl-boxes">
				{data.boxes.map(b => <article class={`cwl-box is-tier-${b.tier}`} key={b.tier}>
					<span class="cwl-box-edition">{b.tier === 1 ? '01 / ROSE' : b.tier === 2 ? '02 / MOONLIGHT' : '03 / GOLDEN'}</span>
					<div class="cwl-box-art" aria-hidden="true"><img src={`/cherish/tour/midnight/ribbon-box-${b.tier}-v1.png`} alt="" /></div>
					<h5>{b.ko}</h5>
					<p class="cwl-box-caption">{b.tier === 1 ? '설렘을 묶은 작은 선물' : b.tier === 2 ? '은은한 달빛을 담은 선물' : '반짝이는 순간을 위한 선물'}</p>
					<button class="cwt-btn cwl-open" disabled={this.boxBusy || balance < b.price} onClick={() => this.openBox(b.tier)}>
						<span>🎀 {b.price}</span><span>{this.boxBusy ? '개봉 중…' : '개봉하기'}</span>
					</button>
					{balance < b.price ? <small class="cwl-short">리본 {b.price - balance}개가 더 필요해요</small> : null}
				</article>)}
			</div>
			{data.ledger.length ? <details class="cwl-ledger"><summary>리본 내역</summary>
				<ul>{data.ledger.map((l, i) => <li key={i}><span>{new Date(l.at).toLocaleDateString('ko-KR')}</span><b class={l.delta > 0 ? 'is-plus' : 'is-minus'}>{l.delta > 0 ? `+${l.delta}` : l.delta}</b><span>{l.note}</span></li>)}</ul>
			</details> : null}
		</section>;
	}

	// ---- 빙글빙글컵 (2026-10-13) --------------------------------------------------

	/** 시상 아래 한 줄 — 준우승과 (공동) 3위. 순위표는 서버가 최종 순위로 세워 보낸다. */
	renderSwissPodiumLine(sw: CwxBoard) {
		const second = sw.standings.filter(r => r.rank === 2).map(r => r.name);
		const third = sw.standings.filter(r => r.rank === 3).map(r => r.name);
		if (!second.length && !third.length) return null;
		return <p class="cwx-podium-line">
			{second.length ? <span><small>준우승</small><b>{second.join(', ')}</b></span> : null}
			{third.length ? <span><small>{third.length > 1 ? '공동 3위' : '3위'}</small><b>{third.join(', ')}</b></span> : null}
		</p>;
	}

	/** 이번 라운드 칸의 상태 글 — 순위표 오른쪽 칸. */
	swissStateKo(row: CwxRow, sw: CwxBoard, byName: { [id: string]: string }): preact.ComponentChildren {
		const foe = row.opponent ? byName[row.opponent] ?? '상대' : null;
		if (row.excluded && row.state === 'idle') return <span class="cwx-st is-out">제외</span>;
		switch (row.state) {
			case 'bye': return <span class="cwx-st is-bye">부전승</span>;
			case 'live': return <span class="cwx-st is-live">{foe ? `${foe} 와 경기 중` : '경기 중'}</span>;
			case 'calling': return <span class="cwx-st is-calling">{foe ? `${foe} 호출 중` : '호출 중'}</span>;
			case 'ready': return <span class="cwx-st">{foe ? `${foe} 와 곧 시작` : '곧 시작'}</span>;
			case 'done': return <span class={`cwx-st ${row.won ? 'is-won' : 'is-lost'}`}>{row.won ? '승리' : '패배'}</span>;
			default: return sw.round ? <span class="cwx-st is-dim">—</span> : null;
		}
	}

	/**
	 * 빙글빙글컵 판 — 대진표 대신 **순위표 + 이번 라운드 칸**. 공개 연출은 대진표와 같은 오버레이를 쓴다.
	 *
	 * 순위표는 서버가 세운 순서 그대로다(승수 → 상대 전적 → 직접 대결 → 추첨, 동점은 같은 순위). 화면에서
	 * 다시 정렬하지 않는다 — 관전자와 참가자가 다른 표를 보면 안 된다. 이번 라운드 칸은 내 판을 맨 위에,
	 * 그다음 진행 중인 판(관전 진입점)을 둔다. 끝난 라운드의 「마지막 남은 판」이 자연히 메인이 된다.
	 */
	renderSwiss(narrow: boolean) {
		const view = this.view!;
		const sw = view.swiss!;
		const my = this.myEntrantId();
		const revealing = view.phase === 'reveal';
		const round = view.status === 'done' ? null : (view.currentRound ?? sw.round);
		const isFinal = round !== null && round === sw.rounds + 1;
		const byName: { [id: string]: string } = {};
		for (const r of sw.standings) byName[r.entrantId] = r.name;
		const current = round ? view.matches.filter(m => m.round === round) : view.matches.filter(m => m.round === sw.rounds + 1);
		const weight = (m: CwtMatch) => (my && (m.p1?.entrantId === my || m.p2?.entrantId === my) ? 0 : m.state === 'live' ? 1 : m.state === 'calling' || m.state === 'ready' ? 2 : m.state === 'bye' ? 4 : 3);
		const ordered = current.slice().sort((a, b) => weight(a) - weight(b) || a.slot - b.slot);
		const liveLeft = current.filter(m => m.state === 'live' || m.state === 'calling' || m.state === 'ready').length;
		return <section class={`cwt-bracket-stage cwx${narrow ? ' is-narrow' : ''}${revealing ? ' is-revealing' : ''}`}
			ref={this.setBracketStage} aria-label="빙글빙글컵 순위표">
			{this.renderPodium()}
			{!revealing && current.length ? <div class="cwx-round" key={`round-${round ?? 'final'}`}>
				<div class="cwx-round-head">
					<span class="cwt-stage-eyebrow">{isFinal || round === null ? (sw.finalFormat === 'six' ? 'GRAND FINAL · 6 vs 6' : 'GRAND FINAL') : `ROUND ${round} / ${sw.rounds}`}</span>
					<h4>{round === null ? '결승 결과' : this.roundLabel(round)}</h4>
					{round !== null && !isFinal ? <small>{liveLeft > 0 ? `${liveLeft}판 진행 중 · 끝난 판부터 관전할 수 있어요` : '모든 판이 끝났어요'}</small> : null}
				</div>
				<div class="cwt-feed-list">{ordered.map(this.renderMatch)}</div>
			</div> : null}
			<div class="cwx-table-wrap">
				<div class="cwx-table-head"><span class="cwt-stage-eyebrow">STANDINGS</span><h4>{view.status === 'done' ? '최종 순위' : '순위표'}</h4>
					<small>{sw.rounds}라운드 뒤 1·2위가 결승 한 판. 순위는 승수, 같으면 만난 상대들의 승수 합(상대 전적)으로 가려요.</small></div>
				<table class="cwx-table">
					<thead><tr><th>순위</th><th>선수</th><th>승-패</th><th title="만난 상대들의 승수 합">상대 전적</th><th>{view.status === 'done' ? '' : '이번 라운드'}</th></tr></thead>
					<tbody>
						{sw.standings.map((r, i) => {
							const prev = sw.standings[i - 1];
							const tied = !!prev && prev.rank === r.rank;
							const finalist = !!sw.finalists && sw.finalists.indexOf(r.entrantId) >= 0;
							const cls = ['cwx-row'];
							if (my === r.entrantId) cls.push('is-mine');
							if (r.excluded) cls.push('is-excluded');
							if (finalist) cls.push('is-finalist');
							if (r.rank <= 3) cls.push(`is-top${r.rank}`);
							return <tr class={cls.join(' ')} key={r.entrantId}>
								<td class="cwx-rank">{tied ? <small>=</small> : null}{r.rank}</td>
								<td class="cwx-who">
									<span class="cwt-ava">{r.avatar && !r.anonymous ? <img src={r.avatar} alt="" /> : <span class="cwt-ava-blank" />}</span>
									<span class="cwt-name" title={r.name}>{r.name}</span>
									{my === r.entrantId ? <span class="cwt-mine-label">나</span> : null}
									{r.wins > 0 ? <span class="cwt-stamps" title={`${r.wins}승`}>{'●'.repeat(Math.min(r.wins, 6))}</span> : null}
								</td>
								<td class="cwx-rec"><b>{r.wins}</b>-{r.losses}{r.byes ? <small title="부전승 포함"> (부전 {r.byes})</small> : null}</td>
								<td class="cwx-opp">{r.opp}</td>
								<td class="cwx-state">{view.status === 'done' ? null : this.swissStateKo(r, sw, byName)}</td>
							</tr>;
						})}
					</tbody>
				</table>
			</div>
			{revealing ? this.renderReveal() : null}
		</section>;
	}

	closeChampionPopup = () => {
		this.popupKey = '';
		this.forceUpdate();
	};

	/** 우승 확정 팝업 — 전신 아바타를 무대 위에 세운다. 익명 우승자는 실루엣. */
	renderChampionPopup() {
		const view = this.view;
		const champion = view?.status === 'done' ? view.champion : null;
		if (!view || !champion || this.popupKey !== `${view.id}-${champion.entrantId}`) return null;
		const body = champion.anonymous ? null : champion.body;
		return <div class="cwt-pop" role="dialog" aria-modal="true" aria-label={`${view.title} 우승자`}
			onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Escape') this.closeChampionPopup(); }}>
			<div class="cwt-pop-backdrop" onClick={this.closeChampionPopup} />
			<div class="cwt-pop-card">
				{this.renderConfetti()}
				<div class="cwt-pop-rays" aria-hidden="true" />
				<p class="cwt-pop-eyebrow">CHERISH CUP CHAMPION</p>
				<div class="cwt-pop-stage">
					<span class="cwt-pop-glow" aria-hidden="true" />
					{body ? <img class="cwt-pop-body" src={body} alt="" />
						: <span class="cwt-pop-silhouette" aria-hidden="true">?</span>}
					<img class="cwt-pop-trophy" src="/cherish/tour/midnight/trophy.png" alt="" />
					<span class="cwt-pop-floor" aria-hidden="true" />
				</div>
				<h3 class="cwt-pop-name">{champion.name}</h3>
				<p class="cwt-pop-title">{view.title} 우승</p>
				<button class="cwt-btn cwt-pop-close" autoFocus onClick={this.closeChampionPopup}>확인</button>
			</div>
		</div>;
	}

	/**
	 * 해설석 — 최신 화자의 반신과 대사. 이전 줄은 접힌 목록에 보존한다.
	 *
	 * 쇼다운 채팅이 아니라 **우리 화면**에 띄우는 이유: 담임마다 얼굴·이름이 달라야 하는데
	 * 관전 그림자는 계정 하나(담임체리)로 말한다. 그러면 리치의 말이 체리 이름으로 나간다.
	 */
	renderTalk() {
		const lines = this.view?.talk ?? [];
		const music = this.view?.bracketMusic;
		// 새싹컵 뽑기도 「데려갔어요」 효과음을 쓴다 — 끌 수 있게 같은 단추를 보인다
		const hasSounds = this.view?.phase === 'reveal' || this.view?.phase === 'pick' || !!this.view?.roulette;
		if (lines.length === 0 && !music && !hasSounds) return null;
		if (lines.length === 0 && !music && this.view?.roulette) return <div class="cwt-roulette-audio">
			<CwtMusicPlayer key="music" ref={this.setMusicPlayer} room={this.props.room} music={music} showEffects={hasSounds} />
		</div>;
		// 응답 배열을 변경하지 않고 실제 시각 순으로 정렬한다. 새 해설에만 등장 연출을 적용한다.
		const ordered = lines.slice().sort((a, b) => a.at - b.at);
		const latest = ordered[ordered.length - 1];
		const speaker = latest && CWT_TEACHERS[latest.teacher] ? latest.teacher : 'cherry';
		const who = CWT_TEACHERS[speaker];
		return <section class="cwt-talk" aria-label="대회 해설">
			<div class={`cwt-broadcast is-${speaker}${music || hasSounds ? ' has-music' : ''}${!latest ? ' music-only' : ''}`}>
				{latest ? <div class="cwt-presenter" aria-hidden="true">
					<img src={who.portrait} alt="" />
				</div> : null}
				{latest ? <div class="cwt-commentary">
					<div class="cwt-commentary-label"><span>CHERISH CUP</span><span>해설석</span></div>
					<div class="cwt-speaker"><b>{who.name}</b><span>{who.role}</span></div>
					<div aria-live="polite" aria-atomic="true">
						<p class="cwt-dialogue" key={`${latest.at}-${latest.teacher}`}>{latest.text}</p>
					</div>
				</div> : null}
				<CwtMusicPlayer key="music" ref={this.setMusicPlayer} room={this.props.room} music={music} showEffects={hasSounds} />
			</div>
			{ordered.length > 1 ? <details class="cwt-transcript">
				<summary>이전 해설 <span>{ordered.length - 1}</span></summary>
			{ordered.slice(0, -1).map((l) => {
				const who = CWT_TEACHERS[l.teacher] ?? CWT_TEACHERS.cherry!;
				return <div class="cwt-say" key={`${l.at}-${l.teacher}`}>
					<img class="cwt-say-face" src={who.img} alt="" />
					<b class="cwt-say-name">{who.name}</b>
					<span class="cwt-say-text">{l.text}</span>
				</div>;
			})}
			</details> : null}
		</section>;
	}

	/**
	 * 명장면 3선 — 끝난 경기의 얼굴. 판 전체에 흩어진 세 턴이다.
	 *
	 * 결승에만 붙인다. 8강까지 다 붙이면 대진표가 글로 덮이고, 어차피 사람들이 다시 보는 건
	 * 마지막 판이다. 리플레이 점프는 아직 없어서 **몇 턴에 무슨 일이 있었나**만 적는다.
	 */
	renderHighlights(match: CwtMatch) {
		const hl = match.highlights;
		if (!hl || hl.length === 0) return null;
		return <section class="cwt-hl" aria-label="결승 명장면">
			<span class="cwt-stage-eyebrow">FINAL HIGHLIGHTS</span>
			<h4>결승을 돌아보며</h4>
			<ol class="cwt-hl-list">
				{hl.map((h) => <li>
					<b class="cwt-hl-turn">{h.turn}턴</b>
					<span class="cwt-hl-what">{h.events.map((e) => CWT_EVENT_KO[e] ?? e).join(', ') || '치열한 공방'}</span>
					{h.hpLost > 0 ? <em class="cwt-hl-hp">체력 감소 {Math.round(h.hpLost)}%</em> : null}
				</li>)}
			</ol>
		</section>;
	}

	/** 색종이 — 절차 생성이다. 낱조각 그림을 반복 배치하면 같은 모양이 줄줄이 보인다. */
	renderConfetti() {
		const bits = [];
		for (let i = 0; i < 30; i++) {
			const x = (i * 137) % 100;
			const delay = ((i * 313) % 900) / 1000;
			const dur = 2.6 + ((i * 71) % 900) / 1000;
			const rot = (i % 2 ? 1 : -1) * (120 + (i * 37) % 180);
			bits.push(<i class="cwt-bit" style={`left:${x}%;animation-delay:${delay}s;animation-duration:${dur}s;--cwt-rot:${rot}deg`} />);
		}
		return <div class="cwt-confetti" aria-hidden="true">{bits}</div>;
	}

	// ---- 렌더 ----------------------------------------------------------------

	override render() {
		const room = this.props.room;
		const view = this.view;
		const narrow = room.width > 0 && room.width < 720;

		return <PSPanelWrapper room={room} scrollable>
			<style>{CWT_CSS}</style>
			<style>{CWS_CSS}</style>
			<style>{CWR_CSS}</style>
			<style>{CWX_CSS}</style>
			<style>{CWT_REWARDS_CSS}</style>
			<div class={`cwt-root cwt-midnight${narrow ? ' is-narrow' : ''}${view && ['running', 'done'].includes(view.status) ? ' is-competition' : ''}`}>
				<div class="cwt-arena" aria-hidden="true" />
				{!this.loaded ? <div class="cwt-status-panel" role="status"><span class="cwt-stage-eyebrow">CHERISH CUP</span><h3>대회장을 준비하고 있어요</h3><p>대회 정보를 불러오는 중입니다.</p></div> : null}
				{this.loaded && this.failed && !view
					? <div class="cwt-status-panel" role="status"><h3>대회장에 연결하지 못했어요</h3><p>연결되면 대회 정보를 다시 표시합니다.</p></div> : null}
				{this.failed && view
					? <p class="cwt-connection-note" role="status">연결이 잠시 불안정해요. 아래는 마지막으로 받은 내용이에요.</p> : null}
				{this.loaded && !this.failed && !view ? <>
					<div class="cwt-shop-status">
						<img src="/cherish/tour/midnight/trophy.png" alt="" />
						<div><h3>체리쉬 컵</h3><p>다음 대회를 기다리는 동안, 모아둔 리본으로 선물을 만나보세요.</p></div>
					</div>
					{this.renderRibbonShop()}
				</> : null}
				{cwtFeed.stale ? <div class="cwt-stale" role="status">
					<span><b>새 버전이 있어요.</b> 지금 화면은 옛 버전이라 일부가 다르게 보일 수 있어요. 새로고침하면 최신 화면으로 바뀌어요.</span>
					<button class="cwt-btn" onClick={() => { location.reload(); }}>새로고침</button>
				</div> : null}
				{view ? <div class="cwt-head">
					<h3>{view.title}</h3>
					<span class="cwt-badges">
						{view.mode === 'sprout' ? <em class="cwt-badge is-sprout">새싹컵</em> : null}
						{view.mode === 'raid' ? <em class="cwt-badge is-raid">마왕토벌전</em> : null}
						{view.mode === 'swiss' ? <em class="cwt-badge is-swiss">빙글빙글컵</em> : null}
						{view.status === 'signup' ? <em class={`cwt-badge${view.sprout ? ' is-live' : ''}`}>{view.phase === 'lend' ? '빌려주기' : view.phase === 'pick' ? '뽑기 진행 중' : view.signupClosed ? '접수 마감' : '접수 중'}</em> : null}
						{view.status === 'running' && view.raid
							? <em class="cwt-badge is-live">{this.raidPhaseLabel(view.raid)}</em>
							: view.status === 'running' && view.currentRound
							? <em class="cwt-badge is-live">{view.phase === 'reveal' ? '대진 공개' : `${this.roundLabel(view.currentRound)} ${view.phase === 'intermission' ? '준비 중' : '진행 중'}`}</em> : null}
						{view.status === 'done' ? <em class="cwt-badge">종료</em> : null}
						{view.status === 'canceled' ? <em class="cwt-badge">취소</em> : null}
					</span>
				</div> : null}
				{view?.status === 'canceled' ? <div class="cwt-status-panel"><h3>이번 대회가 취소되었습니다</h3><p>다음 대회에서 다시 만나요.</p></div> : null}

				{view && view.status === 'signup' && view.sprout ? this.renderSprout() : null}
				{view ? this.renderVoicePopup() : null}

				{view && view.status === 'signup' && !view.sprout && view.mode === 'sprout' ? <>
					{this.renderSignup()}
					<section class="cwt-guide cwt-lobby-guide is-sprout" aria-label="새싹컵 진행 안내">
						<div class="cwt-guide-heading"><span class="cwt-stage-eyebrow">ROAD TO THE SPROUT CUP</span><h4>새싹컵은 이렇게 진행돼요</h4><p>잘하는 선배가 아이를 빌려주고, 새싹 선수가 그 아이와 함께 싸워요.</p></div>
						<ol>
							<li><span>01</span><div><b>반 나누기</b><p>신청이 마감되면 1대1 점수 순으로 반을 나눠요. 점수가 같으면 스타팅을 늦게 받은 분이 아래쪽이에요.</p></div></li>
							<li><span>02</span><div><b>빌려주기 2분</b><p>위쪽 절반은 아이 한 마리를 골라 빌려줘요. 대회 동안만 쓰는 복사본이라 내 아이는 그대로예요.</p></div></li>
							<li><span>03</span><div><b>뽑기</b><p>아래쪽 절반이 가장 아래 순서부터 한 명씩, 30초 안에 데려올 아이를 골라요.</p></div></li>
							<li><span>04</span><div><b>함께 출전</b><p>데려온 아이 하나와 내 아이 둘로 매 판 싸워요. 선배들은 응원과 승부 예측으로 함께해요.</p></div></li>
						</ol>
						<p class="cwt-warn"><b>입장 안내</b> 뽑기와 대회 중에는 배틀 사이트에서 대기해주세요. 차례가 왔을 때 자리에 없으면 남은 아이 중 하나가 정해지고, 경기는 잠시 기다린 뒤 부전패로 처리돼요.</p>
					</section>
				</> : null}

				{view && view.status === 'signup' && view.mode === 'raid' ? <>
					{this.renderSignup()}
					<section class="cwt-guide cwt-lobby-guide is-raid" aria-label="마왕토벌전 진행 안내">
						<div class="cwt-guide-heading"><span class="cwt-stage-eyebrow">ROAD TO THE DEMON KING RAID</span><h4>마왕토벌전은 이렇게 진행돼요</h4><p>마왕 몇 명이 {cwrCountKo(this.raidSizes().boss)}로 버티고, 토벌대가 {cwrCountKo(this.raidSizes().raider)}씩 들고 차례로 덤벼요.</p></div>
						<ol>
							<li><span>01</span><div><b>팀 나누기</b><p>신청이 마감되면 {this.raidSizes().boss > 3 ? '6대6' : '1대1'} 점수 순으로 위에서 몇 분이 마왕이 돼요. 마왕은 명단 앞 {this.raidSizes().boss}마리가 그대로, 토벌대는 명단에서 판마다 {cwrCountKo(this.raidSizes().raider)}를 골라요.</p></div></li>
							<li><span>02</span><div><b>리더 투표 30초</b><p>팀마다 카드를 눌러 리더를 뽑아요. 리더는 매 판 다음 주자를 지명해요. 바꾸지 않아요.</p></div></li>
							<li><span>03</span><div><b>선택 창 30초</b><p>토벌대 리더는 다음 도전자를, 서 있는 마왕은 선봉을 동시에 비공개로 골라요. 토벌대원은 「나 갈래」로 손들 수 있어요. 끝나면 동시에 공개돼요.</p></div></li>
							<li><span>04</span><div><b>이어지는 체력</b><p>이긴 쪽은 체력과 상태이상 그대로 다음 상대를 맞아요. PP·랭크업·도구는 새 판처럼 돌아와요. 한 팀이 전원 쓰러지면 끝이에요.</p></div></li>
						</ol>
						<p class="cwt-warn"><b>입장 안내</b> 대회 중에는 배틀 사이트에서 기다려주세요. 지명됐을 때 자리에 없으면 20초 뒤 쓰러진 것으로 처리돼요.</p>
					</section>
				</> : null}

				{view && view.status === 'signup' && view.mode === 'swiss' ? <>
					{this.renderSignup()}
					<section class="cwt-guide cwt-lobby-guide is-swiss" aria-label="빙글빙글컵 진행 안내">
						<div class="cwt-guide-heading"><span class="cwt-stage-eyebrow">ROAD TO THE ROUND ROBIN CUP</span><h4>빙글빙글컵은 이렇게 진행돼요</h4><p>아무도 탈락하지 않아요. 전원이 매 라운드 동시에 싸우고, 마지막에 1·2위가 결승을 붙어요.</p></div>
						<ol>
							<li><span>01</span><div><b>파트너 준비</b><p>「내 포켓몬」의 명단 6칸을 준비하세요. 시작하면 그 6마리로 잠기고, 라운드마다 그 안에서 3마리를 골라요.</p></div></li>
							<li><span>02</span><div><b>전원 동시 라운드</b><p>인원에 따라 3~5라운드. 라운드마다 모두가 동시에 한 판씩 싸우고, 다음 라운드는 승수가 같은 사람끼리 붙어요. 이미 만난 상대와는 다시 붙지 않아요.</p></div></li>
							<li><span>03</span><div><b>순위표</b><p>이긴 판 수로 순위를 매기고, 같으면 만난 상대들의 승수 합으로 가려요. 자기 판이 끝나면 남은 판을 관전할 수 있어요.</p></div></li>
							<li><span>04</span><div><b>결승 6대6</b><p>라운드가 모두 끝나면 1위와 2위가 여섯 마리 전부를 데리고 결승 한 판을 붙어요. 승부 예측은 이 한 판에만 열려요.</p></div></li>
						</ol>
						<p class="cwt-warn"><b>입장 안내</b> 대회 중에는 배틀 사이트에서 대기해주세요. 라운드가 시작될 때 자리에 없으면 잠시 기다린 뒤 그 판은 상대 부전승이 되고, 두 라운드 연속 자리에 없으면 남은 라운드에서 빠져요.</p>
					</section>
				</> : null}

				{view && view.status === 'signup' && view.mode !== 'sprout' && view.mode !== 'raid' && view.mode !== 'swiss' ? <>
					{this.renderSignup()}
					<section class="cwt-guide cwt-lobby-guide" aria-label="대회 진행 안내">
						<div class="cwt-guide-heading"><span class="cwt-stage-eyebrow">ROAD TO THE CUP</span><h4>우승까지의 여정</h4><p>신청부터 마지막 배틀까지, 이렇게 진행됩니다.</p></div>
						<ol>
							<li><span>01</span><div><b>파트너 준비</b><p>「내 포켓몬」의 명단 6칸을 준비하세요. 개막 후에는 그 안에서 판마다 3마리를 선택해요.</p></div></li>
							<li><span>02</span><div><b>대진 공개, 자동 입장</b><p>참가자가 차례로 공개되고, 대진이 완성되면 배틀이 자동으로 시작돼요.</p></div></li>
							<li><span>03</span><div><b>마지막 무대까지</b><p>승자는 다음 라운드로 진출해요. 탈락 후에는 관전하며 준결승과 결승을 함께 즐겨요.</p></div></li>
						</ol>
						<p class="cwt-warn"><b>입장 안내</b> 대회 중에는 배틀 사이트에서 대기해주세요. 차례가 왔을 때 자리에 없으면 잠시 기다린 뒤 부전패로 처리돼요.</p>
					</section>
				</> : null}

				{view && view.raid && (view.status === 'running' || view.status === 'done') ? <>
					{view.status === 'done' ? view.roulette ? this.renderRouletteSummary() : this.renderRaidCeremony() : null}
					{view.status === 'done' ? this.renderRouletteBox() : null}
					{view.status === 'done' && !view.roulette ? this.renderStandings() : null}
					{view.status === 'done' && view.roulette ? this.renderResultRecords(narrow) : this.renderRaid()}
					{view.status === 'done' ? view.roulette ? this.renderResultShop() : this.renderRibbonShop() : null}
				</> : null}

				{view && view.status === 'running' && !view.raid ? <>
					{this.renderRoundStage()}
					{this.renderMyMatch()}
					{this.renderBet()}
					{view.phase === 'reveal' ? null : this.renderLineup()}
					{view.swiss ? this.renderSwiss(narrow) : this.renderArena(narrow)}
				</> : null}

				{view && view.status === 'done' && !view.raid ? <>
					{view.roulette ? this.renderRouletteSummary() : this.renderCeremony()}
					{this.renderRouletteBox()}
					{view.roulette ? null : this.renderStandings()}
					{view.roulette ? this.renderResultRecords(narrow) : <>{this.renderBet()}{view.swiss ? this.renderSwiss(narrow) : this.renderArena(narrow)}</>}
					{view.roulette ? this.renderResultShop() : this.renderRibbonShop()}
					{view.swiss ? null : (() => {
						// 결승 칸 = 라운드가 가장 큰 칸. 그 경기의 명장면만 보여 준다.
						const last = view.matches.reduce<CwtMatch | null>(
							(best, m) => (!best || m.round > best.round ? m : best),
							null,
						);
						return last?.highlights?.length ? <details class="cwt-transcript"><summary>결승 명장면 보기</summary>{this.renderHighlights(last)}</details> : null;
					})()}
				</> : null}
				{view && ['signup', 'running', 'done'].includes(view.status) ? this.renderTalk() : null}
				{this.renderChampionPopup()}
			</div>
		</PSPanelWrapper>;
	}
}

PS.addRoomType(CherishTourPanel);

/**
 * 배틀홈 배너 — 열린 대회가 있을 때만 뜬다. 누르면 위 방이 열린다.
 *
 * **자기 데이터를 직접 들고 온다.** 홈 패널의 공용 로더(`loadCherishData`)에 끼우지 않는
 * 이유는 수술 면적이다 — 로더에 손대면 패널 4개가 쓰는 공용 코드를 건드려야 하고, 이
 * 패치를 되돌릴 때도 그만큼 흔적이 남는다. 배너는 열린 대회가 없으면 **아무것도 그리지
 * 않으므로**(null) 평시 홈에는 존재하지 않는다.
 */
export class CherishTourBanner extends preact.Component {
	view: CwtView | null = null;
	override componentDidMount() {
		cwtFeed.subscribe(this.sub);
	}
	override componentWillUnmount() {
		cwtFeed.unsubscribe(this.sub);
	}
	onFeed = () => {
		const d = cwtFeed.view;
		// 끝난 대회도 잠깐 띄운다(서버가 15분만 내려 준다) — 우승 발표를 놓친 사람이 들어올 길
		this.view = d && (d.status === 'signup' || d.status === 'running' || d.status === 'done')
			? d : null;
		this.forceUpdate();
	};
	sub: CwtSub = { fn: () => this.onFeed(), want: () => 15000 };
	handleOpen = () => {
		CherishTourStrip.showBracket();
	};
	override render() {
		const v = this.view;
		// 대회가 없을 때는 리본 알림만 — 리본이 있는 사람에게 상점으로 가는 길 (요청은 홈을 열 때 한 번)
		if (!v || (v.status === 'done' && v.resultBannerHidden)) return cwtFeed.loaded ? <CwtRibbonPill onOpen={this.handleOpen} /> : null;
		const live = v.status === 'running';
		const over = v.status === 'done';
		if (v.status === 'signup' && !v.signupClosed && !v.sprout) {
			const mode = v.mode === 'swiss' ? 'swiss' : v.mode === 'raid' ? 'raid' : v.mode === 'sprout' ? 'sprout' : 'cup';
			const detail = mode === 'swiss' ? '탈락 없이 끝까지 · 마지막은 6대6 결승' : mode === 'raid' ? `함께 도전하는 마왕토벌전 · ${(v.raidSizes && v.raidSizes.raider) || 1}마리부터 참가` : mode === 'sprout' ? '새로운 시작을 응원하는 무대 · 2마리부터 참가' : '함께 겨루고, 새로운 우승의 순간을 만나요';
			return <><style>{CWT_BANNER_CSS}</style>
				<button type="button" class={`cwt-invite is-${mode}`} onClick={this.handleOpen} aria-label={`${v.title} 참가 신청 화면 열기`}>
					<div class="cwt-invite-copy"><span class="cwt-invite-eyebrow"><i /> 참가 신청 진행 중</span><strong>{v.title}</strong><span class="cwt-invite-detail">{detail}</span><span class="cwt-invite-cta">대회 참가하기 <b>↗</b></span></div>
					<div class="cwt-invite-art" aria-hidden="true"><span /><img src="/cherish/tour/midnight/trophy.png" alt="" /><i>CHERISH TOURNAMENT</i></div>
				</button></>;
		}
		return <div class="cwt-banner" onClick={this.handleOpen}>
			<style>{CWT_BANNER_CSS}</style>
			<div class="cwt-banner-in">
				<strong>{v.title}</strong>
				<span class="cwt-banner-sub">{cwtFeed.stale ? '새 버전이 있어요. 새로고침해주세요'
					: over ? '우승자가 정해졌어요'
					: live ? '지금 진행 중이에요. 보러 갈까요?'
					: v.sprout ? (v.sprout.phase === 'lend' ? '선배들이 빌려줄 아이를 고르고 있어요' : '새싹 선수들이 함께할 아이를 뽑고 있어요')
					: v.signupClosed ? '접수가 마감됐어요. 곧 시작해요'
					: v.mode === 'sprout' ? '새싹컵 참가 신청을 받고 있어요. 2마리부터 참가할 수 있어요'
					: v.mode === 'raid' ? `마왕토벌전 참가 신청을 받고 있어요. ${(v.raidSizes && v.raidSizes.raider) || 1}마리부터 참가할 수 있어요`
					: v.mode === 'swiss' ? '빙글빙글컵 참가 신청을 받고 있어요. 탈락 없이 전원이 끝까지 뛰어요'
					: '참가 신청을 받고 있어요'}</span>
			</div>
			<span class="cwt-banner-go">{over ? '결과 보기' : live ? '관전하기' : v.sprout || v.signupClosed ? '보러 가기' : '신청하기'}</span>
		</div>;
	}
}

/**
 * 홈의 리본 한 줄 (2026-10-14) — 열린 대회가 없을 때 대회 배너 자리에 「🎀 리본 N개 · 리본 상점 ›」. 누르면 대회 방이 열리고
 * (대회가 없어도 방은 열린다 — 「지금은 열린 대회가 없어요」 아래가 상점이다) 거기서 상자를 산다.
 * **리본이 0 개여도 그린다** — 상점으로 가는 길이 이것 하나라 숨기면 상점이 없는 것과 같다 (사용자 지적). 로그인 전(응답 실패)엔 안 그린다.
 * 지갑은 **마운트 때 한 번만** 묻는다(평시 홈에 폴링을 더하지 않는다). 상자를 연 뒤 홈으로 돌아오면 다시 마운트돼 새 값을 본다.
 */
export class CwtRibbonPill extends preact.Component<{ onOpen: () => void }> {
	balance: number | null = null;
	override componentDidMount() {
		fetch(`${CWT_API.replace(/\/tournament$/, '')}/ribbons`, { credentials: 'include' })
			.then(r => (r.ok ? r.json() : null))
			.then(json => {
				if (json?.ok) {
					this.balance = (json.data as CwlRibbons).wallet.balance;
					this.forceUpdate();
				}
			})
			.catch(() => undefined);
	}
	override render() {
		if (this.balance === null) return null;
		return <button type="button" class="cwt-ribbon-pill" onClick={this.props.onOpen} aria-label={`리본 ${this.balance}개, 리본 상점 열기`}>
			<style>{CWT_BANNER_CSS}</style>
			🎀 리본 <b>{this.balance}</b>개 <span>{this.balance > 0 ? '리본 상점 ›' : '대회에 나가면 받아요 · 리본 상점 ›'}</span>
		</button>;
	}
}

/*
 * 대회 중 자동매칭 잠금 (2026-10-27) — 배틀홈 「자동매칭 시작」 자리의 대회 카드 + 서버 팝업의 대회판.
 *
 * 대회가 접수·진행 중이면 api 가 **전 유저의** 자동매칭을 잠근다(`tournamentLocksEveryone`). 전에는 홈이 그걸 몰라
 * 「자동매칭 시작」이 멀쩡히 켜져 있었고, 누르면 서버가 학교 졸업 문구를 흰 기본 팝업으로 띄웠다(잠금 표식 하나를
 * 학교와 같이 써서). 이제 홈은 통로가 받아 둔 `matchLocked` 를 보고 **누르기 전에** 대회 카드를 그린다.
 * 참가자만 잠그는 공개 리허설은 이 응답이 「나」를 모르므로 false 다 — 그 사람은 서버 팝업으로 알고, 그 팝업도
 * 아래 `PS.alert` 감싸기가 대회판으로 바꿔 그린다. 통로가 1분 주기라 접수가 막 열린 직후에도 같은 길로 안다.
 */

/**
 * 서버 게이트(patch-school-gate.py)가 보내는 대회 문구와 **같은 글자**다. 이 문장이 오면 흰 기본 팝업 대신
 * 대회 팝업을 그린다. 한쪽만 고치면 흰 팝업으로 돌아간다(기능은 그대로 — 문구만 기본 상자에 뜬다).
 */
const CWT_LOCK_POPUP = '지금은 대회가 진행 중이라 매치메이킹이 불가능해요.\n대회가 끝나면 다시 열려요';

/** 서버 팝업은 줄바꿈을 `||` 로 실어 보내고 클라가 되돌린다 — 공백만 다르게 와도 알아보게 고르게 편다. */
function cwtFlat(s: string) {
	return s.replace(/\|\|/g, ' ').replace(/\s+/g, ' ').trim();
}

/** 홈이 대회 카드를 그릴 때인가 — 접수·진행 중이고 서버가 전원 잠금이라고 했을 때만. */
function cwtLockShows(v: CwtView | null): v is CwtView {
	return !!v && v.matchLocked === true && (v.status === 'signup' || v.status === 'running');
}

/**
 * 「자동매칭 시작」 자리를 감싼다 (patch-ffa-client.py `renderSearchButton`). 잠겨 있으면 대회 카드, 아니면 children.
 * 대기 중(이미 큐에 선 사람)에는 감싸지 않는다 — 그 사람에겐 「취소」 버튼이 있어야 한다. 요청은 통로 하나에 얹는다.
 */
export class CherishTourMatchGate extends preact.Component<{ children?: any }> {
	view: CwtView | null = null;
	override componentDidMount() {
		cwtFeed.subscribe(this.sub);
	}
	override componentWillUnmount() {
		cwtFeed.unsubscribe(this.sub);
	}
	sub: CwtSub = { fn: () => { this.view = cwtFeed.view; this.forceUpdate(); }, want: () => 15000 };
	override render() {
		const v = this.view;
		if (!cwtLockShows(v)) return this.props.children;
		const live = v.status === 'running';
		return <div class="menugroup" style="max-width:none">
			<style>{CWT_LOCK_CSS}</style>
			<button type="button" class="cw-cta is-tour" onClick={() => CherishTourStrip.showBracket()}>
				<span class="cw-cta-icon"><img src="/cherish/tour/midnight/trophy.png" alt="" /></span>
				<span class="cw-cta-body">
					<span class="cwt-lock-eyebrow"><i />{live ? 'LIVE' : '곧 시작'}</span>
					<span class="cw-cta-title">{v.title} {live ? '진행 중' : '준비 중'}</span>
					<span class="cw-cta-sub">{CWT_LOCK_POPUP.replace('\n', ' ')}</span>
				</span>
				<span class="cwt-lock-go">{live ? '관전하기' : '보러 가기'} ›</span>
			</button>
		</div>;
	}
}

let cwtLockModalOpen = false;
/** 대회판 팝업 — 첫 안내 팝업(cw-intro)과 같은 틀에 대회 머리띠만 두른다. DOM 으로 body 에 붙인다(다른 팝업들과 같은 방식). */
function cwtShowLockModal() {
	if (cwtLockModalOpen || typeof document === 'undefined') return;
	cwtLockModalOpen = true;
	const v = cwtFeed.view;
	const live = v?.status !== 'signup';
	const el = (tag: string, cls?: string, text?: string) => {
		const n = document.createElement(tag);
		if (cls) n.className = cls;
		if (text) n.textContent = text;
		return n;
	};
	const wrap = el('div', 'cw-intro-backdrop cwt-lock-backdrop');
	const style = el('style');
	style.textContent = CWT_LOCK_CSS;
	const box = el('div', 'cw-intro cwt-lock-modal');
	box.setAttribute('role', 'dialog');
	box.setAttribute('aria-modal', 'true');
	const head = el('div', 'cwt-lock-modal-head');
	const img = el('img') as HTMLImageElement;
	img.src = '/cherish/tour/midnight/trophy.png';
	img.alt = '';
	const titles = el('span');
	titles.appendChild(el('small', '', live ? 'LIVE' : '곧 시작'));
	titles.appendChild(el('b', '', v?.title ? `${v.title} ${live ? '진행 중' : '준비 중'}` : '대회 진행 중'));
	head.appendChild(img);
	head.appendChild(titles);
	const body = el('div', 'cwt-lock-modal-body');
	body.appendChild(el('p', '', CWT_LOCK_POPUP));
	const foot = el('div', 'cw-intro-foot');
	const close = el('button', 'cw-intro-close', '닫기') as HTMLButtonElement;
	const go = el('button', 'cw-intro-go', live ? '관전하러 가기' : '보러 가기') as HTMLButtonElement;
	foot.appendChild(close);
	foot.appendChild(go);
	body.appendChild(foot);
	box.appendChild(head);
	box.appendChild(body);
	wrap.appendChild(style);
	wrap.appendChild(box);
	const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') shut(); };
	const shut = () => {
		cwtLockModalOpen = false;
		document.removeEventListener('keydown', onKey, true);
		wrap.remove();
	};
	close.onclick = shut;
	go.onclick = () => { shut(); CherishTourStrip.showBracket(); };
	// 바깥(어두운 바탕)만 눌렀을 때 닫는다 — 상자 안 글자를 누른 건 무시
	wrap.onclick = (e: MouseEvent) => { if (e.target === wrap) shut(); };
	document.addEventListener('keydown', onKey, true);
	document.body.appendChild(wrap);
	go.focus();
	// 홈이 아직 대회를 모른다는 뜻이다 — 통로를 한 번 당겨 카드도 바로 바뀌게 한다
	cwtFeed.refresh();
}

/*
 * 서버 팝업은 `case 'popup'` → `PS.alert()` 로 온다. 대회 문구일 때만 가로채고 나머지는 그대로 넘긴다
 * (영어 차단 patch-alert-mask.py 도 원래 함수 안에 있어 그대로 탄다). 이 스크립트는 client-main 뒤에 실행된다.
 */
if (typeof PS !== 'undefined' && PS && typeof (PS as any).alert === 'function') {
	const cwtAlert = (PS as any).alert;
	(PS as any).alert = function (this: any, message: string, opts?: any) {
		if (typeof message === 'string' && cwtFlat(message) === cwtFlat(CWT_LOCK_POPUP)) {
			cwtShowLockModal();
			return;
		}
		return cwtAlert.call(this, message, opts);
	};
}

/**
 * 이 방이 **지금 도는 대회의 경기**인가.
 *
 * 종료 카드가 「메인 메뉴」 대신 「대진표로」를 그릴지 고르는 데 쓴다 (panel-battle.tsx 의
 * `renderCherishTourEnd`). 방 id 로만 판정한다 — 참가자든 관전자든 이 방을 보고 있으면
 * 돌아갈 곳은 대진표다. 통로가 이미 받아 둔 값을 볼 뿐이라 **요청을 새로 만들지 않는다**.
 */
export function cherishTourHasRoom(roomid: string): boolean {
	const v = cwtFeed.view;
	// 끝난 대회도 잠깐은 본다 — 결승 종료 카드에서 우승 연출로 가는 길이 여기다
	if (!v || (v.status !== 'running' && v.status !== 'done')) return false;
	return v.matches.some(m => m.roomId === roomid);
}
if (typeof window !== 'undefined') (window as any).cherishTourHasRoom = cherishTourHasRoom; // 마왕토벌전 실시간판 종료 카드가 typeof 가드로 쓴다 (11-03)

/**
 * 배틀방 조작줄의 관전 띠 — 「대진표」 + 지금 돌아가는 다른 경기 칩.
 *
 * 배틀을 보면서도 다른 판세가 보이고, 칩을 누르면 그 경기로 **갈아탄다**(관전 슬롯 1개).
 * 대회가 돌지 않으면 아무것도 그리지 않는다.
 *
 * ⚠️ 여기에 **관전 인원수를 넣지 않는다.** 경기마다 몇 명이 보는지를 나란히 늘어놓으면
 *    인기 서열이 그대로 드러난다(사용자 확정). 조작줄 오른쪽의 방 인원 표시는 업스트림
 *    것이고 한 방 기준이라 무관하다.
 */
export class CherishTourStrip extends preact.Component<{ room: { id: string }, commentary?: boolean }> {
	/**
	 * 종료 카드의 「대진표로 돌아가기」 (patch-tournament-panel.py 가 부른다).
	 *
	 * ⚠️ 대진표를 열고 배틀방을 닫는 것만으로는 **홈으로 떨어질 때가 있다** (2026-09-28 18회).
	 * 배틀이 막 끝난 직후엔 서버가 그 방 메시지를 조금 더 보내는데, 닫은 방에 `|init|` 이 오면
	 * 클라가 방을 다시 열어 포커스를 옮기고, 이어서 `|deinit|` 이 오면 그 방을 닫으며 **홈으로**
	 * 보낸다(브라우저에서 재현 확인). 그래서 몇 초 동안 지켜보며 되살아난 방은 다시 닫고,
	 * 홈으로 떨어졌으면 대진표로 되돌린다. 사용자가 그 사이 다른 방으로 가면 건드리지 않는다.
	 *
	 * ⚠️ 2026-10-10 「눌렀더니 메인화면」 — 위 가드가 두 자리에서 헛돌았다 (코드로 확인).
	 *   ① 되살아난 방을 **먼저** 닫았다. 그 방은 init 의 autofocus 로 포커스를 쥐고 있어 `leave` → `removeRoom`
	 *      이 「포커스 방이 닫힘」으로 보고 `PS.room` 을 홈으로 보낸 뒤 그 자리에서 다시 그렸다.
	 *   ② 그다음 `PS.join(tour)` 는 이미 있는 방이라 `focusRoom` 만 한다 — **화면을 다시 그리지 않는다.**
	 *      그래서 `PS.room` 은 대진표인데 화면은 홈인 채 남고, 홈을 한 번 누르는 순간 포커스까지 홈으로 굳는다.
	 *   순서를 「대진표 먼저 → 되살아난 방 닫기 → update」 로 바꿨다. 되살아난 방이 포커스를 아예 못 뺏게 하는
	 *   쪽(`patch-one-battle-tab.py` 의 autofocus 예외)과 한 쌍이다.
	 */
	/** 대진표를 **보이게** 연다 — 방이 있으면 focusRoom(패널까지 잡는다), 없으면 join(만들고 포커스). `PS.join` 하나로는 안 된다(위 주석). */
	static showBracket() {
		const tour = 'cherish-tour' as RoomID;
		if (PS.rooms[tour]) {
			PS.focusRoom(tour);
			// focusRoom 은 room 이 이미 같으면 setFocus 만 하고 패널을 안 바꾼다 — 패널이 다른 방이면 손으로 잡아 준다
			const room = PS.rooms[tour]!;
			if (PS.panel !== room || PS.leftPanel !== room) {
				(PS as any).leftPanel = room;
				(PS as any).panel = room;
				(PS as any).room = room;
				PS.update();
			}
		} else {
			PS.join(tour);
		}
	}
	static returnToBracket(roomid: string) {
		const tour = 'cherish-tour' as RoomID;
		/*
		 * 2026-10-12 세 번째 신고 — 코드로는 더 못 좁혀서 **현장을 적는다**. 누른 뒤 15초 동안 포커스를 옮기거나 방을 만들고
		 * 없애는 호출(focusRoom·removeRoom·addRoom)과 이 방의 init/deinit 을 스택과 함께 모아 api 로그로 보낸다
		 * (`POST /tournament/client-log`). 그동안 사용자가 아무것도 안 눌렀는데 화면이 대진표를 떠나면 되돌린다.
		 * 원인이 잡히면 이 기록 장치는 뺀다.
		 */
		const t0 = Date.now();
		const log: string[] = [];
		const stack = () => (new Error().stack || '').split('\n').slice(2, 6).map(l => l.trim().replace(/^at /, '').slice(0, 70)).join(' < ');
		const state = () => `room=${PS.room?.id ?? '-'} panel=${PS.panel?.id ?? '-'} left=${PS.leftPanel?.id ?? '-'} right=${PS.rightPanel?.id ?? '-'} rooms=${Object.keys(PS.rooms).filter(k => k.startsWith('battle-') || k.startsWith('cherish-tour')).join(',')}`;
		const note = (s: string) => { if (log.length < 70) log.push(`${Date.now() - t0}ms ${s}`); };
		const ps = PS as any;
		const orig = { removeRoom: ps.removeRoom, focusRoom: ps.focusRoom, addRoom: ps.addRoom, receive: ps.receive };
		ps.removeRoom = function (room: any) { note(`removeRoom ${room?.id} [${stack()}] ${state()}`); return orig.removeRoom.call(this, room); };
		ps.focusRoom = function (id: string) { if (id !== tour) note(`focusRoom ${id} [${stack()}]`); return orig.focusRoom.call(this, id); };
		ps.addRoom = function (opts: any) { note(`addRoom ${opts?.id} autofocus=${opts?.autofocus} [${stack()}]`); return orig.addRoom.call(this, opts); };
		ps.receive = function (msg: string) {
			if (typeof msg === 'string' && msg.startsWith('>' + roomid) && /\n\|(init|deinit)\|/.test(msg)) note(`receive ${msg.slice(0, 60).replace(/\n/g, ' ')}`);
			return orig.receive.call(this, msg);
		};
		let touched = false;
		const onPointer = () => { touched = true; };
		window.addEventListener('pointerdown', onPointer, true);
		note('click ' + state());
		/*
		 * 2026-10-13 로그로 확정한 원인: 누르는 순간 `PS.room` 은 이미 대진표인데 **보이는 패널(`panel`·`leftPanel`)은 배틀방**인
		 * 경우가 절반이었다. 그러면 `PS.join(tour)` 는 「이미 그 방」이라 아무것도 안 하고, 배틀방을 닫을 때 `removeRoom` 이
		 * 「보이던 패널이 닫혔다」로 패널을 홈으로 돌린다 — 내부 포커스는 대진표, 화면은 홈. 그래서 **패널을 직접** 옮긴다:
		 * `focusRoom` 은 room 이 같아도 leftPanel·panel 을 다시 잡고 update 까지 한다. 방이 없으면 join 이 만든다.
		 */
		CherishTourStrip.showBracket();
		PS.leave(roomid as RoomID);
		CherishTourStrip.showBracket();
		note('after ' + state());
		const until = t0 + 15000;
		const finish = () => {
			ps.removeRoom = orig.removeRoom; ps.focusRoom = orig.focusRoom; ps.addRoom = orig.addRoom; ps.receive = orig.receive;
			window.removeEventListener('pointerdown', onPointer, true);
			note('end ' + state());
			fetch(`${CWT_API}/client-log`, {
				method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ kind: 'return-to-bracket', roomid, events: log }),
			}).catch(() => undefined);
		};
		const guard = () => {
			const revived = !!PS.rooms[roomid];
			// 「떠났다」는 보이는 패널 기준이다 — PS.room 만 보면 위 사례를 정상으로 읽는다
			const away = PS.room?.id !== tour || PS.panel?.id !== tour || PS.leftPanel?.id !== tour;
			if (revived || (away && !touched)) {
				note(`guard revived=${revived} away=${away} ${state()}`);
				CherishTourStrip.showBracket();
				if (revived) PS.leave(roomid as RoomID);
				PS.update();
			}
			if (Date.now() < until) setTimeout(guard, 200);
			else finish();
		};
		setTimeout(guard, 0);
	}
	view: CwtView | null = null;
	override componentDidMount() {
		cwtFeed.subscribe(this.sub);
	}
	override componentWillUnmount() {
		cwtFeed.unsubscribe(this.sub);
	}
	onFeed = () => {
		const d = cwtFeed.view;
		this.view = d && d.status === 'running' ? d : null;
		this.forceUpdate();
	};
	/**
	 * ⚠️ 띠는 **배틀방마다 하나씩** 붙는다 — 여기서 바라는 주기가 곧 방 개수만큼 곱해지던
	 * 자리다. 지금은 통로가 하나라 몇 개가 붙어도 요청은 한 벌이고, 대회가 돌지 않으면
	 * 통로가 알아서 1분으로 내려간다. 이 값을 다시 개별 타이머로 되돌리지 말 것.
	 */
	sub: CwtSub = {
		fn: () => this.onFeed(),
		want: () => (cwtFeed.view?.status === 'running' ? 4000 : CWT_POLL_NONE),
	};
	/**
	 * 이 방의 최신 해설 한 줄.
	 *
	 * 준결승·결승은 한 판씩 도니까 사람들이 **대진표가 아니라 배틀 화면**에 모여 있다. 그런데
	 * 해설은 대진표 페이지에만 떠서, 정작 실황이 흐르는 동안 아무도 못 봤다 (2026-09-19).
	 * 방 id 가 붙은 줄만 고른다 — 남의 경기 실황이 내 화면에 끼어들면 안 된다.
	 */
	latestSay(): { teacher: string, text: string, at: number } | null {
		const v = this.view;
		if (!v) return null;
		const here = this.props.room.id;
		let best: { teacher: string, text: string, at: number } | null = null;
		for (const l of v.talk) {
			if (l.roomid !== here) continue;
			if (!best || l.at > best.at) best = { teacher: l.teacher, text: l.text, at: l.at };
		}
		return best;
	}

	handleBracket = () => {
		CherishTourStrip.showBracket();
	};
	handleHop = (e: Event) => {
		const roomid = (e.currentTarget as HTMLElement).getAttribute('data-room');
		if (!roomid) return;
		const here = this.props.room as unknown as { id: string, side?: unknown };
		// 내가 뛰는 방에서는 갈아타지 않는다 (관전 중일 때만 닫는다)
		if (here.id && here.id !== roomid && !here.side) PS.leave(here.id as RoomID);
		PS.join(roomid as RoomID);
	};
	override render() {
		const v = this.view;
		if (!v) return null;
		const here = this.props.room.id;
		// 내가 뛰는 판에서는 다른 경기 칩을 안 띄운다 (2026-10-06). 배틀 탭은 하나만이라
		// (patch-one-battle-tab.py) 다른 경기를 여는 순간 이 판이 기권으로 닫힌다 — 판 도중
		// 다른 배틀을 여는 길은 이 칩뿐이었다. 판이 끝나면 다시 뜬다.
		const playing = !!(this.props.room as any).isPlaying?.();
		const live = playing ? [] : v.matches.filter(m => m.state === 'live' && m.roomId && m.roomId !== here);
		const say = this.latestSay();
		if (this.props.commentary) {
			if (!say) return null;
			const who = CWT_TEACHERS[say.teacher] ?? CWT_TEACHERS.cherry!;
			return <section class="cwt-live-commentary" aria-label="경기 해설">
				<style>{CWT_STRIP_CSS}</style>
				<img src={who.img} alt="" />
				<div><header><b>{who.name}</b><span>경기 해설</span></header>
					<p key={`${say.at}-${say.teacher}`} aria-live="polite" aria-atomic="true">{say.text}</p>
				</div>
			</section>;
		}
		return <>
			{/* 배틀방에는 배너가 없으니 띠가 자기 CSS 를 직접 싣는다 */}
			<style>{CWT_STRIP_CSS}</style>
			<button class="button cwt-strip-btn" onClick={this.handleBracket} title="대진표로 돌아가기">
				<i class="fa fa-sitemap" aria-hidden></i> 대진표
			</button>
			{live.map(m => <button
				class={m.lastStand ? 'button cwt-strip-chip is-last' : 'button cwt-strip-chip'}
				data-room={m.roomId!}
				onClick={this.handleHop}
				title="이 경기로 갈아타기"
			>{m.slot + 1}경기{m.alive ? ` ${m.alive[0]}-${m.alive[1]}` : ''}{m.lastStand ? '!' : ''}</button>)}
		</>;
	}
}

const CWT_BANNER_CSS = `
.cwt-invite{--invite-accent:#f0d39a;position:relative;isolation:isolate;display:flex;align-items:center;justify-content:space-between;overflow:hidden;width:100%;min-height:228px;margin:0 0 16px;padding:28px 36px;border:1px solid #a58e62;border-radius:18px;background:linear-gradient(90deg,#0b142af5 5%,#152039d9 52%,#12203855),url('/cherish/tour/midnight/arena.png') center 47%/cover;color:#fff4dc;font:inherit;text-align:left;cursor:pointer;box-shadow:0 12px 28px #101b3026;transition:transform .2s,box-shadow .2s}
.cwt-invite.is-swiss{--invite-accent:#b9dfed}.cwt-invite.is-sprout{--invite-accent:#c6e6b3}.cwt-invite.is-raid{--invite-accent:#f0b1a0}
.cwt-invite:hover{transform:translateY(-2px);box-shadow:0 16px 32px #101b303d}.cwt-invite:focus-visible{outline:3px solid #d996bd;outline-offset:3px}
.cwt-invite-copy{display:flex;flex-direction:column;align-items:flex-start;gap:12px;z-index:1;max-width:72%}
.cwt-invite-eyebrow{font-size:11px;letter-spacing:2px;color:var(--invite-accent);display:flex;align-items:center;gap:8px}.cwt-invite-eyebrow i{width:6px;height:6px;border-radius:50%;background:var(--invite-accent);box-shadow:0 0 10px var(--invite-accent)}
.cwt-invite-copy strong{font-size:clamp(26px,3vw,38px);letter-spacing:-1px;line-height:1.2}.cwt-invite-detail{font-size:13px;color:#c1cddd;line-height:1.7}.cwt-invite-cta{display:flex;gap:24px;align-items:center;margin-top:6px;padding:11px 18px;border:1px solid #f2d99c;background:linear-gradient(120deg,#f4dfb2,#d9b975);color:#302a23;border-radius:8px;font-size:13px;font-weight:700}.cwt-invite-cta b{font-size:18px}
.cwt-invite-art{position:relative;display:flex;flex-direction:column;align-items:center;width:30%;flex-shrink:0}.cwt-invite-art img{width:156px;max-width:100%;position:relative;filter:drop-shadow(0 0 24px #efd19355)}.cwt-invite-art span{position:absolute;inset:0;border:1px solid #efd19340;border-radius:50%;transform:scale(1.15)}.cwt-invite-art i{font-size:8px;font-style:normal;letter-spacing:3px;color:#e0c589;white-space:nowrap;margin-top:8px}
@media(max-width:520px){.cwt-invite{padding:24px 20px;min-height:236px}.cwt-invite-copy{max-width:76%;gap:12px}.cwt-invite-copy strong{font-size:28px}.cwt-invite-detail{font-size:12px}.cwt-invite-art{width:24%;align-self:center;margin-right:-6px}.cwt-invite-art i{display:none}.cwt-invite-eyebrow{font-size:10px;letter-spacing:1px}}
@media(prefers-reduced-motion:reduce){.cwt-invite{transition:none}}

.cwt-banner{display:flex;align-items:center;gap:10px;cursor:pointer;margin:0 0 12px;
 padding:11px 14px;border-radius:12px;color:#5c4a42;background:#fdf7ee;
 border:1px solid #c8a89a;transition:transform .15s}
.cwt-banner:hover{transform:translateY(-1px)}
.cwt-banner-in{display:flex;flex-direction:column;min-width:0;flex:1 1 auto}
.cwt-banner-in strong{font-size:16px}
.cwt-banner-sub{font-size:12px;opacity:.7}
.cwt-banner-go{flex:0 0 auto;font-size:13px;padding:5px 12px;border-radius:999px;
 background:#ce6e7a;color:#fff}
.cwt-ribbon-pill{font:inherit;display:inline-flex;align-items:center;gap:5px;margin:0 0 12px;padding:7px 14px;border-radius:999px;
 cursor:pointer;color:#5c4a42;background:#fdf1f5;border:1px solid #e9b8c9;font-size:13px}
.cwt-ribbon-pill b{color:#c14d6e}
.cwt-ribbon-pill span{opacity:.7;font-size:12px}
.cwt-ribbon-pill:hover{transform:translateY(-1px)}
.cwt-banner-signup-art{display:block;width:100%;aspect-ratio:5/1;min-height:0;box-sizing:border-box;
 border:0;padding:0;margin:0 0 12px;cursor:pointer;
 background:#071222 url('/cherish/tour/midnight/banner-signup-a-desktop.png') center 45%/100% auto no-repeat;
 filter:drop-shadow(0 8px 15px #0c162b30);transition:filter .18s}
.cwt-banner-signup-art:hover{filter:drop-shadow(0 9px 18px #0c162b58) brightness(1.07)}
.cwt-banner-signup-art:focus-visible{outline:3px solid #f4d692;outline-offset:3px}
@media(max-width:720px){.cwt-banner-signup-art{aspect-ratio:4/3;
 background-image:url('/cherish/tour/midnight/banner-signup-a-mobile.png');
 background-position:center;background-size:cover}}
@media(prefers-reduced-motion:reduce){.cwt-banner-signup-art{transition:none}}
`;

/*
 * 대회 중 자동매칭 잠금 카드·팝업 (2026-10-27). 카드는 평소 「자동매칭 시작」(스킨 .cw-cta)의 틀·크기·글꼴을 그대로 물려받고
 * 재질만 대회 미드나잇(배너와 같은 경기장·트로피 그림)으로 바꾼다. 글꼴은 지정하지 않는다 — 다른 홈 버튼처럼 본문 글꼴을 탄다.
 */
const CWT_LOCK_CSS = `
.cw-cta.is-tour{justify-content:flex-start;gap:12px;padding:14px 14px 14px 16px;color:#fff4dc;
 background:linear-gradient(90deg,#0b142af2 0%,#152039e0 55%,#1220388a 100%),url('/cherish/tour/midnight/arena.png') center 47%/cover,#0e1830;
 box-shadow:0 10px 24px -8px #101b3070,inset 0 0 0 1px #a58e6288,inset 0 1px 0 #f0d39a40}
.cw-cta.is-tour::before{opacity:.06}
.cw-cta.is-tour:hover{box-shadow:0 14px 28px -8px #101b3088,inset 0 0 0 1px #a58e62aa,inset 0 1px 0 #f0d39a40}
.cw-cta.is-tour:focus-visible{outline-color:#f0d39a}
.cw-cta.is-tour .cw-cta-icon{width:46px;height:46px;background:radial-gradient(circle at 50% 40%,#f0d39a33,#0b142a00 70%);box-shadow:inset 0 0 0 1px #efd19366}
.cw-cta.is-tour .cw-cta-icon img{display:block;width:34px;height:34px;object-fit:contain;filter:drop-shadow(0 0 8px #efd19366)}
.cw-cta.is-tour .cw-cta-body{flex:1 1 auto;text-align:left}
.cw-cta.is-tour .cw-cta-title{margin-top:3px;color:#fff4dc;word-break:keep-all}
.cw-cta.is-tour .cw-cta-sub{margin-top:4px;white-space:normal;word-break:keep-all;color:#c1cddd;opacity:1;line-height:1.45}
.cwt-lock-eyebrow{display:flex;align-items:center;gap:6px;font-size:10.5px;font-weight:700;letter-spacing:.08em;color:#f0d39a}
.cwt-lock-eyebrow i{width:6px;height:6px;border-radius:50%;background:#ff6b81}
.cwt-lock-go{flex:0 0 auto;align-self:center;padding:7px 10px;border-radius:9px;border:1px solid #f2d99c;
 background:linear-gradient(120deg,#f4dfb2,#d9b975);color:#302a23;font-size:12px;font-weight:800;white-space:nowrap}
.cwt-lock-modal{padding:0;overflow:hidden;background:#fff}
.cwt-lock-modal-head{display:flex;align-items:center;gap:12px;padding:16px 18px;color:#fff4dc;
 background:linear-gradient(90deg,#0b142af2,#152039d9),url('/cherish/tour/midnight/arena.png') center 47%/cover,#0e1830}
.cwt-lock-modal-head img{width:40px;height:40px;object-fit:contain;filter:drop-shadow(0 0 8px #efd19366)}
.cwt-lock-modal-head small{display:block;font-size:11px;letter-spacing:.06em;color:#f0d39a}
.cwt-lock-modal-head b{display:block;margin-top:2px;font-size:16px;word-break:keep-all}
.cwt-lock-modal-body{padding:16px 18px 14px}
.cwt-lock-modal-body p{margin:0;font-size:14px;line-height:1.6;color:var(--cw-ink);white-space:pre-line;word-break:keep-all}
.cwt-lock-modal .cw-intro-go{background:linear-gradient(120deg,#f4dfb2,#d9b975);color:#302a23;border:1px solid #f2d99c;box-shadow:0 6px 14px -6px #8a6d3088}
`;

const CWT_STRIP_CSS = `
/* 해설은 cw-bbar 밖에 둔다. 긴 문장도 버튼 줄 너비를 늘리지 않는다. */
.cwt-live-commentary{box-sizing:border-box;display:grid;grid-template-columns:38px minmax(0,1fr);gap:10px;
 width:100%;min-width:0;margin:4px 0 10px;padding:12px 14px;border-radius:12px;
 background:linear-gradient(120deg,#fff7fa,#f5f8ff);border:1px solid #e6d8df;color:#39455e;text-align:left}
.cwt-live-commentary>img{width:38px;height:38px;object-fit:cover;border-radius:50%}
.cwt-live-commentary>div{min-width:0}
.cwt-live-commentary header{display:flex;align-items:center;gap:8px;margin-bottom:5px}
.cwt-live-commentary header b{font-size:13px;color:#a84464}
.cwt-live-commentary header span{font-size:11px;color:#768298}
.cwt-live-commentary p{font-size:14px;line-height:1.75;margin:0;white-space:normal;overflow-wrap:anywhere;
 max-height:8.75em;overflow-y:auto;animation:cwt-say-in .35s ease-out both}
html.cw-bshell .cw-bbar{min-width:0;flex-wrap:wrap}
html.cw-bshell .cw-bbar>.cwt-strip-chip{max-width:100%;white-space:normal}
@media(max-width:480px){.cwt-live-commentary{padding:10px;grid-template-columns:30px minmax(0,1fr);gap:8px}
 .cwt-live-commentary>img{width:30px;height:30px}.cwt-live-commentary p{font-size:13px}}
@media(prefers-reduced-motion:reduce){.cwt-live-commentary p{animation:none}}
@keyframes cwt-say-in{from{opacity:0;transform:translateY(-3px)}to{opacity:1;transform:none}}

.cwt-strip-btn{white-space:nowrap}
.cwt-strip-chip{white-space:nowrap;font-size:12px}
`;


/*
 * CSS 는 컴포넌트가 직접 들고 있다 (약점 가이드와 같은 방식) — 스킨 파일을 건드리지 않으므로
 * 이 패치만 되돌리면 흔적이 남지 않는다.
 *
 * 기본 스타일 뒤에 Midnight 테마를 한정 적용한다. 홈 배너/전투 해설 띠와는 분리한다.
 * 애니메이션은 transform/opacity 만 만진다 — box-shadow 키프레임은 저사양에서 매 프레임 리페인트다.
 */
const CWT_CSS = `
.cwt-root{--cwt-ink:#5c4a42;--cwt-paper:#fdf7ee;--cwt-line:#c8a89a;--cwt-accent:#ce6e7a;
 padding:12px 14px 24px;color:var(--cwt-ink);max-width:1180px;margin:0 auto}
.cwt-root *{box-sizing:border-box}
.cwt-head{display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin:2px 0 12px}
.cwt-head h3{margin:0;font-size:21px}
.cwt-badges{display:flex;gap:6px;flex-wrap:wrap}
.cwt-badge{font-style:normal;font-size:12px;padding:2px 8px;border-radius:999px;
 background:var(--cwt-paper);border:1px solid var(--cwt-line);color:var(--cwt-ink)}
.cwt-badge.is-live{background:var(--cwt-accent);border-color:transparent;color:#fff}
.cwt-badge.is-rest{border-style:dashed}
.cwt-note{font-size:13px;color:var(--cwt-ink);opacity:.75;margin:4px 0}
.cwt-note.is-dim{opacity:.5}
.cwt-warn{font-size:13px;margin-top:8px;padding:8px 10px;border-radius:8px;
 background:var(--cwt-paper);border:1px dashed var(--cwt-accent)}
.cwt-idle{padding:24px 0}
.cwt-btn{font:inherit;min-height:40px;padding:0 16px;border-radius:9px;cursor:pointer;
 border:1px solid transparent;background:var(--cwt-accent);color:#fff}
.cwt-btn:disabled{opacity:.5;cursor:default}
.cwt-btn.is-ghost{background:transparent;border-color:var(--cwt-line);color:var(--cwt-ink)}
.cwt-check{display:flex;align-items:center;gap:7px;margin:8px 0;font-size:14px;cursor:pointer}
.cwt-signup{padding:10px 12px;border-radius:10px;background:var(--cwt-paper);
 border:1px solid var(--cwt-line);margin-bottom:12px}
.cwt-signed{margin:0 0 4px}
.cwt-guide{padding:10px 12px;border-radius:10px;border:1px solid var(--cwt-line)}
.cwt-guide h4{margin:0 0 6px;font-size:15px}
.cwt-guide ol{margin:0;padding-left:20px;font-size:13px;line-height:1.7}

/* 라인업 */
.cwt-lineup{padding:10px 12px;border-radius:10px;background:var(--cwt-paper);
 border:1px solid var(--cwt-line);margin-bottom:12px}
.cwt-lineup h4{margin:0 0 4px;font-size:15px}
.cwt-slots{display:flex;gap:6px;flex-wrap:wrap;margin:8px 0}
/* 칸 하나 = 그림 + 이름. 숫자만 있던 시절의 52px 정사각을 이름 줄만큼 키웠다 (2026-09-19). */
.cwt-slot{font:inherit;position:relative;width:74px;padding:4px 3px 5px;border-radius:9px;cursor:pointer;
 display:flex;flex-direction:column;align-items:center;gap:2px;
 border:1px solid var(--cwt-line);background:#fff;color:var(--cwt-ink)}
.cwt-slot.is-on{border-color:var(--cwt-accent);border-width:2px;padding:3px 2px 4px}
.cwt-slot-img{display:block;width:48px;height:48px;object-fit:contain;image-rendering:auto}
.cwt-slot-name{display:block;max-width:68px;font-size:11px;line-height:1.25;
 overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-slot em,.cwt-slot-order{position:absolute;right:3px;top:2px;font-style:normal;font-size:11px;
 color:var(--cwt-accent);font-weight:700}

/* 카드 — 「종이와 도장」. 그림이 없어도 CSS 로 종이 카드가 그려진다. */
.cwt-card{display:flex;align-items:center;gap:7px;padding:6px 8px;min-width:0;
 border-radius:8px;background:var(--cwt-paper);border:1px solid var(--cwt-line);
 position:relative;transition:transform .18s,opacity .18s}
.cwt-card.is-empty{justify-content:center;opacity:.45;border-style:dashed;background:transparent}
.cwt-card.is-mine{border-style:dashed;border-width:2px;border-color:var(--cwt-accent)}
.cwt-card.is-won{border-color:var(--cwt-accent)}
.cwt-card.is-lost{opacity:.55;transform:rotate(-2deg)}
.cwt-ava{width:26px;height:26px;border-radius:50%;overflow:hidden;flex:0 0 auto;
 background:#e8ddd3;display:block}
.cwt-ava img{width:100%;height:100%;object-fit:cover;display:block}
.cwt-ava-blank{display:block;width:100%;height:100%}
.cwt-card.is-anon .cwt-ava{background:var(--cwt-line)}
.cwt-name{font-size:13px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-stamps{font-size:9px;letter-spacing:1px;color:var(--cwt-accent);margin-left:auto;flex:0 0 auto}
.cwt-used{display:flex;gap:2px;flex:0 0 auto}
.cwt-used-dot{width:6px;height:6px;border-radius:50%;background:var(--cwt-line);display:block}
.cwt-bye{font-size:12px}
/* 승자 도장 — 자산이 올라오면 background-image 가 얹히고, 없으면 원형 잉크로 보인다 */
.cwt-stamp-win{position:absolute;right:4px;top:-6px;width:22px;height:22px;border-radius:50%;
 background:var(--cwt-accent);opacity:.75;
 background-image:url('/cherish/tour/stamp-win.png');background-size:contain;
 background-repeat:no-repeat;background-position:center}

/* 한 칸 */
.cwt-match{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:6px}
.cwt-mid{display:flex;flex-direction:column;align-items:center;gap:3px;min-width:56px}
.cwt-vs{width:18px;height:18px;opacity:.5;background:var(--cwt-line);
 background-image:url('/cherish/tour/mark-vs.png');background-size:contain;
 background-repeat:no-repeat;background-position:center;
 -webkit-mask:url('/cherish/tour/mark-vs.png') center/contain no-repeat;
 mask:url('/cherish/tour/mark-vs.png') center/contain no-repeat}
.cwt-pips{display:flex;align-items:center;gap:2px;font-size:10px;opacity:.8}
.cwt-pips b{opacity:.4;margin:0 2px}
.cwt-pip{width:7px;height:7px;border-radius:50%;background:var(--cwt-line);display:block}
.cwt-pip.is-on{background:var(--cwt-accent)}
.cwt-sets{font-size:11px;opacity:.75;letter-spacing:.5px}
.cwt-last{font-size:11px;font-weight:700;color:var(--cwt-accent);white-space:nowrap}
.cwt-match.is-last .cwt-card{border-color:var(--cwt-accent)}
.cwt-strip-chip.is-last{font-weight:700}
.cwt-watch{font:inherit;font-size:12px;min-height:26px;padding:0 9px;border-radius:7px;cursor:pointer;
 border:1px solid var(--cwt-accent);background:#fff;color:var(--cwt-accent)}
.cwt-calling{font-size:11px;color:var(--cwt-accent);white-space:nowrap}
.cwt-wait{font-size:11px;opacity:.55;white-space:nowrap}
.cwt-match.is-live .cwt-vs{opacity:1;animation:cwt-pulse 1.6s ease-in-out infinite}
@keyframes cwt-pulse{0%,100%{transform:scale(1);opacity:.7}50%{transform:scale(1.12);opacity:1}}

/* PC 세로 트리 */
/* 대진표는 아래에서 위로 올라간다 (2026-09-19). column-reverse 라 DOM 은 1라운드부터 그대로다. */
.cwt-tree{display:flex;flex-direction:column-reverse;gap:12px;overflow-x:auto;padding-bottom:8px}
.cwt-row{display:flex;flex-direction:column;gap:5px;min-width:100%}
.cwt-row-h{margin:0;font-size:13px;opacity:.7;text-align:center}
.cwt-row-grid{display:grid;gap:8px;align-items:start}
.cwt-cell{display:flex;align-items:center;padding:0;min-width:0}
.cwt-cell>.cwt-match{width:100%}

/* 모바일 라운드 피드 */
.cwt-chips{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px}
.cwt-chip{font:inherit;font-size:13px;min-height:34px;padding:0 12px;border-radius:999px;cursor:pointer;
 border:1px solid var(--cwt-line);background:transparent;color:var(--cwt-ink)}
.cwt-chip.is-on{background:var(--cwt-accent);border-color:transparent;color:#fff}
.cwt-feed-list{display:flex;flex-direction:column;gap:8px}
.cwt-feed .cwt-match{padding:8px;border-radius:10px;border:1px solid var(--cwt-line)}
.cwt-feed .cwt-match.is-mine{border-color:var(--cwt-accent);border-width:2px}

/* 공개 연출 — 애니메이션은 transform/opacity 만 만진다 */
.cwt-reveal{display:flex;flex-direction:column;align-items:center;justify-content:center;
 gap:6px;padding:18px 0 22px;min-height:132px}
.cwt-reveal-title{font-size:26px;font-weight:700;animation:cwt-in .6s ease-out both}
.cwt-reveal-go{font-size:22px;font-weight:700;color:var(--cwt-accent);
 animation:cwt-beat .9s ease-in-out infinite}
.cwt-reveal-card{display:flex;flex-direction:column;align-items:center;gap:7px;
 padding:14px 22px;border-radius:14px;background:var(--cwt-paper);
 border:1px solid var(--cwt-line);animation:cwt-pop .42s cubic-bezier(.2,1.5,.4,1) both}
.cwt-reveal-card .cwt-ava{width:56px;height:56px}
.cwt-reveal-card>b{font-size:18px}
@keyframes cwt-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@keyframes cwt-pop{from{opacity:0;transform:scale(.72)}to{opacity:1;transform:scale(1)}}
@keyframes cwt-beat{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}

/* 아직 등장하지 않은 자리 — 빈 종이 */
.cwt-card.is-hidden{min-height:38px;background:var(--cwt-paper);opacity:.5;
 border:1px dashed var(--cwt-line)}
/* 방금 자리에 꽂힌 카드 */
.cwt-card.is-new{animation:cwt-drop .5s cubic-bezier(.2,1.4,.4,1) both}
@keyframes cwt-drop{from{opacity:0;transform:translateY(-10px) scale(.94)}to{opacity:1;transform:none}}
/* 도장이 찍히는 순간 */
.cwt-card.is-won .cwt-stamp-win{animation:cwt-stamp .34s cubic-bezier(.2,1.6,.4,1) both}
@keyframes cwt-stamp{from{opacity:0;transform:scale(1.9) rotate(-14deg)}to{opacity:.75;transform:none}}

/* 색종이 — 절차 생성 */
.cwt-champ{position:relative;overflow:hidden}
/* 명장면 */
.cwt-hl{margin:4px 0 14px;padding:10px 12px;border-radius:10px;
 background:var(--cwt-paper);border:1px solid var(--cwt-line)}
.cwt-hl h4{margin:0 0 6px;font-size:15px}
.cwt-hl-list{margin:0;padding-left:18px;display:flex;flex-direction:column;gap:4px}
.cwt-hl-list li{font-size:13px}
.cwt-hl-turn{color:var(--cwt-accent);margin-right:6px}
.cwt-hl-hp{font-style:normal;opacity:.6;font-size:12px;margin-left:6px}

/* 해설 띠 */
.cwt-talk{margin-top:14px;display:flex;flex-direction:column;gap:6px}
.cwt-say{display:grid;grid-template-columns:28px auto 1fr;align-items:center;gap:8px;
 padding:7px 10px;border-radius:10px;background:var(--cwt-paper);border:1px solid var(--cwt-line)}
.cwt-say-face{width:28px;height:28px;border-radius:50%;object-fit:cover;display:block}
.cwt-say-name{font-size:12px;opacity:.7;white-space:nowrap}
.cwt-say-text{font-size:14px;line-height:1.5}
.cwt-say.is-fresh{animation:cwt-in .35s ease-out both}
.cwt-say.is-berry{border-style:dashed}
.cwt-confetti{position:absolute;inset:0;pointer-events:none}
.cwt-bit{position:absolute;top:-12px;width:7px;height:11px;border-radius:2px;
 background:var(--cwt-accent);opacity:.85;
 animation-name:cwt-fall;animation-timing-function:linear;animation-iteration-count:1;
 animation-fill-mode:both}
.cwt-bit:nth-child(3n){background:var(--cwt-line)}
.cwt-bit:nth-child(3n+1){background:var(--cwt-ink);opacity:.35}
@keyframes cwt-fall{from{transform:translateY(-14px) rotate(0)}
 to{transform:translateY(190px) rotate(var(--cwt-rot,180deg));opacity:0}}

/* 우승 */
.cwt-champ{margin:4px 0 14px}
.cwt-champ h4{margin:0 0 6px;font-size:15px}
.cwt-champ-card{display:flex;align-items:center;gap:9px;padding:10px 12px;border-radius:10px;
 background:var(--cwt-paper);border:1px solid var(--cwt-accent)}
.cwt-champ-card .cwt-ava{width:38px;height:38px}

/* Midnight Arena — 모든 장식은 패널 안에만 적용. 전투 화면/홈 배너에는 영향이 없다. */
.cwt-root.cwt-midnight{--cwt-ink:#edf1fc;--cwt-paper:#182338;--cwt-line:#58647d;--cwt-accent:#e9c987;
 position:relative;isolation:isolate;max-width:1440px;min-height:650px;padding:28px 28px 32px;
 background:#0b1425;border-radius:18px;overflow:hidden;color:var(--cwt-ink)}
.cwt-midnight .cwt-arena{position:absolute;z-index:-1;inset:0;pointer-events:none;
 background:linear-gradient(180deg,rgba(8,16,32,.23),rgba(8,16,32,.78) 55%,#0b1425 88%),
 url('/cherish/tour/midnight/arena.png') center top/cover no-repeat;opacity:.85}
.cwt-midnight .cwt-head{align-items:center;justify-content:space-between;padding:0 0 18px;
 margin-bottom:16px;border-bottom:1px solid rgba(233,201,135,.25)}
.cwt-midnight .cwt-head h3{font-size:24px;letter-spacing:-.7px;color:#fff7df;text-shadow:0 2px 15px #000}
.cwt-midnight .cwt-badge{padding:5px 10px;font-size:11px;background:#19263a;border-color:#5e6373}
.cwt-midnight .cwt-badge.is-live{color:#f4d99e;border-color:#b69b64;background:#302c28}
.cwt-midnight .cwt-note{opacity:.9}
.cwt-midnight .cwt-note.is-dim{opacity:.7}
.cwt-midnight .cwt-btn,.cwt-midnight .cwt-chip.is-on{color:#172033;font-weight:700}
.cwt-midnight .cwt-btn.is-ghost{color:var(--cwt-ink)}
.cwt-midnight .cwt-signup,.cwt-midnight .cwt-lineup,.cwt-midnight .cwt-guide,.cwt-midnight .cwt-hl{
 background:rgba(19,30,48,.95);border-color:#49536a;padding:18px;border-radius:14px}
.cwt-midnight .cwt-guide{margin-top:18px;padding:24px}
.cwt-midnight .cwt-guide h4{color:#f3dfb5;margin-bottom:16px}
.cwt-midnight .cwt-guide ol{display:grid;grid-template-columns:1fr 1fr;gap:16px 32px;padding-left:22px;line-height:1.8}
.cwt-midnight .cwt-guide li::marker{color:#e9c987;font-weight:bold}
.cwt-midnight .cwt-lobby{display:grid;grid-template-columns:1.1fr 1fr;padding:0;overflow:hidden;border-color:#96805b}
.cwt-midnight .cwt-lobby-intro{position:relative;min-width:0;padding:36px 30px 150px;
 background:radial-gradient(ellipse at 75% 100%,#b49a6033,transparent 70%),linear-gradient(130deg,#26334b,#121e30)}
.cwt-midnight .cwt-lobby-intro h4{position:relative;z-index:1;font-size:28px;line-height:1.4;color:#fff0cd;margin:20px 0 12px;word-break:keep-all}
.cwt-midnight .cwt-lobby-intro p{color:#c2cce0;font-size:14px;line-height:1.7}
.cwt-midnight .cwt-lobby-time{position:relative;z-index:1;display:flex;flex-direction:column;gap:8px;margin-top:28px}
.cwt-midnight .cwt-lobby-time>span{font-size:11px;color:#b8c5da}
.cwt-midnight .cwt-lobby-time time,.cwt-midnight .cwt-lobby-time b{font-size:17px;color:#efdaa9}
.cwt-midnight .cwt-lobby-trophy{position:absolute;bottom:10px;right:24px;width:140px;height:140px;object-fit:contain;filter:drop-shadow(0 0 22px #c7a56c33)}
.cwt-midnight .cwt-lobby-entry{padding:36px 30px;align-self:center;min-width:0}
.cwt-midnight .cwt-entry-status{display:inline-block;padding:7px 12px;border:1px solid #867655;border-radius:30px;font-size:11px;color:#e9d3a1;margin-bottom:20px}
.cwt-midnight .is-registered .cwt-entry-status{background:#e9c987;color:#172033}
.cwt-midnight .cwt-lobby .cwt-note{line-height:1.9;font-size:14px;margin:0 0 24px}
.cwt-midnight .cwt-lobby .cwt-check{display:flex;align-items:center;gap:12px;padding:16px;background:#202e43;border:1px solid #4b5970;border-radius:10px;margin-bottom:20px;cursor:pointer}
.cwt-midnight .cwt-lobby .cwt-check input{width:18px;height:18px;flex-shrink:0;accent-color:#e9c987}
.cwt-midnight .cwt-lobby .cwt-check small{display:block;font-size:11px;color:#b9c6dc;margin-top:6px}
.cwt-midnight .cwt-lobby .cwt-btn{min-height:46px;width:100%;font-size:14px}
.cwt-midnight .cwt-lobby .cwt-btn:disabled{opacity:.5;cursor:wait}
.cwt-midnight .cwt-lobby .cwt-signed{margin:0 0 20px;overflow-wrap:anywhere}
.cwt-midnight .cwt-signed b{display:block;font-size:23px;color:#fff0cd}
.cwt-midnight .cwt-signed span{display:block;font-size:12px;color:#bcc9de;margin-top:8px}
.cwt-midnight.is-narrow .cwt-lobby{grid-template-columns:1fr}
.cwt-midnight.is-narrow .cwt-lobby-intro{padding:24px 20px 28px}
.cwt-midnight.is-narrow .cwt-lobby-intro h4{font-size:23px;max-width:220px}
.cwt-midnight.is-narrow .cwt-lobby-time{padding-right:58px}
.cwt-midnight.is-narrow .cwt-lobby-trophy{width:78px;height:78px;right:8px;bottom:18px}
.cwt-midnight.is-narrow .cwt-lobby-entry{padding:24px 20px}
.cwt-midnight.is-narrow .cwt-guide{padding:20px}
.cwt-midnight.is-narrow .cwt-guide ol{grid-template-columns:1fr;gap:12px}
.cwt-midnight .cwt-lineup-head{display:flex;justify-content:space-between;align-items:center;gap:16px}
.cwt-midnight .cwt-lineup-head h4{font-size:21px;color:#fff0cd;margin:10px 0 14px}
.cwt-midnight .cwt-selection-count{color:#f1d69d;font-size:28px;font-variant-numeric:tabular-nums;white-space:nowrap}
.cwt-midnight .cwt-selection-count small{font-size:14px;color:#b2c0d5}
.cwt-midnight .cwt-lineup .cwt-note{font-size:13px;line-height:1.8;color:#c0cde0}
.cwt-midnight .cwt-slots{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:12px;margin:22px 0}
.cwt-midnight .cwt-slot{background:#202e45;width:100%;min-width:0;padding:20px 8px 12px;border:2px solid #4b5a70;border-radius:12px;transition:background .2s,border-color .2s}
.cwt-midnight .cwt-slot.is-on{padding:20px 8px 12px;border-color:#e9c987;box-shadow:inset 0 0 20px #e9c98712}
.cwt-midnight .cwt-slot:disabled:not(.is-on){opacity:.55;cursor:default}
.cwt-midnight .cwt-slot-img{width:100%;height:86px;margin:0 auto 10px}
.cwt-midnight .cwt-slot-name{max-width:none;font-size:13px;line-height:1.5;white-space:normal;overflow-wrap:anywhere;color:#f1e8d6}
.cwt-midnight .cwt-slot .cwt-slot-order{top:7px;right:7px;border-radius:50%;width:22px;height:22px;display:grid;place-items:center;background:#e9c987;color:#142034;font-weight:bold}
.cwt-midnight .cwt-lineup-footer{display:flex;align-items:center;justify-content:space-between;gap:18px;border-top:1px solid #43516a;padding-top:18px}
.cwt-midnight .cwt-lineup-footer b{display:block;font-size:13px;color:#efdcba}
.cwt-midnight .cwt-lineup-footer span{display:block;font-size:11px;color:#bdc9dc;margin-top:6px}
.cwt-midnight .cwt-lineup-footer .cwt-btn{min-height:44px;min-width:170px}
.cwt-midnight .cwt-lineup-footer .cwt-btn:disabled{opacity:.55;cursor:default}
.cwt-midnight .cwt-my-match{display:flex;align-items:center;justify-content:space-between;gap:20px;padding:22px 24px;margin:18px 0;border:1px solid #d1b477;border-radius:14px;background:linear-gradient(110deg,#394034,#17273d)}
.cwt-midnight .cwt-my-match h4{font-size:19px;color:#fff0cd;margin:10px 0}
.cwt-midnight .cwt-my-match p{font-size:13px;line-height:1.7;color:#cbd5e5;margin:0}
.cwt-midnight .cwt-my-match .cwt-btn{min-height:44px;flex-shrink:0}
.cwt-midnight.is-narrow .cwt-slots{grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}
.cwt-midnight.is-narrow .cwt-slot-img{height:65px}
.cwt-midnight.is-narrow .cwt-slot-name{font-size:12px}
.cwt-midnight.is-narrow .cwt-lineup-head h4{font-size:18px}
.cwt-midnight.is-narrow .cwt-lineup-footer,.cwt-midnight.is-narrow .cwt-my-match{flex-direction:column;align-items:stretch}
.cwt-midnight.is-narrow .cwt-my-match{padding:20px}
.cwt-midnight .cwt-slot.is-on{background:#34362f}
.cwt-midnight .cwt-mine-label{flex-shrink:0;display:inline-block;font-size:10px;line-height:18px;padding:0 5px;border-radius:4px;background:#e9c987;color:#142034;font-weight:bold}
.cwt-midnight .cwt-follow-round{font:inherit;font-size:12px;min-height:40px;margin:0 0 12px;padding:0 14px;border:1px solid #8e7c59;border-radius:8px;background:#20314a;color:#efd59e;cursor:pointer}
.cwt-midnight .cwt-chips{gap:8px}
.cwt-midnight .cwt-chip{min-height:42px}
.cwt-midnight .cwt-hl{margin:20px 0 28px;padding:26px}
.cwt-midnight .cwt-hl h4{font-size:21px;color:#fff0cd;margin:10px 0 22px}
.cwt-midnight .cwt-hl-list{list-style:none;padding:0;gap:0}
.cwt-midnight .cwt-hl-list li{position:relative;display:flex;flex-wrap:wrap;align-items:center;gap:10px;margin-left:7px;padding:8px 0 22px 23px;border-left:1px solid #8c7753;line-height:1.7}
.cwt-midnight .cwt-hl-list li:before{content:'';position:absolute;left:-4px;top:17px;width:7px;height:7px;background:#e9c987;border-radius:50%}
.cwt-midnight .cwt-hl-list li:last-child{padding-bottom:8px}
.cwt-midnight .cwt-hl-turn{border:1px solid #645e51;border-radius:5px;padding:2px 8px;color:#f0d394;font-size:12px}
.cwt-midnight .cwt-hl-what{color:#dbe5f5}
.cwt-midnight .cwt-hl-hp{opacity:1;color:#b9c7dc;font-size:11px}
.cwt-midnight .cwt-status-panel{text-align:center;padding:54px 24px;margin:24px 0;border:1px solid #655d4e;border-radius:16px;background:radial-gradient(ellipse at 50% 0,#b5985a22,transparent 75%),#121e30ed}
.cwt-midnight .cwt-status-panel>img{width:110px;height:110px;object-fit:contain;margin-bottom:16px}
.cwt-midnight .cwt-status-panel h3{font-size:23px;line-height:1.5;color:#f6e6c1;word-break:keep-all}
.cwt-midnight .cwt-status-panel p{font-size:13px;line-height:1.8;color:#c4cfe0}
.cwt-midnight .cwt-connection-note{padding:14px 18px;border:1px solid #917955;border-radius:10px;background:#342f27;color:#f2d8af;font-size:13px;line-height:1.7}
.cwt-midnight.is-narrow .cwt-hl{padding:20px 16px}
.cwt-midnight .cwt-prize{display:flex;flex-direction:column;align-items:center;margin:0 auto 18px;gap:3px}
.cwt-midnight .cwt-prize img{width:108px;height:108px;object-fit:contain;filter:drop-shadow(0 4px 20px #c49d4633)}
.cwt-midnight .cwt-prize span{font-size:10px;font-weight:700;letter-spacing:3px;color:#edcf94}
.cwt-midnight .cwt-podium{display:flex;justify-content:center;margin:12px auto 26px;pointer-events:none}
.cwt-midnight .cwt-podium .cwt-prize{align-self:center;margin:0}
.cwt-midnight.is-narrow .cwt-podium{gap:4px;margin:12px auto 20px}
.cwt-midnight.is-narrow .cwt-podium .cwt-prize img{width:72px;height:72px}
.cwt-midnight.is-narrow .cwt-podium .cwt-prize span{font-size:8px;letter-spacing:1.5px}
.cwt-midnight .cwt-tree{gap:25px;padding:4px 0 20px;scrollbar-color:#6d7180 #132038}
.cwt-midnight .cwt-row{gap:12px}
.cwt-midnight .cwt-row-h{opacity:1;color:#ddd4bf;font-size:12px;letter-spacing:2px}
.cwt-midnight .cwt-row-grid{gap:16px}
.cwt-midnight .cwt-cell{justify-content:center;position:relative}
.cwt-midnight .cwt-row:not(:first-child) .cwt-cell:before{content:'';position:absolute;
 top:100%;left:50%;height:14px;border-left:1px solid #a28a5e88;pointer-events:none}
.cwt-midnight .cwt-row:not(:first-child) .cwt-cell:after{content:'';position:absolute;
 top:calc(100% + 14px);left:calc(25% - 4px);right:calc(25% - 4px);height:37px;
 border:1px solid #a28a5e88;border-bottom:0;border-radius:5px 5px 0 0;pointer-events:none}
.cwt-midnight .cwt-tree .cwt-match{display:grid;grid-template-columns:minmax(0,1fr) auto;
 grid-template-rows:1fr 1fr;gap:6px 8px;padding:9px;max-width:340px;border:1px solid #5a6274;
 border-radius:12px;background:linear-gradient(135deg,#253249f7,#121d30f7);box-shadow:0 8px 20px #02071655}
.cwt-midnight .cwt-tree .cwt-card{grid-column:1;min-height:42px}
.cwt-midnight .cwt-tree .cwt-mid{grid-column:2;grid-row:1/span 2;min-width:44px}
.cwt-midnight .cwt-match.is-live,.cwt-midnight .cwt-match.is-calling{border-color:#d8b775;
 box-shadow:0 0 0 1px #d8b77522,0 6px 24px #d8b7750f}
.cwt-midnight .cwt-card{background:#172236;border-color:#48566c;border-radius:7px;min-height:42px}
.cwt-midnight .cwt-card.is-won{background:#36352e;border-color:#caaa69}
.cwt-midnight .cwt-card.is-lost{transform:none;opacity:.55}
.cwt-midnight .cwt-card.is-empty{background:transparent;opacity:.7}
.cwt-midnight .cwt-stamp-win{display:none}
.cwt-midnight .cwt-card.is-won:after{content:'승';color:#f3d99e;font-size:10px;margin-left:auto;flex:none}
.cwt-midnight .cwt-vs{background:none;mask:none;-webkit-mask:none;width:auto;height:auto;opacity:.8}
.cwt-midnight .cwt-vs:after{content:'VS';font-size:9px;letter-spacing:1px;color:#d5bb8c}
.cwt-midnight .cwt-watch{background:#e9c987;color:#142034;border-color:#e9c987;min-height:36px;font-weight:700}
.cwt-midnight .cwt-wait{opacity:.85}
.cwt-midnight .cwt-champ{text-align:center;max-width:380px;margin:0 auto 22px}
.cwt-midnight .cwt-champ-card{justify-content:center;background:#292d32;padding:16px}
.cwt-midnight .cwt-reveal{position:relative;min-height:218px;margin:8px 0 20px;padding:24px 16px;
 border:1px solid #bfa26b66;border-radius:16px;overflow:hidden;
 background:radial-gradient(ellipse at 50% 100%,#b28d3933,transparent 65%),linear-gradient(120deg,#142036ed,#0c1627ed)}
.cwt-midnight .cwt-reveal:before,.cwt-midnight .cwt-reveal:after{content:'';position:absolute;pointer-events:none;
 top:0;bottom:0;width:1px;background:linear-gradient(transparent,#edcf9488,transparent);transform:rotate(25deg)}
.cwt-midnight .cwt-reveal:before{left:18%}.cwt-midnight .cwt-reveal:after{right:18%;transform:rotate(-25deg)}
.cwt-midnight .cwt-reveal-title{position:relative;font-size:28px;text-align:center;letter-spacing:-.5px;color:#fff1cf}
.cwt-midnight .cwt-reveal-kicker{font-size:10px;letter-spacing:2px;color:#d9bf8d}
.cwt-midnight .cwt-reveal-card{min-width:240px;max-width:100%;padding:18px 28px;gap:12px;
 background:linear-gradient(130deg,#2b3549,#152033);border-color:#c4a66c;box-shadow:0 12px 32px #0005;
 animation:cwt-entrance .65s cubic-bezier(.15,.8,.25,1) both}
.cwt-midnight .cwt-reveal-visual{display:flex;align-items:flex-end;justify-content:center;gap:12px;height:176px;max-width:100%}
.cwt-midnight .cwt-reveal-trainer{display:flex;align-items:flex-end;justify-content:center;min-width:128px;height:100%}
.cwt-midnight .cwt-reveal-body{height:100%;max-width:180px;object-fit:contain;object-position:bottom;
 filter:drop-shadow(0 8px 12px #0009)}
.cwt-midnight .cwt-reveal-face{width:110px;height:110px;border-radius:50%;object-fit:cover;align-self:center}
.cwt-midnight .cwt-reveal-silhouette{display:flex;align-items:center;justify-content:center;width:110px;height:90%;
 border-radius:54px 54px 12px 12px;background:linear-gradient(#3a4661,#1b2438);color:#9aabc5;font-size:40px}
.cwt-midnight .cwt-reveal-partner{display:flex;flex-direction:column;align-items:center;justify-content:flex-end;min-width:110px;max-width:145px;height:100%}
.cwt-midnight .cwt-reveal-partner span{font-size:9px;letter-spacing:2px;color:#e9c987}
.cwt-midnight .cwt-reveal-partner img{display:block;width:120px;height:120px;max-width:100%;object-fit:contain;
 filter:drop-shadow(0 8px 10px #0008)}
.cwt-midnight .cwt-reveal-partner b{font-size:12px;line-height:1.25;color:#dce6f5}
.cwt-midnight .cwt-reveal-card>b{font-size:20px;text-align:center;overflow-wrap:anywhere}
.cwt-midnight .cwt-reveal-go{font-size:34px;color:#f4d694;text-align:center;
 animation:cwt-entrance .45s ease-out both}
.cwt-midnight .cwt-reveal-progress{height:3px;width:160px;border-radius:3px;background:#536079;margin-top:12px;overflow:hidden}
.cwt-midnight .cwt-reveal-progress i{display:block;width:100%;height:100%;background:#edcf94;transform-origin:left;transition:transform .4s ease}
.cwt-midnight .cwt-award{position:relative;isolation:isolate;overflow:hidden;margin:8px 0 24px;padding:28px 16px 14px;
 border:1px solid #c4a66c99;border-radius:18px;background:radial-gradient(ellipse at 50% 40%,#d5aa4433,transparent 65%),#111d30e8}
.cwt-midnight .cwt-award:before{content:'';position:absolute;inset:12px;border:1px solid #c4a66c33;border-radius:12px;pointer-events:none;z-index:-1}
.cwt-midnight .cwt-award .cwt-prize img{width:156px;height:156px}
.cwt-midnight .cwt-award .cwt-champ h4{font-size:12px;letter-spacing:2px;color:#e5c488;margin:0 0 12px}
.cwt-midnight .cwt-award .cwt-champ-card{border:0;background:transparent;padding:2px 0;gap:12px}
.cwt-midnight .cwt-award .cwt-champ-card b{font-size:24px;line-height:1.4;overflow-wrap:anywhere;min-width:0;color:#fff3d6}
.cwt-midnight .cwt-award .cwt-ava{border:1px solid #d9b878;width:44px;height:44px}
.cwt-midnight .cwt-award-caption{font-size:12px;color:#b9c5d9;margin:14px 0 0}
.cwt-midnight .cwt-award-replay{margin-top:12px;font-size:12px;padding:6px 14px}

/* 우승 팝업 (09-24) — 전신 아바타를 무대 위에 세운다 */
.cwt-pop{position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px}
.cwt-pop-backdrop{position:absolute;inset:0;background:radial-gradient(ellipse at 50% 40%,rgba(40,32,18,.72),rgba(4,8,18,.92));
 animation:cwt-pop-fade .4s ease-out both}
.cwt-pop-card{position:relative;isolation:isolate;overflow:hidden;width:min(440px,100%);max-height:calc(100vh - 32px);
 padding:26px 22px 22px;border-radius:20px;text-align:center;color:#fff3d6;
 background:linear-gradient(180deg,#26324a 0%,#141d31 62%,#0d1426 100%);
 border:1px solid #d9b878;box-shadow:0 0 0 6px #d9b87814,0 24px 70px #000c,0 0 90px #e9c98733;
 animation:cwt-pop-rise .7s cubic-bezier(.15,.8,.3,1) both}
.cwt-pop-card:before{content:'';position:absolute;inset:9px;border:1px solid #d9b87833;border-radius:14px;pointer-events:none}
.cwt-pop-rays{position:absolute;z-index:-1;left:50%;top:38%;width:720px;height:720px;margin:-360px 0 0 -360px;opacity:.28;
 background:repeating-conic-gradient(from 0deg,#f7dca0 0 7deg,transparent 7deg 20deg);
 -webkit-mask:radial-gradient(circle,#000 8%,transparent 58%);mask:radial-gradient(circle,#000 8%,transparent 58%);
 animation:cwt-pop-spin 36s linear infinite}
.cwt-pop-eyebrow{margin:0;font-size:11px;letter-spacing:3.5px;font-weight:700;color:#e9c987}
.cwt-pop-stage{position:relative;display:flex;align-items:flex-end;justify-content:center;height:min(46vh,340px);margin:10px 0 4px}
.cwt-pop-glow{position:absolute;z-index:-1;left:50%;bottom:6%;width:78%;height:88%;transform:translateX(-50%);border-radius:50%;
 background:radial-gradient(ellipse at 50% 60%,#ffe7ad66,#e9c98722 45%,transparent 70%)}
.cwt-pop-body{position:relative;z-index:1;height:100%;width:auto;max-width:78%;object-fit:contain;object-position:bottom;
 filter:drop-shadow(0 10px 18px #000a) drop-shadow(0 0 22px #ffe3a444);animation:cwt-pop-body 1s .25s cubic-bezier(.15,.8,.3,1) both}
.cwt-pop-silhouette{position:relative;z-index:1;display:flex;align-items:center;justify-content:center;width:150px;height:78%;
 border-radius:75px 75px 18px 18px;background:linear-gradient(180deg,#3a4661,#1b2438);border:1px solid #58647d;
 font-size:54px;font-weight:700;color:#8f9bb3;animation:cwt-pop-body 1s .25s cubic-bezier(.15,.8,.3,1) both}
.cwt-pop-trophy{position:absolute;z-index:2;right:4%;bottom:2%;width:92px;height:92px;object-fit:contain;
 filter:drop-shadow(0 0 16px #e9c98766);animation:cwt-trophy-arrive 1.1s .55s cubic-bezier(.15,.8,.3,1) both}
.cwt-pop-floor{position:absolute;z-index:0;left:50%;bottom:-6px;width:70%;height:22px;transform:translateX(-50%);border-radius:50%;
 background:radial-gradient(ellipse,#e9c98766,transparent 70%)}
.cwt-pop-name{margin:10px 0 2px;font-size:30px;line-height:1.3;letter-spacing:-.5px;color:#fff7df;overflow-wrap:anywhere;
 text-shadow:0 2px 16px #000,0 0 26px #e9c98766;animation:cwt-entrance .7s .7s ease-out both}
.cwt-pop-title{margin:0 0 18px;font-size:13px;color:#c9d2e3;animation:cwt-entrance .7s .85s ease-out both}
.cwt-pop-close{min-width:120px;animation:cwt-entrance .6s 1s ease-out both}
.cwt-pop .cwt-bit{width:6px;height:10px;background:#ebcf8c;animation-name:cwt-award-fall}
.cwt-pop .cwt-bit:nth-child(3n){background:#92b8dc}
.cwt-pop .cwt-bit:nth-child(3n+1){background:#f9ead0;opacity:.85}
@keyframes cwt-pop-fade{from{opacity:0}to{opacity:1}}
@keyframes cwt-pop-rise{from{opacity:0;transform:translateY(26px) scale(.94)}to{opacity:1;transform:none}}
@keyframes cwt-pop-body{from{opacity:0;transform:translateY(30px) scale(.96)}to{opacity:1;transform:none}}
@keyframes cwt-pop-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.cwt-pop *{animation:none!important}}
.cwt-midnight .cwt-award.is-celebrating .cwt-prize{animation:cwt-trophy-arrive 1.1s cubic-bezier(.15,.8,.3,1) both}
.cwt-midnight .cwt-award.is-celebrating .cwt-champ{animation:cwt-entrance .8s .45s ease-out both}
.cwt-midnight .cwt-award .cwt-bit{width:5px;height:9px;background:#ebcf8c;animation-name:cwt-award-fall}
.cwt-midnight .cwt-award .cwt-bit:nth-child(3n){background:#92b8dc}
.cwt-midnight .cwt-award .cwt-bit:nth-child(3n+1){background:#f9ead0;opacity:.8}
@keyframes cwt-entrance{from{opacity:0;transform:translateY(18px) scale(.96)}to{opacity:1;transform:none}}
@keyframes cwt-trophy-arrive{from{opacity:0;transform:translateY(26px) scale(.8)}to{opacity:1;transform:none}}
@keyframes cwt-award-fall{from{transform:translateY(-18px) rotate(0);opacity:0}15%{opacity:.9}to{transform:translateY(430px) rotate(var(--cwt-rot,180deg));opacity:0}}
.cwt-midnight .cwt-talk{margin-top:32px;gap:0}
.cwt-midnight .cwt-broadcast{position:relative;display:grid;grid-template-columns:180px minmax(0,1fr);
 min-height:205px;border:1px solid #8b7857;border-radius:16px;
 background:linear-gradient(110deg,#30313b,#18243a 45%,#101a2c);box-shadow:0 16px 32px #03091655}
.cwt-midnight .cwt-presenter{position:relative;align-self:stretch;overflow:hidden;border-radius:15px 0 0 15px;
 background:radial-gradient(ellipse at 55% 65%,#c1a17730,transparent 70%)}
.cwt-midnight .cwt-presenter img{position:absolute;width:142px;height:auto;max-width:none;top:4px;left:50%;
 transform:translateX(-50%);filter:drop-shadow(0 0 12px #0005)}
.cwt-midnight .is-berry .cwt-presenter img{width:153px;top:4px}
.cwt-midnight .is-lychee .cwt-presenter img{width:124px;top:8px}
.cwt-midnight .cwt-commentary{padding:24px 28px 24px 8px;min-width:0}
.cwt-midnight .cwt-commentary-label{display:flex;align-items:center;gap:12px;font-size:10px;
 letter-spacing:2px;color:#d7bd89;margin-bottom:18px}
.cwt-midnight .cwt-commentary-label span+span{letter-spacing:0;color:#adb8cd}
.cwt-midnight .cwt-speaker{display:flex;align-items:center;gap:10px;margin-bottom:10px}
.cwt-midnight .cwt-speaker b{font-size:20px;color:#fff0cd}
.cwt-midnight .cwt-speaker span{font-size:11px;color:#b7c2d7}
.cwt-midnight .cwt-dialogue{margin:0;font-size:15px;line-height:1.8;overflow-wrap:anywhere;animation:cwt-in .35s ease-out both}
.cwt-midnight .cwt-broadcast.has-music{grid-template-columns:180px minmax(0,1fr) 220px}
.cwt-midnight .cwt-broadcast.music-only{display:block;min-height:0}
.cwt-midnight .cwt-music{align-self:center;margin:22px 20px 22px 0;padding-left:20px;min-width:0;
 border-left:1px solid #72664f;color:#dce5f6;display:flex;flex-direction:column;gap:12px}
.cwt-midnight .cwt-music-label{font-size:9px;letter-spacing:2px;color:#d7bd89}
.cwt-midnight .cwt-music strong{font-size:13px;line-height:1.5;overflow-wrap:anywhere}
.cwt-midnight .cwt-music-actions{display:flex;align-items:center;gap:8px}
.cwt-midnight .cwt-music-actions span{font-size:10px;color:#bec9dc}
.cwt-midnight .cwt-music button{font:inherit;font-size:12px;font-weight:700;min-height:36px;cursor:pointer;
 border:1px solid #b99a61;border-radius:8px;background:#e9c987;color:#142034;padding:0 12px}
.cwt-midnight .cwt-music label{display:flex;align-items:center;gap:8px;font-size:10px;white-space:nowrap}
.cwt-midnight .cwt-music input{min-width:0;width:100%;accent-color:#e9c987;cursor:pointer}
.cwt-midnight .cwt-music small{font-size:11px;line-height:1.5;color:#ffc1bd}
.cwt-midnight .music-only .cwt-music{margin:20px;border:0;padding:0}
.cwt-midnight .cwt-transcript{margin-top:8px;padding:0 14px;border-radius:10px;background:#121e31}
.cwt-midnight .cwt-transcript summary{cursor:pointer;font-size:12px;padding:13px 0;color:#c3cce0}
.cwt-midnight .cwt-transcript summary span{color:#e9c987;margin-left:4px}
.cwt-midnight .cwt-say{background:transparent;border:0;border-top:1px solid #303d54;border-radius:0;padding:12px 0}
.cwt-midnight .cwt-say-name{opacity:1;color:#d9c69e}
.cwt-midnight .cwt-say-text{font-size:13px;overflow-wrap:anywhere}
.cwt-midnight :focus-visible{outline:2px solid #f4d691;outline-offset:4px}
.cwt-midnight.is-narrow{padding:18px 12px 24px;border-radius:10px;min-height:560px}
.cwt-midnight.is-narrow .cwt-head h3{font-size:20px}
.cwt-midnight.is-narrow .cwt-prize img{width:80px;height:80px}
.cwt-midnight.is-narrow .cwt-reveal{min-height:206px;padding:18px 10px}
.cwt-midnight.is-narrow .cwt-reveal-title{font-size:23px}
.cwt-midnight.is-narrow .cwt-reveal-card{min-width:0;width:100%;max-width:260px;padding:16px}
.cwt-midnight.is-narrow .cwt-reveal-visual{height:130px;gap:6px}
.cwt-midnight.is-narrow .cwt-reveal-trainer{min-width:100px}
.cwt-midnight.is-narrow .cwt-reveal-body{max-width:120px}
.cwt-midnight.is-narrow .cwt-reveal-partner{min-width:82px;max-width:100px}
.cwt-midnight.is-narrow .cwt-reveal-partner img{width:85px;height:85px}
.cwt-midnight.is-narrow .cwt-reveal-face{width:78px;height:78px}
.cwt-midnight.is-narrow .cwt-reveal-silhouette{width:78px}
.cwt-midnight.is-narrow .cwt-reveal-go{font-size:28px}
.cwt-midnight.is-narrow .cwt-award .cwt-prize img{width:80px;height:80px}
.cwt-midnight.is-narrow .cwt-award .cwt-champ-card b{font-size:20px}
.cwt-midnight.is-narrow .cwt-feed .cwt-match{background:#162237ed;grid-template-columns:minmax(0,1fr) auto minmax(0,1fr)}
.cwt-midnight.is-narrow .cwt-card{padding:7px 5px;gap:4px}
.cwt-midnight.is-narrow .cwt-name{font-size:12px}
.cwt-midnight.is-narrow .cwt-used{display:none}
.cwt-midnight.is-narrow .cwt-broadcast{grid-template-columns:96px minmax(0,1fr);min-height:192px}
.cwt-midnight.is-narrow .cwt-presenter img{width:117px;top:18px}
.cwt-midnight.is-narrow .is-berry .cwt-presenter img{width:126px}
.cwt-midnight.is-narrow .is-lychee .cwt-presenter img{width:102px}
.cwt-midnight.is-narrow .cwt-commentary{padding:18px 14px 18px 4px}
.cwt-midnight.is-narrow .cwt-commentary-label{font-size:9px;letter-spacing:1px;gap:6px;margin-bottom:12px}
.cwt-midnight.is-narrow .cwt-dialogue{font-size:13px;line-height:1.75}
.cwt-midnight.is-narrow .cwt-speaker b{font-size:18px}
.cwt-midnight.is-narrow .cwt-broadcast.has-music{grid-template-columns:96px minmax(0,1fr)}
.cwt-midnight.is-narrow .cwt-music{grid-column:1/-1;margin:0 14px 16px;padding:14px 0 0;
 border-left:0;border-top:1px solid #72664f;gap:10px}
.cwt-midnight.is-narrow .music-only .cwt-music{margin:16px;border:0;padding:0}
.cwt-midnight .cwt-round-stage{position:relative;overflow:hidden;text-align:center;margin:18px 0 24px;padding:28px 24px;
 border:1px solid #7f735d;border-radius:16px;background:radial-gradient(ellipse at 50% 0,#bca06522,transparent 75%),#101d30eb;animation:cwt-in .6s ease-out both}
.cwt-midnight .cwt-round-stage.is-final{border-color:#d0b375;background:radial-gradient(ellipse at 50% 0,#d4b47433,transparent 75%),#101d30f2}
.cwt-midnight .cwt-stage-eyebrow{font-size:10px;letter-spacing:4px;color:#e9c987}
.cwt-midnight .cwt-round-stage h4{margin:12px 0 20px;font-size:26px;color:#fff0cd}
.cwt-midnight .cwt-stage-clock{display:flex;justify-content:center;align-items:center;gap:14px;color:#b8c5da;font-size:12px}
.cwt-midnight .cwt-stage-clock b{font-size:24px;color:#f2d69a;font-variant-numeric:tabular-nums;min-width:66px}
.cwt-midnight .cwt-final-pair{display:grid;grid-template-columns:minmax(0,1fr) auto minmax(0,1fr);gap:24px;max-width:620px;margin:0 auto 24px;align-items:center}
.cwt-midnight .cwt-finalist{display:flex;flex-direction:column;align-items:center;gap:12px;min-width:0;color:#f5eddb}
.cwt-midnight .cwt-finalist .cwt-ava{width:58px;height:58px;border:1px solid #cfb879;box-shadow:0 0 22px #c8a66522}
.cwt-midnight .cwt-finalist b{font-size:18px;overflow-wrap:anywhere;max-width:100%}
.cwt-midnight .cwt-final-vs{font-size:18px;letter-spacing:3px;color:#d3b579}
.cwt-midnight .cwt-stage-caption{margin:0;color:#bac7db;font-size:12px}
.cwt-midnight.is-narrow .cwt-round-stage{padding:24px 14px}
.cwt-midnight.is-narrow .cwt-round-stage h4{font-size:22px}
.cwt-midnight.is-narrow .cwt-final-pair{gap:12px}
.cwt-midnight.is-narrow .cwt-finalist b{font-size:14px}
/* 실서비스 구도: 중앙 대진 + 하단 해설석. 장식용 행을 쌓지 않는다. */
.cwt-root.cwt-midnight.is-competition{max-width:1480px;min-height:0;padding:16px 24px 18px}
.cwt-midnight.is-competition .cwt-head{padding:0 0 10px;margin-bottom:8px}
.cwt-midnight.is-competition .cwt-head h3{font-size:22px}
.cwt-midnight.is-competition .cwt-arena{background:linear-gradient(180deg,#08102033,#08102070 50%,#0b1425e8),url('/cherish/tour/midnight/arena.png') center/cover no-repeat}
.cwt-midnight.is-competition .cwt-round-stage{display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:8px 18px;margin:0 0 8px;padding:8px 14px;border-radius:8px;animation:none}
.cwt-midnight.is-competition .cwt-round-stage>.cwt-stage-eyebrow{display:none}
.cwt-midnight.is-competition .cwt-round-stage h4{font-size:14px;margin:0}
.cwt-midnight.is-competition .cwt-stage-clock{gap:8px;font-size:11px}
.cwt-midnight.is-competition .cwt-stage-clock b{font-size:18px;min-width:40px}
.cwt-midnight.is-competition .cwt-final-pair{display:flex;gap:10px;margin:0;max-width:100%;min-width:0}
.cwt-midnight.is-competition .cwt-finalist{flex-direction:row;gap:5px}
.cwt-midnight.is-competition .cwt-finalist .cwt-ava{width:24px;height:24px}
.cwt-midnight.is-competition .cwt-finalist b{font-size:12px}
.cwt-midnight.is-competition .cwt-final-vs{font-size:11px}
.cwt-midnight.is-competition .cwt-my-match{margin:0 0 8px;padding:8px 12px;border-radius:8px;gap:12px}
.cwt-midnight.is-competition .cwt-my-match .cwt-stage-eyebrow,.cwt-midnight.is-competition .cwt-my-match p{display:none}
.cwt-midnight.is-competition .cwt-my-match h4{margin:0;font-size:13px}
.cwt-midnight.is-competition .cwt-my-match .cwt-btn{min-height:32px;font-size:12px}
.cwt-midnight .cwt-team-summary{display:flex;align-items:center;gap:12px;min-height:44px;padding:2px 12px;margin-bottom:6px;border:1px solid #6c614a66;border-radius:8px;background:#111d30c9}
.cwt-midnight .cwt-team-summary>b{font-size:12px;color:#eed8ad;white-space:nowrap}
.cwt-midnight .cwt-team-mini{display:flex;gap:4px;align-items:center}
.cwt-midnight .cwt-team-mini img{width:38px;height:38px;object-fit:contain;display:block}
.cwt-midnight .cwt-team-hint{font-size:11px;color:#b7c5da}
.cwt-midnight .cwt-team-summary>.cwt-btn{margin-left:auto;min-height:32px;padding:0 12px;white-space:nowrap;font-size:12px}
.cwt-midnight .cwt-team-dialog{box-sizing:border-box;width:min(900px,calc(100% - 24px));max-height:calc(100dvh - 32px);padding:0;color:var(--cwt-ink);background:#142035;border:1px solid #bd9f66;border-radius:16px;box-shadow:0 24px 90px #0009}
.cwt-team-dialog::backdrop{background:#030917c9;backdrop-filter:blur(5px)}
.cwt-midnight .cwt-team-dialog .cwt-lineup{border:0;margin:0;padding:22px;background:transparent}
.cwt-midnight .cwt-dialog-close{display:block;margin:0 0 4px auto;min-height:36px;padding:0 14px;border:1px solid #6c7890;border-radius:8px;background:#202d42;color:#e7edf9;cursor:pointer;font:inherit;font-size:12px}
.cwt-midnight .cwt-bracket-stage{position:relative;isolation:isolate;padding:0 0 8px}
.cwt-midnight .cwt-bracket-stage.is-revealing{min-height:350px}
.cwt-midnight .cwt-bracket-stage>.cwt-reveal{position:absolute;inset:12px 15%;z-index:2;margin:0;min-height:0;
 border:0;background:none;overflow:visible}
.cwt-midnight .cwt-bracket-stage>.cwt-reveal:before,
.cwt-midnight .cwt-bracket-stage>.cwt-reveal:after{display:none}
.cwt-midnight .cwt-card.is-reveal-current{border-color:#e9c987;box-shadow:0 0 0 2px #e9c98755,0 0 24px #e9c98766;
 animation:cwt-reveal-seat .55s ease-out both}
@keyframes cwt-reveal-seat{from{opacity:.2;transform:scale(.88)}to{opacity:1;transform:none}}
.cwt-midnight.is-narrow .cwt-bracket-stage>.cwt-reveal{inset:0;min-height:0}
.cwt-midnight.is-narrow .cwt-bracket-stage.is-revealing{min-height:320px}
@media(prefers-reduced-motion:reduce){.cwt-midnight .cwt-card.is-reveal-current{animation:none}
 .cwt-midnight .cwt-reveal-card{animation:none}}
.cwt-midnight .cwt-bracket-stage>.cwt-podium{display:block;max-width:none;margin:0;height:54px}
.cwt-midnight .cwt-bracket-stage .cwt-prize{gap:0;margin:0}
.cwt-midnight .cwt-bracket-stage .cwt-prize img{width:46px;height:46px}
.cwt-midnight .cwt-bracket-stage .cwt-prize span{font-size:8px;letter-spacing:2px}
.cwt-midnight .cwt-bracket-stage .cwt-tree{gap:22px;padding:0 0 4px;overflow:visible}
.cwt-midnight .cwt-bracket-stage .cwt-row{gap:7px}
.cwt-midnight .cwt-bracket-stage .cwt-row:not(:first-child){width:100%;min-width:0;align-self:center}
.cwt-midnight .cwt-bracket-stage .cwt-row:not(:first-child) .cwt-match{max-width:min(320px,50%)}
.cwt-midnight .cwt-bracket-stage .cwt-row-grid{gap:12px}
.cwt-midnight .cwt-bracket-stage .cwt-row-h{font-size:11px;line-height:16px;letter-spacing:2px}
.cwt-midnight .cwt-bracket-stage .cwt-tree .cwt-match{grid-template-columns:minmax(0,1fr) auto minmax(0,1fr);grid-template-rows:auto;max-width:520px;padding:5px;gap:5px;border-radius:8px}
.cwt-midnight .cwt-bracket-stage .cwt-tree .cwt-card{min-height:28px;padding:3px 6px;gap:5px;box-sizing:border-box}
.cwt-midnight .cwt-bracket-stage .cwt-tree .cwt-card:last-child{grid-column:3}
.cwt-midnight .cwt-bracket-stage .cwt-tree .cwt-ava{width:20px;height:20px}
.cwt-midnight .cwt-bracket-stage .cwt-tree .cwt-name{font-size:12px}
.cwt-midnight .cwt-bracket-stage .cwt-tree .cwt-mid{grid-column:2;grid-row:1;min-width:20px;gap:2px}
.cwt-midnight .cwt-bracket-stage .cwt-tree .cwt-calling{font-size:9px;white-space:normal;text-align:center;max-width:38px}
.cwt-midnight .cwt-bracket-stage .cwt-tree .cwt-watch{min-height:28px;padding:0 6px;font-size:11px}
.cwt-midnight .cwt-bracket-stage .cwt-row:not(:first-child) .cwt-cell:before{height:10px}
.cwt-midnight .cwt-bracket-stage .cwt-row:not(:first-child) .cwt-cell:after{top:calc(100% + 10px);height:25px}
.cwt-midnight .cwt-bracket-stage.is-round-view{padding:0 0 8px}
.cwt-midnight .is-round-view>.cwt-podium{height:38px}
.cwt-midnight .is-round-view .cwt-prize{display:none}
.cwt-midnight .is-round-view .cwt-feed{margin-top:-38px}
.cwt-midnight:not(.is-narrow) .is-round-view .cwt-feed-list{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;max-height:clamp(190px,calc(100dvh - 530px),360px);overflow-y:auto;padding:2px}
.cwt-midnight .cwt-feed .cwt-match{background:#142036ed}
.cwt-midnight.is-competition .cwt-talk{margin-top:8px}
.cwt-midnight.is-competition .cwt-broadcast{grid-template-columns:130px minmax(0,1fr);min-height:130px;border-radius:12px}
.cwt-midnight.is-competition .cwt-broadcast.has-music{grid-template-columns:130px minmax(0,1fr) 210px}
.cwt-midnight.is-competition .cwt-presenter img{width:102px;top:0}
.cwt-midnight.is-competition .is-berry .cwt-presenter img{width:112px}
.cwt-midnight.is-competition .is-lychee .cwt-presenter img{width:92px}
.cwt-midnight.is-competition .cwt-commentary{padding:14px 20px 12px 4px}
.cwt-midnight.is-competition .cwt-commentary-label{margin-bottom:7px;font-size:9px}
.cwt-midnight.is-competition .cwt-speaker{margin-bottom:5px}
.cwt-midnight.is-competition .cwt-speaker b{font-size:17px}
.cwt-midnight.is-competition .cwt-dialogue{font-size:13px;line-height:1.65}
.cwt-midnight.is-competition .cwt-music{margin:12px 14px 12px 0;padding-left:14px;gap:7px}
.cwt-midnight.is-competition .cwt-transcript{margin-top:4px}
.cwt-midnight.is-competition .cwt-transcript summary{padding:6px 0;font-size:11px}
.cwt-midnight.is-competition .cwt-award{padding:14px;margin:6px 0 10px}
.cwt-midnight.is-competition .cwt-award .cwt-podium{display:none}
.cwt-midnight.is-competition .cwt-award .cwt-champ{margin:0 auto}
.cwt-midnight.is-competition .cwt-award-caption{display:none}
.cwt-midnight.is-narrow.is-competition{padding:12px 10px;min-height:0}
.cwt-midnight.is-narrow .cwt-team-summary{gap:7px;padding:2px 7px}
.cwt-midnight.is-narrow .cwt-team-mini{gap:0}
.cwt-midnight.is-narrow .cwt-team-mini img{width:30px;height:34px}
.cwt-midnight.is-narrow .cwt-team-hint{display:none}
.cwt-midnight.is-narrow .cwt-team-summary>.cwt-btn{padding:0 8px;font-size:11px;min-height:38px}
.cwt-midnight.is-narrow .cwt-team-dialog .cwt-lineup{padding:16px 12px}
.cwt-midnight.is-narrow .cwt-team-dialog .cwt-slots{margin:14px 0}
.cwt-midnight.is-narrow.is-competition .cwt-round-stage{gap:6px;padding:8px}
.cwt-midnight.is-narrow.is-competition .cwt-stage-clock span{display:none}
.cwt-midnight.is-narrow.is-competition .cwt-my-match{flex-direction:row;align-items:center}
.cwt-midnight.is-narrow.is-competition .cwt-broadcast,.cwt-midnight.is-narrow.is-competition .cwt-broadcast.has-music{grid-template-columns:80px minmax(0,1fr);min-height:140px}
.cwt-midnight.is-narrow.is-competition .cwt-presenter img{width:94px;top:8px}
.cwt-midnight.is-narrow.is-competition .cwt-commentary{padding:12px 10px 12px 4px}
.cwt-midnight.is-narrow.is-competition .cwt-music{grid-column:1/-1;margin:0 12px 12px;padding:10px 0 0;border-left:0}
.cwt-midnight.is-narrow .cwt-bracket-stage .cwt-feed .cwt-match{min-height:52px;padding:6px;box-sizing:border-box}
.cwt-midnight.is-narrow .cwt-bracket-stage .cwt-card{min-height:34px;box-sizing:border-box}

/* 참가 접수: 경기장 초대장과 개인 참가권. 지속 타이머/새 애니메이션 없음. */
.cwt-midnight .cwt-lobby{grid-template-columns:minmax(0,1.25fr) minmax(0,1fr);border:1px solid #a68a566e;border-radius:18px;box-shadow:0 20px 50px #0003}
.cwt-midnight .cwt-lobby-intro{padding:32px 32px 28px;min-height:280px;background:linear-gradient(90deg,#0b1629f5 8%,#142039bf 64%,#15243b60),url('/cherish/tour/midnight/arena.png') center/cover}
.cwt-midnight .cwt-lobby-intro:before{content:'';position:absolute;inset:12px;border:1px solid #d5b87530;border-radius:10px;pointer-events:none}
.cwt-midnight .cwt-lobby-intro h4{max-width:360px;font-size:clamp(26px,3vw,38px);letter-spacing:-1.4px;margin:24px 0 12px;padding-right:65px}
.cwt-midnight .cwt-lobby-intro>p{position:relative;z-index:1;font-size:13px;padding-right:100px;margin:0;line-height:1.9}
.cwt-midnight .cwt-lobby-trophy{width:150px;height:180px;right:8px;bottom:64px;opacity:.94;pointer-events:none}
.cwt-midnight .cwt-lobby-facts{position:relative;z-index:1;display:flex;gap:20px;margin:22px 0;color:#bdc9dc;font-size:11px}
.cwt-midnight .cwt-lobby-facts span{display:flex;align-items:baseline;gap:5px}
.cwt-midnight .cwt-lobby-facts b{font-size:22px;color:#eace91;font-variant-numeric:tabular-nums}
.cwt-midnight .cwt-lobby-time{margin-top:18px;border-top:1px solid #d5b87535;padding-top:16px;gap:5px}
.cwt-midnight .cwt-lobby-entry{padding:28px;align-self:stretch;border-left:1px solid #b99a6133;background:linear-gradient(140deg,#213048,#121e31);display:flex;flex-direction:column;justify-content:center}
.cwt-midnight .cwt-entry-heading{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:18px}
.cwt-midnight .cwt-entry-heading .cwt-stage-eyebrow{font-size:9px;letter-spacing:3px;color:#9fafc6}
.cwt-midnight .cwt-entry-heading .cwt-entry-status{margin:0;font-size:10px;padding:5px 10px}
.cwt-midnight .cwt-entry-title{font-size:21px;letter-spacing:-.6px;color:#fff0d1;margin:0 0 14px;line-height:1.5;word-break:keep-all}
.cwt-midnight .cwt-lobby .cwt-note{font-size:13px;line-height:1.85;margin-bottom:18px}
.cwt-midnight .cwt-lobby .cwt-check{background:#101c2c80;border-color:#64728a66;padding:13px;margin-bottom:14px;font-size:13px}
.cwt-midnight .cwt-lobby .cwt-btn:not(.is-ghost){background:linear-gradient(110deg,#f3db9e,#d6b573);border-color:#e9cc92;box-shadow:0 5px 18px #080f2066;color:#18243a}
.cwt-midnight .cwt-lobby-guide{padding:22px 24px;margin-top:14px;background:#111d30eb;border-color:#5b6b824d}
.cwt-midnight .cwt-guide-heading{display:flex;align-items:baseline;gap:10px 18px;flex-wrap:wrap;margin-bottom:18px}
.cwt-midnight .cwt-guide-heading .cwt-stage-eyebrow{font-size:9px;letter-spacing:2px}
.cwt-midnight .cwt-guide-heading h4{font-size:17px;margin:0}
.cwt-midnight .cwt-guide-heading>p{font-size:11px;color:#aab9cf;margin:0}
.cwt-midnight .cwt-lobby-guide ol{list-style:none;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));padding:0;gap:20px;margin:0}
.cwt-midnight .cwt-lobby-guide li{display:flex;gap:12px;min-width:0}
.cwt-midnight .cwt-lobby-guide li>span{font-size:22px;font-weight:700;color:#c9ad76;line-height:1.3;font-variant-numeric:tabular-nums}
.cwt-midnight .cwt-lobby-guide li b{font-size:13px;color:#e5eaf3}
.cwt-midnight .cwt-lobby-guide li p{font-size:12px;line-height:1.8;color:#aebdd1;margin:7px 0 0;word-break:keep-all;overflow-wrap:anywhere}
.cwt-midnight .cwt-lobby-guide .cwt-warn{margin:18px 0 0;padding:12px 0 0;border-top:1px solid #5b6b824d;font-size:11px;line-height:1.8;color:#bac5d6}
.cwt-midnight .cwt-lobby-guide .cwt-warn b{color:#ddc492;margin-right:8px}
.cwt-midnight.is-narrow .cwt-lobby{grid-template-columns:1fr}
.cwt-midnight.is-narrow .cwt-lobby-intro{min-height:0;padding:26px 22px 20px}
.cwt-midnight.is-narrow .cwt-lobby-intro h4{font-size:27px;max-width:none;padding-right:60px;margin:18px 0 12px}
.cwt-midnight.is-narrow .cwt-lobby-intro>p{font-size:12px;padding-right:50px}
.cwt-midnight.is-narrow .cwt-lobby-trophy{width:92px;height:120px;right:2px;bottom:82px;opacity:.75}
.cwt-midnight.is-narrow .cwt-lobby-facts{gap:16px;margin:16px 0}
.cwt-midnight.is-narrow .cwt-lobby-time{padding-right:0}
.cwt-midnight.is-narrow .cwt-lobby-entry{padding:22px;border-left:0;border-top:1px solid #b99a6133}
.cwt-midnight.is-narrow .cwt-lobby-guide{padding:20px 18px}
.cwt-midnight.is-narrow .cwt-lobby-guide ol{grid-template-columns:1fr;gap:16px}
.cwt-midnight.is-narrow .cwt-guide-heading>p{width:100%}

/* 승부 예측 카드 (2026-09-23, 최소판) */
.cwt-bet{margin:0 0 14px;padding:18px;border-radius:14px;border:1px solid #96805b;background:rgba(19,30,48,.95)}
.cwt-bet-head{display:flex;justify-content:space-between;align-items:flex-end;gap:12px;margin-bottom:12px}
.cwt-bet-head h4{margin:8px 0 0;font-size:19px;color:#fff0cd}
.cwt-bet-clock{font-size:14px;color:#efdaa9;white-space:nowrap}
.cwt-bet-sides{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:12px}
.cwt-bet-side{font:inherit;display:flex;flex-direction:column;align-items:center;gap:6px;padding:14px 10px;border-radius:12px;
 border:1px solid #49536a;background:#1b2840;color:#e8eef8;cursor:pointer;min-width:0}
.cwt-bet-side:disabled{cursor:default}
.cwt-bet-side.is-on{border-color:#e9c987;background:#2b3754}
.cwt-bet-side.is-mine{border-color:#e9c987;box-shadow:inset 0 0 0 2px #e9c98766}
.cwt-bet-name{font-size:14px;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-bet-odds{font-size:22px;color:#f3dfb5}
.cwt-bet-stakes{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px}
.cwt-bet-foot{display:flex;justify-content:space-between;align-items:center;gap:14px}
.cwt-bet-foot .cwt-btn{min-height:44px;min-width:170px;flex-shrink:0}
.cwt-bet-mine{list-style:none;margin:12px 0 0;padding:12px 0 0;border-top:1px solid #43516a;display:grid;gap:6px}
.cwt-bet-mine:first-child{margin-top:0;padding-top:0;border-top:0}
.cwt-bet-mine li{display:grid;grid-template-columns:90px 1fr auto;gap:10px;font-size:13px;color:#c0cde0}
.cwt-bet-mine li b{color:#efdcba}
.cwt-bet-mine li.is-won b{color:#9fe0b0}
.cwt-bet-mine li.is-lost b{color:#9aa6b8}
.cwt-midnight.is-narrow .cwt-bet-foot{flex-direction:column;align-items:stretch}
.cwt-midnight.is-narrow .cwt-bet-mine li{grid-template-columns:70px 1fr auto}

@media(prefers-reduced-motion:reduce){.cwt-root.cwt-midnight *{animation:none!important;transition:none!important}
 .cwt-midnight .cwt-confetti{display:none}}
`;

/**
 * 새싹컵 (2026-10-06) — 빌려주기·뽑기 화면. 대회장 미드나이트 테마 위에 **새싹 초록**을 한 색 더한다.
 * 금색은 「확정·선택」, 초록은 「새싹컵·고를 수 있음」. 움직임은 전부 CSS — 요청과 무관하다.
 * 줄인 동작 설정이면 대회장 전체 규칙(.cwt-root.cwt-midnight * animation:none)이 함께 끈다.
 */
const CWS_CSS = `
.cwt-midnight .cwt-badge.is-sprout{color:#d6f5dc;border-color:#6f9f7a;background:#1f3a30}
.cwt-midnight .cwt-lobby.is-sprout{border-color:#6f8f6c}
.cwt-midnight .cwt-lobby.is-sprout .cwt-lobby-intro{background:radial-gradient(ellipse at 75% 100%,#7fd09a33,transparent 70%),linear-gradient(130deg,#24394a,#121e30)}
.cwt-midnight .cwt-lobby.is-sprout .cwt-lobby-intro h4{color:#eaffe0}
.cwt-midnight .cwt-guide.is-sprout{border-color:#5c7a62}
.cwt-midnight .cwt-guide.is-sprout h4{color:#dff6d6}
.cwt-midnight .cwt-card-lent{width:26px;height:26px;object-fit:contain;flex-shrink:0;margin-left:-2px;filter:drop-shadow(0 2px 3px #0008)}
.cwt-midnight .cwt-reveal-partner.is-borrowed span{color:#bfeec8;letter-spacing:0;font-size:10px}
.cwt-midnight .cwt-slot.is-borrowed{border-color:#9ad9a5;background:#22382d}
.cwt-midnight .cwt-slot.is-borrowed:disabled{opacity:1;cursor:default}
.cwt-midnight .cwt-slot-tag{display:inline-block;margin-top:6px;font-size:10px;padding:2px 7px;border-radius:20px;background:#9ad9a5;color:#143022;font-weight:700}

.cwt-midnight .cws{position:relative;display:grid;gap:18px;margin-bottom:22px}
.cwt-midnight .cws-mine-slot:empty{display:none}
.cwt-midnight .cws-hero{position:relative;display:flex;align-items:center;justify-content:space-between;gap:20px;
 padding:26px 28px;border:1px solid #6f8f6c;border-radius:16px;overflow:hidden;
 background:radial-gradient(ellipse at 8% 130%,#7fd09a33,transparent 60%),radial-gradient(ellipse at 95% -30%,#e9c9872b,transparent 55%),linear-gradient(120deg,#1d3040,#132036)}
.cwt-midnight .cws.is-my-turn .cws-hero{border-color:#9ad9a5;box-shadow:0 0 0 1px #9ad9a555,0 0 40px #9ad9a522}
.cwt-midnight .cws-hero-copy{position:relative;z-index:1;min-width:0}
.cwt-midnight .cws-hero h4{font-size:24px;line-height:1.35;color:#f4f8e8;margin:10px 0 8px;word-break:keep-all;animation:cws-rise .45s cubic-bezier(.2,.8,.2,1) both}
.cwt-midnight .cws-hero p{margin:0;font-size:13px;line-height:1.7;color:#c4d3d6}

.cwt-midnight .cws-clock{position:relative;flex-shrink:0;width:84px;height:84px;display:grid;place-items:center}
.cwt-midnight .cws-clock svg{position:absolute;inset:0;width:100%;height:100%;transform:rotate(-90deg)}
.cwt-midnight .cws-clock-track{fill:none;stroke:#ffffff1c;stroke-width:6}
.cwt-midnight .cws-clock-fill{fill:none;stroke:#9ad9a5;stroke-width:6;stroke-linecap:round;transition:stroke-dashoffset .25s linear,stroke .3s}
.cwt-midnight .cws-clock b{font-size:26px;line-height:1;color:#f6fbef;font-variant-numeric:tabular-nums;margin-top:-8px}
.cwt-midnight .cws-clock small{position:absolute;bottom:19px;font-size:10px;color:#b5c6c8}
.cwt-midnight .cws-clock.is-hurry .cws-clock-fill{stroke:#f08a7e}
.cwt-midnight .cws-clock.is-hurry b{color:#ffc9bf;animation:cws-beat .5s ease-in-out infinite}

.cwt-midnight .cws-mine{display:flex;align-items:center;gap:18px;padding:20px 22px;border:1px solid #4a5a70;border-radius:14px;
 background:rgba(19,30,48,.95);animation:cws-rise .4s both}
.cwt-midnight .cws-mine>div{flex:1;min-width:0}
.cwt-midnight .cws-mine h5{margin:8px 0 6px;font-size:18px;line-height:1.4;color:#fff0cd;word-break:keep-all}
.cwt-midnight .cws-mine p{margin:0;font-size:13px;line-height:1.7;color:#c0cde0}
.cwt-midnight .cws-mine .cwt-btn{min-height:46px;min-width:180px;flex-shrink:0}
.cwt-midnight .cws-mine .cwt-btn:disabled{opacity:.5;cursor:default}
.cwt-midnight .cws-mine.is-turn{border-color:#9ad9a5;
 background:linear-gradient(110deg,#25402f,#17273d);box-shadow:0 0 0 1px #9ad9a544,0 12px 30px #00000066}
.cwt-midnight .cws-mine.is-turn h5{color:#e6ffd8}
.cwt-midnight .cws-mine.is-done{border-color:#d1b477;background:linear-gradient(110deg,#394034,#17273d)}
.cwt-midnight .cws-mine-mon{width:72px;height:72px;object-fit:contain;flex-shrink:0;filter:drop-shadow(0 6px 10px #0008);animation:cws-hop 2.6s ease-in-out infinite}
.cwt-midnight .cws-mine.is-lender{display:grid;gap:14px;border-color:#6f8f6c}
.cwt-midnight .cws-mine-head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px}
.cwt-midnight .cws-mine-badge{flex-shrink:0;padding:7px 12px;border-radius:30px;background:#9ad9a5;color:#143022;font-size:11px;font-weight:700;animation:cws-pop .35s both}
.cwt-midnight .cws-filter{font:inherit;width:100%;min-height:40px;padding:0 14px;border:1px solid #4b5970;border-radius:10px;background:#202e43;color:#edf1fc}
.cwt-midnight .cws-filter:focus{outline:2px solid #9ad9a5;outline-offset:1px}
.cwt-midnight .cws-opts{display:grid;grid-template-columns:repeat(auto-fill,minmax(112px,1fr));gap:10px;max-height:330px;overflow:auto;padding:2px;scrollbar-color:#6d7180 #132038}
.cwt-midnight .cws-opt{font:inherit;position:relative;display:flex;flex-direction:column;align-items:center;gap:4px;padding:14px 8px 10px;
 border:2px solid #44536a;border-radius:12px;background:#1d2a40;color:#edf1fc;cursor:pointer;transition:transform .15s,border-color .15s,background .15s}
.cwt-midnight .cws-opt:hover{transform:translateY(-2px);border-color:#7a8aa3}
.cwt-midnight .cws-opt:focus-visible{outline:2px solid #9ad9a5;outline-offset:2px}
.cwt-midnight .cws-opt img{width:64px;height:64px;object-fit:contain}
.cwt-midnight .cws-opt b{font-size:13px;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-midnight .cws-opt small{font-size:10px;color:#a9b8cc}
.cwt-midnight .cws-opt em{position:absolute;top:6px;left:6px;font-style:normal;font-size:9px;padding:1px 5px;border-radius:4px;background:#e9c98733;color:#f1d69d}
.cwt-midnight .cws-opt.is-on{border-color:#9ad9a5;background:#24392f;box-shadow:inset 0 0 18px #9ad9a51f}
.cwt-midnight .cws-opt.is-current:after{content:'등록됨';position:absolute;top:6px;right:6px;font-size:9px;padding:1px 5px;border-radius:4px;background:#9ad9a5;color:#143022;font-weight:700}
.cwt-midnight .cws-mine-foot{display:flex;align-items:center;justify-content:space-between;gap:14px;border-top:1px solid #43516a;padding-top:14px}
.cwt-midnight .cws-mine-foot>span{display:flex;align-items:center;gap:8px;min-width:0;font-size:13px;color:#c9d5e6}
.cwt-midnight .cws-mine-foot img{width:40px;height:40px;object-fit:contain}
.cwt-midnight .cws-mine-foot b{color:#fff0cd}

.cwt-midnight .cws-stage{display:grid;grid-template-columns:minmax(0,1fr) 260px;gap:18px;align-items:start}
.cwt-midnight .cws-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(176px,1fr));gap:16px;perspective:1400px}

.cwt-midnight .cws-card{font:inherit;position:relative;display:block;width:100%;padding:0;border:0;border-radius:16px;background:none;color:inherit;text-align:left;cursor:default}
.cwt-midnight .cws-card-inner{position:relative;border-radius:16px;transform-style:preserve-3d}
.cwt-midnight .cws-face{position:relative;display:flex;flex-direction:column;align-items:center;gap:6px;min-height:300px;padding:14px 12px 12px;
 border:1px solid #56647c;border-radius:16px;background:linear-gradient(160deg,#25344c,#141f33 70%);
 box-shadow:0 10px 24px #02071666;backface-visibility:hidden;-webkit-backface-visibility:hidden;transition:transform .2s,box-shadow .2s,border-color .2s,filter .3s}
.cwt-midnight .cws-back{position:absolute;inset:0;display:grid;place-items:center;border:1px solid #6f8f6c;border-radius:16px;overflow:hidden;
 transform:rotateY(180deg);backface-visibility:hidden;-webkit-backface-visibility:hidden;
 background:repeating-linear-gradient(45deg,#ffffff08 0 10px,transparent 10px 20px),radial-gradient(circle at 50% 44%,#7fd09a40,transparent 58%),linear-gradient(160deg,#1f3a35,#122131)}
.cwt-midnight .cws-back-mark{position:relative;width:66px;height:66px;border-radius:50%;border:2px solid #9ad9a5aa;box-shadow:0 0 22px #9ad9a533}
.cwt-midnight .cws-back-mark:before,.cwt-midnight .cws-back-mark:after{content:'';position:absolute;bottom:20px;left:31px;width:16px;height:24px;
 border-radius:0 100% 0 100%;background:#9ad9a5;transform-origin:0 100%;transform:rotate(-14deg)}
.cwt-midnight .cws-back-mark:after{background:#c6eec9;transform:scaleX(-1) rotate(-14deg)}

.cwt-midnight .cws-card.is-back{display:flex;flex-direction:column;gap:10px}
.cwt-midnight .cws-card.is-back .cws-card-inner{height:210px;animation:cws-deal .55s cubic-bezier(.2,.8,.2,1) both;animation-delay:calc(var(--cws-i) * 80ms)}
.cwt-midnight .cws-card.is-back .cws-back{transform:none}
.cwt-midnight .cws-card.is-back.is-ready .cws-back{border-color:#9ad9a5;box-shadow:0 0 0 1px #9ad9a555,0 0 26px #9ad9a533}
.cwt-midnight .cws-card.is-back.is-ready .cws-back-mark{animation:cws-glow 2.4s ease-in-out infinite}
.cwt-midnight .cws-card.is-face .cws-card-inner{animation:cws-flip .8s cubic-bezier(.2,.8,.2,1) both;animation-delay:calc(var(--cws-i) * 280ms)}

.cwt-midnight .cws-card-foot{display:flex;align-items:center;gap:8px;min-width:0;width:100%;font-size:12px;color:#d6dfeb}
.cwt-midnight .cws-face .cws-card-foot{margin-top:auto;padding-top:10px;border-top:1px solid #ffffff14}
.cwt-midnight .cws-lender-name{flex:0 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-midnight .cws-card.is-back .cws-lender-name{flex:1}
.cwt-midnight .cws-card-foot small{flex-shrink:0;color:#9fb0c6;font-size:11px}
.cwt-midnight .cws-ava{flex-shrink:0;display:grid;place-items:center;width:24px;height:24px;border-radius:50%;overflow:hidden;background:#2b3a52}
.cwt-midnight .cws-ava img{width:100%;height:100%;object-fit:cover}
.cwt-midnight .cws-ready{flex-shrink:0;padding:3px 8px;border-radius:20px;background:#9ad9a5;color:#143022;font-size:10px;font-weight:700;animation:cws-pop .4s cubic-bezier(.2,1.4,.4,1) both}
.cwt-midnight .cws-wait{display:inline-flex;gap:3px;flex-shrink:0}
.cwt-midnight .cws-wait i{width:5px;height:5px;border-radius:50%;background:#a9b8cc;animation:cws-dot 1.2s infinite}
.cwt-midnight .cws-wait i:nth-child(2){animation-delay:.15s}
.cwt-midnight .cws-wait i:nth-child(3){animation-delay:.3s}

.cwt-midnight .cws-art{position:relative;display:grid;place-items:center;width:100%;height:96px}
.cwt-midnight .cws-art-glow{position:absolute;width:92px;height:92px;border-radius:50%;background:radial-gradient(circle,#e9c98738,transparent 70%)}
.cwt-midnight .cws-sprite{position:relative;max-width:96px;max-height:96px;object-fit:contain;filter:drop-shadow(0 8px 10px #0009);
 animation:cws-hop 3s ease-in-out infinite;animation-delay:calc(var(--cws-i) * -.4s)}
.cwt-midnight .cws-shiny{position:absolute;top:2px;right:14px;color:#ffe9a8;font-size:14px;animation:cws-twinkle 1.8s ease-in-out infinite}
.cwt-midnight .cws-mon-name{max-width:100%;font-size:16px;color:#fff3d6;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-midnight .cws-species{margin-top:-4px;font-size:11px;color:#a9b8cc}
.cwt-midnight .cws-types{display:flex;flex-wrap:wrap;justify-content:center;gap:4px}
.cwt-midnight .cws-type{padding:2px 7px;border:1px solid #ffffff2a;border-radius:20px;background:#ffffff0d;font-size:10px;color:#dfe8f4}
.cwt-midnight .cws-meta{display:grid;gap:3px;width:100%;margin:4px 0 0;font-size:11px}
.cwt-midnight .cws-meta>div{display:flex;gap:6px;min-width:0}
.cwt-midnight .cws-meta dt{flex-shrink:0;color:#93a5bd}
.cwt-midnight .cws-meta dd{margin:0;min-width:0;color:#e6edf7;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-midnight .cws-moves{display:grid;grid-template-columns:1fr 1fr;gap:4px;width:100%;margin:2px 0 0;padding:0;list-style:none}
.cwt-midnight .cws-moves li{padding:3px 6px;border-radius:6px;background:#0b142566;font-size:10.5px;color:#d7e1ee;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

.cwt-midnight .cws-card.is-pickable{cursor:pointer}
.cwt-midnight .cws-card.is-pickable .cws-face{border-color:#9ad9a5;box-shadow:0 0 0 1px #9ad9a566,0 0 24px #9ad9a52e,0 10px 24px #02071666}
.cwt-midnight .cws-card.is-pickable:hover .cws-face{transform:translateY(-6px);box-shadow:0 0 0 1px #9ad9a5aa,0 0 32px #9ad9a544,0 16px 30px #02071677}
.cwt-midnight .cws-card:focus-visible{outline:2px solid #9ad9a5;outline-offset:3px}
.cwt-midnight .cws-card.is-chosen .cws-face{border-color:#f1d69d;background:linear-gradient(160deg,#3a3a2c,#18223a 70%);box-shadow:0 0 0 2px #e9c987,0 0 34px #e9c98744;transform:translateY(-6px)}
.cwt-midnight .cws-check{position:absolute;top:-8px;right:-8px;z-index:3;display:grid;place-items:center;width:28px;height:28px;border-radius:50%;
 background:#e9c987;color:#142034;font-weight:900;box-shadow:0 4px 12px #0007;animation:cws-pop .3s both}
.cwt-midnight .cws-card.is-blocked .cws-face{filter:grayscale(.75) brightness(.6)}
.cwt-midnight .cws-block{position:absolute;left:50%;top:40%;z-index:2;transform:translate(-50%,-50%);padding:5px 10px;border:1px solid #6a7486;border-radius:20px;
 background:#1a2233ee;font-style:normal;font-size:11px;color:#e3e9f2;white-space:nowrap;animation:cws-fade .3s both}
.cwt-midnight .cws-grid.is-dealing .cws-block,.cwt-midnight .cws-grid.is-dealing .cws-check{animation-delay:calc(var(--cws-i) * 280ms + 700ms)}
.cwt-midnight .cws-card.is-taken .cws-face{filter:brightness(.5) saturate(.55)}
.cwt-midnight .cws-card.is-taken.is-blocked .cws-block{top:auto;bottom:16px;transform:translateX(-50%)}
.cwt-midnight .cws-card.is-taken.is-pickable .cws-face{filter:none}
.cwt-midnight .cws-taken{position:absolute;left:10px;right:10px;top:34%;z-index:2;display:flex;flex-direction:column;align-items:center;gap:4px;
 padding:10px 8px;border-radius:12px;background:linear-gradient(135deg,#efd496f5,#d8b46ef5);color:#1a2030;box-shadow:0 8px 20px #0008;
 transform:rotate(-6deg);animation:cws-stamp .55s cubic-bezier(.2,1.4,.4,1) both;pointer-events:none}
.cwt-midnight .cws-grid.is-dealing .cws-taken{animation-delay:calc(var(--cws-i) * 280ms + 650ms)}
.cwt-midnight .cws-card.is-pickable .cws-taken{top:auto;bottom:62px;transform:rotate(-3deg);opacity:.92}
.cwt-midnight .cws-taken b{max-width:100%;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-midnight .cws-taken small{font-size:10px;opacity:.8}
.cwt-midnight .cws-taken-avas{display:flex}
.cwt-midnight .cws-taken-avas .cws-ava{width:28px;height:28px;margin-left:-6px;border:2px solid #fff6dd}
.cwt-midnight .cws-taken-avas .cws-ava:first-child{margin-left:0}
.cwt-midnight .cws-card.is-just .cws-face{animation:cws-flash 1.4s ease-out}
.cwt-midnight .cws-card.is-mine:after{content:'내가 빌려준 아이';position:absolute;top:-9px;left:12px;z-index:3;padding:2px 8px;border-radius:20px;
 background:#e9c987;color:#142034;font-size:10px;font-weight:700}

.cwt-midnight .cws-order{min-width:0;padding:16px;border:1px solid #49536a;border-radius:14px;background:rgba(19,30,48,.95)}
.cwt-midnight .cws-order-head{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:10px}
.cwt-midnight .cws-order-head b{font-size:12px;color:#dfe7f2}
.cwt-midnight .cws-order ol{display:grid;gap:6px;margin:0;padding:0;list-style:none}
.cwt-midnight .cws-seat{position:relative;display:flex;align-items:center;gap:8px;min-height:46px;padding:6px 10px;border-radius:10px;background:#1c283c;overflow:hidden;transition:background .3s,opacity .3s}
.cwt-midnight .cws-seat-no{width:18px;flex-shrink:0;text-align:center;font-size:11px;color:#9fb0c6;font-variant-numeric:tabular-nums}
.cwt-midnight .cws-seat-name{display:flex;align-items:center;gap:5px;flex:1;min-width:0;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-midnight .cws-seat-tag{flex-shrink:0;font-size:10px;color:#93a5bd}
.cwt-midnight .cws-seat-tag.is-now{padding:2px 7px;border-radius:20px;background:#9ad9a5;color:#143022;font-weight:700;animation:cws-beat 1.2s ease-in-out infinite}
.cwt-midnight .cws-seat-tag.is-out{color:#f0a59a}
.cwt-midnight .cws-seat-mon{flex-shrink:0;width:34px;height:34px;object-fit:contain;animation:cws-pop .45s cubic-bezier(.2,1.4,.4,1) both}
.cwt-midnight .cws-seat.is-now{background:linear-gradient(90deg,#25402f,#1c283c);box-shadow:inset 0 0 0 1px #9ad9a588}
.cwt-midnight .cws-seat.is-out{opacity:.5}
.cwt-midnight .cws-seat.is-me .cws-seat-name{color:#fff0cd}
.cwt-midnight .cws-seat-bar{position:absolute;left:0;right:0;bottom:0;height:3px;background:#9ad9a5;transform-origin:left;transition:transform .25s linear}

.cwt-midnight .cws-hero p.cws-news{display:inline-flex;align-items:center;gap:8px;max-width:100%;margin:2px 0 0;padding:4px 16px 4px 6px;border-radius:40px;
 background:#f6ecd2;color:#1b2333;font-size:13px;font-weight:600;line-height:1.4;box-shadow:0 6px 18px #0007;animation:cws-toast 4.2s ease both}
.cwt-midnight .cws-news img{width:30px;height:30px;object-fit:contain;flex-shrink:0;animation:cws-pop .4s cubic-bezier(.2,1.4,.4,1) both}
.cwt-midnight .cws-news span{min-width:0;word-break:keep-all}

.cwt-midnight.is-narrow .cws-hero{padding:20px}
.cwt-midnight.is-narrow .cws-hero h4{font-size:19px}
.cwt-midnight.is-narrow .cws-clock{width:68px;height:68px}
.cwt-midnight.is-narrow .cws-clock b{font-size:21px}
.cwt-midnight.is-narrow .cws-clock small{bottom:14px}
.cwt-midnight.is-narrow .cws-stage{grid-template-columns:minmax(0,1fr)}
.cwt-midnight.is-narrow .cws-order{order:-1;padding:12px}
.cwt-midnight.is-narrow .cws-order ol{display:flex;gap:6px;overflow-x:auto;padding-bottom:4px;scroll-snap-type:x proximity;scrollbar-width:thin}
.cwt-midnight.is-narrow .cws-seat{flex:0 0 auto;min-width:172px;max-width:200px;scroll-snap-align:start}
.cwt-midnight.is-narrow .cws-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
.cwt-midnight.is-narrow .cws-face{min-height:280px;padding:12px 8px 10px}
.cwt-midnight.is-narrow .cws-art{height:76px}
.cwt-midnight.is-narrow .cws-sprite{max-width:76px;max-height:76px}
.cwt-midnight.is-narrow .cws-moves{grid-template-columns:1fr}
.cwt-midnight.is-narrow .cws-card.is-back .cws-card-inner{height:160px}
.cwt-midnight.is-narrow .cws-mine{flex-direction:column;align-items:stretch;padding:18px}
.cwt-midnight.is-narrow .cws-mine .cwt-btn{width:100%;min-width:0}
.cwt-midnight.is-narrow .cws-mine-head,.cwt-midnight.is-narrow .cws-mine-foot{flex-direction:column;align-items:stretch}
.cwt-midnight.is-narrow .cws-opts{grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}
.cwt-midnight.is-narrow .cws-mine-mon{align-self:center}

.cwt-pop.cws-voice .cwt-pop-card{width:min(400px,100%);padding:24px 22px 20px;border-color:#9ad9a5;
 box-shadow:0 0 0 6px #9ad9a514,0 24px 70px #000c,0 0 70px #9ad9a522;animation-duration:.45s}
.cwt-pop.cws-voice .cwt-pop-card:before{border-color:#9ad9a533}
.cwt-pop.cws-voice .cwt-pop-eyebrow{color:#9ad9a5}
.cwt-pop.cws-voice .cws-voice-ic{display:block;margin:14px auto 0;font-size:40px;line-height:1;filter:drop-shadow(0 6px 12px #0008);animation:cws-hop 2.6s ease-in-out infinite}
.cwt-pop.cws-voice .cws-voice-title{margin:12px 0 6px;font-size:22px;line-height:1.35;color:#f4f8e8;word-break:keep-all}
.cwt-pop.cws-voice .cws-voice-body{margin:0;font-size:13px;line-height:1.7;color:#c4d3d6;word-break:keep-all}
.cwt-pop.cws-voice .cws-voice-clock{margin:14px 0 0;font-size:28px;line-height:1;font-weight:700;color:#e6ffd8;font-variant-numeric:tabular-nums}
.cwt-pop.cws-voice .cws-voice-clock.is-hurry{color:#ffc9bf;animation:cws-beat .5s ease-in-out infinite}
.cwt-pop.cws-voice .cws-voice-wait{display:inline-flex;gap:7px;margin-top:16px}
.cwt-pop.cws-voice .cws-voice-wait i{width:8px;height:8px;border-radius:50%;background:#9ad9a5;animation:cws-dot 1.2s ease-in-out infinite}
.cwt-pop.cws-voice .cws-voice-wait i:nth-child(2){animation-delay:.15s}
.cwt-pop.cws-voice .cws-voice-wait i:nth-child(3){animation-delay:.3s}
.cwt-pop.cws-voice .cws-voice-btns{display:flex;gap:10px;justify-content:center;flex-wrap:wrap;margin-top:18px}
.cwt-pop.cws-voice .cws-voice-btns .cwt-btn{min-height:44px;min-width:124px;flex:1 1 auto}
.cwt-pop.cws-voice .cws-voice-btns .cwt-btn.is-ghost{flex:0 1 auto;min-width:96px}
.cwt-pop.cws-voice .cwt-pop-close{min-width:160px;flex:0 1 auto}

@keyframes cws-fade{from{opacity:0}to{opacity:1}}
@keyframes cws-rise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@keyframes cws-pop{from{opacity:0;transform:scale(.6)}to{opacity:1;transform:scale(1)}}
@keyframes cws-beat{0%,100%{transform:scale(1)}50%{transform:scale(1.12)}}
@keyframes cws-hop{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
@keyframes cws-deal{from{opacity:0;transform:translateY(-26px) rotate(-5deg) scale(.9)}to{opacity:1;transform:none}}
@keyframes cws-glow{0%,100%{box-shadow:0 0 16px #9ad9a533}50%{box-shadow:0 0 34px #9ad9a588}}
@keyframes cws-flip{0%{transform:rotateY(180deg)}50%{transform:rotateY(90deg) translateY(-14px) scale(1.04)}100%{transform:rotateY(0)}}
@keyframes cws-stamp{0%{opacity:0;transform:rotate(-6deg) scale(1.9)}60%{opacity:1;transform:rotate(-6deg) scale(.94)}100%{opacity:1;transform:rotate(-6deg) scale(1)}}
@keyframes cws-flash{0%{box-shadow:0 0 0 3px #f6ecd2,0 0 60px #e9c987cc}100%{box-shadow:0 10px 24px #02071666}}
@keyframes cws-dot{0%,80%,100%{opacity:.25;transform:translateY(0)}40%{opacity:1;transform:translateY(-3px)}}
@keyframes cws-twinkle{0%,100%{opacity:.5;transform:scale(.9)}50%{opacity:1;transform:scale(1.25)}}
@keyframes cws-toast{0%{opacity:0;transform:translateY(8px) scale(.96)}8%{opacity:1;transform:none}88%{opacity:1;transform:none}100%{opacity:0;transform:translateY(-4px)}}
`;

/* ---- 마왕토벌전 (2026-10-12) ---- */
const CWR_CSS = `
.cwt-midnight .cwt-badge.is-raid{color:#ffd9d2;border-color:#b06a6a;background:#3a1f26}
.cwt-midnight .cwt-lobby.is-raid{border-color:#8f6b6c}
.cwt-midnight .cwt-lobby.is-raid .cwt-lobby-intro{background:radial-gradient(ellipse at 75% 100%,#e0747433,transparent 70%),linear-gradient(130deg,#3a2436,#121e30)}
.cwt-midnight .cwt-lobby.is-raid .cwt-lobby-intro h4{color:#ffe6df}
.cwt-midnight .cwt-guide.is-raid{border-color:#7a5c62}
.cwt-midnight .cwt-guide.is-raid h4{color:#f6d6d6}

.cwt-midnight .cwr{display:grid;gap:18px;margin-bottom:22px}
.cwt-midnight .cwr-stage{position:relative;padding:22px 24px;border:1px solid #6a5468;border-radius:16px;overflow:hidden;
 background:radial-gradient(ellipse at 10% 120%,#e0747433,transparent 60%),radial-gradient(ellipse at 90% -20%,#e9c9872b,transparent 55%),linear-gradient(120deg,#2a1f33,#132036)}
.cwt-midnight .cwr-stage.is-flip{border-color:#e9c987;box-shadow:0 0 0 1px #e9c98755,0 0 40px #e9c98722}
.cwt-midnight .cwr-stage.is-battle{border-color:#9ad9a5}
.cwt-midnight .cwr-stage-head{text-align:center;margin-bottom:14px}
.cwt-midnight .cwr-stage-head h4{margin:8px 0 4px;font-size:24px;color:#fff0cd;animation:cws-rise .45s cubic-bezier(.2,.8,.2,1) both}
.cwt-midnight .cwr-stage-head p{margin:0;font-size:13px;color:#c4d3d6}
.cwt-midnight .cwr-stage-head p b{color:#ffe6df}
.cwt-midnight .cwr-vs{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:16px}
.cwt-midnight .cwr-vs-mid{display:grid;justify-items:center;gap:10px;min-width:96px}
.cwt-midnight .cwr-vs-mid .cwt-final-vs{font-size:28px;color:#e9c987;letter-spacing:.1em}
.cwt-midnight .cwr-alive{font-size:13px;color:#c4d3d6;font-variant-numeric:tabular-nums}
.cwt-midnight .cwr-fighter{display:grid;gap:6px;min-width:0;justify-items:center;text-align:center}
.cwt-midnight .cwr-fighter-card{display:grid;justify-items:center;gap:6px;padding:14px 18px;border-radius:14px;width:min(100%,300px);min-height:150px;align-content:center;
 border:1px solid #4a5a70;background:rgba(19,30,48,.9);animation:cws-rise .4s both}
.cwt-midnight .cwr-fighter.is-boss .cwr-fighter-card{border-color:#b06a6a;background:linear-gradient(160deg,#3a2430,#17273d)}
.cwt-midnight .cwr-fighter.is-raider .cwr-fighter-card{border-color:#6f8f6c;background:linear-gradient(160deg,#24402f,#17273d)}
.cwt-midnight .cwr-fighter.is-mine .cwr-fighter-card{box-shadow:0 0 0 2px #e9c98788}
.cwt-midnight .cwr-fighter-card.is-unknown{opacity:.6;border-style:dashed}
.cwt-midnight .cwr-fighter-card .cws-ava{width:56px;height:56px}
.cwt-midnight .cwr-fighter-card b{font-size:17px;color:#f6fbef;word-break:keep-all}
.cwt-midnight .cwr-fighter-card small{font-size:11px;color:#e9c987}
.cwt-midnight .cwr-stage.is-flip .cwr-fighter-card{animation:cwr-flip .6s cubic-bezier(.2,.8,.2,1) both}
@keyframes cwr-flip{0%{transform:rotateY(90deg) scale(.9);opacity:0}100%{transform:none;opacity:1}}

.cwt-midnight .cwr-mons{display:flex;gap:6px;flex-wrap:wrap}
.cwt-midnight .cwr-fighter-card .cwr-mons{justify-content:center;margin-top:4px}
.cwt-midnight .cwr-mon{position:relative;display:grid;justify-items:center;gap:2px;width:50px;padding:3px 2px 4px;border-radius:9px;
 background:#ffffff10;border:1px solid #ffffff22}
.cwt-midnight .cwr-mon img{width:38px;height:38px;object-fit:contain;filter:drop-shadow(0 2px 3px #0008)}
.cwt-midnight .cwr-mon i{display:grid;place-items:center;width:38px;height:38px;font-style:normal;font-size:20px;color:#b5c6c8}
.cwt-midnight .cwr-mon.is-hidden{opacity:.55;border-style:dashed}
.cwt-midnight .cwr-mon.is-out img{filter:grayscale(1) brightness(.5)}
.cwt-midnight .cwr-hp{display:block;width:100%;height:5px;border-radius:3px;background:#ffffff22;overflow:hidden}
.cwt-midnight .cwr-hp i{display:block;height:100%;background:#9ad9a5;transition:width .4s}
.cwt-midnight .cwr-mon.is-mid .cwr-hp i{background:#e9c987}
.cwt-midnight .cwr-mon.is-low .cwr-hp i{background:#f08a7e}
/* 6마리판 (2026-11-01) — 마왕 카드 한 줄에 여섯 칸이 들어가게 칩을 줄인다 */
.cwt-midnight .cwr.is-six .cwr-mons{gap:4px}
.cwt-midnight .cwr.is-six .cwr-mon{width:38px}
.cwt-midnight .cwr.is-six .cwr-mon img,.cwt-midnight .cwr.is-six .cwr-mon i{width:30px;height:30px}
.cwt-midnight .cwr-st{position:absolute;right:-4px;top:-6px;font-style:normal;font-size:9px;padding:1px 5px;border-radius:10px;background:#e9c987;color:#2a1f33;font-weight:700}
.cwt-midnight .cwr-st.is-par{background:#f1d86f}.cwt-midnight .cwr-st.is-brn{background:#f09a6a}.cwt-midnight .cwr-st.is-psn,.cwt-midnight .cwr-st.is-tox{background:#c88ad8}
.cwt-midnight .cwr-st.is-slp{background:#b7bfd6}.cwt-midnight .cwr-st.is-frz{background:#9ad8e8}.cwt-midnight .cwr-st.is-out{background:#5a5a66;color:#eee}

.cwt-midnight .cwr-teams{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.cwt-midnight.is-narrow .cwr-teams{grid-template-columns:1fr}
.cwt-midnight.is-narrow .cwr-vs{grid-template-columns:1fr;justify-items:center}
.cwt-midnight.is-narrow .cwr-vs-mid{order:-1}
.cwt-midnight.is-narrow .cwr-fighter-card{width:100%}
.cwt-midnight .cwr-team{padding:14px 14px 16px;border:1px solid #4a5a70;border-radius:14px;background:rgba(19,30,48,.9)}
.cwt-midnight .cwr-team.is-boss{border-color:#8f5a66}
.cwt-midnight .cwr-team.is-raider{border-color:#5c7a62}
.cwt-midnight .cwr-team-head{margin-bottom:10px}
.cwt-midnight .cwr-team-head b{display:block;font-size:20px;color:#f6fbef;margin:4px 0 2px}
.cwt-midnight .cwr-team-head small{font-size:12px;color:#b5c6c8}
.cwt-midnight .cwr-team-head p{margin:0;font-size:12px;color:#b5c6c8;line-height:1.6}
.cwt-midnight .cwr-list{display:grid;gap:8px}
.cwt-midnight .cwr-card{font:inherit;text-align:left;position:relative;display:flex;align-items:flex-start;gap:10px;padding:9px 10px;border-radius:12px;
 border:1px solid #3e4b60;background:#1a2538;color:#edf1fc;transition:transform .18s,border-color .18s,opacity .18s}
.cwt-midnight .cwr-card.is-clickable{cursor:pointer}
.cwt-midnight .cwr-card.is-clickable:hover{transform:translateY(-1px);border-color:#e9c987}
.cwt-midnight .cwr-card.is-out{opacity:.45;filter:grayscale(.6)}
.cwt-midnight .cwr-card.is-standing{border-color:#9ad9a5;box-shadow:0 0 0 1px #9ad9a544;background:#1f3030}
.cwt-midnight .cwr-card.is-boss.is-standing{border-color:#f0a0a0;box-shadow:0 0 0 1px #f0a0a044;background:#3a2430}
.cwt-midnight .cwr-card.is-next{border-color:#e9c987;box-shadow:0 0 0 1px #e9c98766}
.cwt-midnight .cwr-card.is-mine{outline:2px dashed #e9c98788;outline-offset:1px}
.cwt-midnight .cwr-card.is-named{border-color:#e9c987;background:#39311f}
.cwt-midnight .cwr-card.is-voted{border-color:#9ad9a5;background:#22382d}
.cwt-midnight .cwr-card .cws-ava{width:34px;height:34px;flex-shrink:0}
.cwt-midnight .cwr-card-main{flex:1;min-width:0;display:grid;gap:5px}
.cwt-midnight .cwr-card-head{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.cwt-midnight .cwr-name{font-weight:700;font-size:14px;max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-midnight .cwr-tag{font-style:normal;font-size:10px;padding:1px 7px;border-radius:10px;background:#ffffff18;color:#dfe6f2}
.cwt-midnight .cwr-tag.is-leader{background:#e9c987;color:#2a1f33;font-weight:700}
.cwt-midnight .cwr-tag.is-standing{background:#9ad9a5;color:#143022;font-weight:700}
.cwt-midnight .cwr-card.is-boss .cwr-tag.is-standing{background:#f0a0a0;color:#3a1a1a}
.cwt-midnight .cwr-tag.is-next{background:#fff0cd;color:#2a1f33;font-weight:700;animation:cws-pop .35s both}
.cwt-midnight .cwr-tag.is-out{background:#5a5a66}
.cwt-midnight .cwr-card-sub{display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-height:14px}
.cwt-midnight .cwr-card-sub:empty{display:none}
.cwt-midnight .cwr-wins{font-size:11px;color:#e9c987;letter-spacing:.1em}
.cwt-midnight .cwr-wins small{margin-left:4px;letter-spacing:0;color:#fff0cd}
.cwt-midnight .cwr-votes{font-size:11px;padding:1px 7px;border-radius:10px;background:#9ad9a5;color:#143022;font-weight:700}
.cwt-midnight .cwr-hand{display:inline-flex;align-items:center;gap:3px;font-size:13px}
.cwt-midnight .cwr-hand img{width:26px;height:26px;object-fit:contain}
.cwt-midnight .cwr-card .cws-check{right:10px;top:10px}
.cwt-midnight .cwr-intent{display:inline-flex;align-items:center;gap:4px;padding:2px 8px 2px 4px;border-radius:12px;background:#ffffff12;font-size:11px;color:#dfe6f2}
.cwt-midnight .cwr-intent img{width:26px;height:26px;object-fit:contain}
.cwt-midnight .cwr-intent i{font-style:normal;font-size:13px}
.cwt-midnight .cwr-intent small{font-size:11px;max-width:90px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-midnight .cwr-intent.is-default{opacity:.6}
.cwt-midnight .cwr-intent.is-hand{background:#9ad9a533;color:#e6ffd8}
.cwt-midnight .cwr-intent-line{display:flex;align-items:center;gap:8px;margin-top:8px!important;font-size:13px}
.cwt-midnight .cwr-intent-line img{width:34px;height:34px;object-fit:contain}
.cwt-midnight .cwr-intent-line b{color:#fff0cd}
.cwt-midnight .cwr-intent-line span{color:#b5c6c8;font-size:12px}
.cwt-midnight .cwr-hand-btn{align-self:center;min-height:44px}

.cwt-midnight .cwr-mine{align-items:flex-start;flex-wrap:wrap}
.cwt-midnight .cwr-picks{display:flex;gap:8px;flex-wrap:wrap;flex-basis:100%}
.cwt-midnight .cwr-picks .cwt-slot{width:84px;background:#202e43;border-color:#4b5970;color:#edf1fc}
.cwt-midnight .cwr-picks .cwt-slot.is-on{border-color:#e9c987}
.cwt-midnight .cwr-picks .cwt-slot .cwr-hp{margin-top:3px}
.cwt-midnight .cwr-picks .cwt-slot-order{color:#e9c987;font-size:10px}

.cwt-midnight .cwr-reveal-wrap{position:relative;min-height:320px;border:1px solid #6a5468;border-radius:16px;overflow:hidden;
 background:radial-gradient(ellipse at 50% 120%,#e0747433,transparent 60%),linear-gradient(120deg,#2a1f33,#132036)}
.cwt-midnight .cwr-reveal{position:absolute;inset:0;display:grid;place-content:center;justify-items:center;gap:10px;padding:20px;text-align:center}
.cwt-midnight .cwr-reveal .cwt-reveal-card{display:grid;justify-items:center;gap:8px;padding:18px 26px;border-radius:16px;background:#1a2538cc;border:1px solid #4a5a70;animation:cws-rise .45s both}
.cwt-midnight .cwr-reveal .cwt-reveal-card.is-boss{border-color:#f0a0a0}
.cwt-midnight .cwr-reveal .cwt-reveal-card.is-raider{border-color:#9ad9a5}
.cwt-midnight .cwr-reveal .cwt-reveal-card b{font-size:22px;color:#fff0cd}
.cwt-midnight .cwr-reveal-role{font-size:12px;color:#c4d3d6}
.cwt-midnight .cwr-reveal .cwt-reveal-face{width:96px;height:96px;border-radius:50%;object-fit:cover}
.cwt-midnight .cwr-reveal .cwt-reveal-progress{width:min(360px,80%)}

.cwt-midnight .cwr-award .cwr-award-row{display:flex;gap:10px;flex-wrap:wrap;justify-content:center;margin:10px 0}
.cwt-midnight .cwr-award .cwt-champ-card{padding:8px 14px}
`;

/* 빙글빙글컵 (2026-10-13) — 순위표 + 이번 라운드. 「종이와 도장」 토큰을 그대로 쓴다(승마다 도장 하나). */
/**
 * 행운의 자리 룰렛 · 종료 순위표 · 리본 상점 (2026-10-14). 대회장 미드나이트 위에 **리본 분홍**을 한 색 더한다.
 * 띠(reel)는 칸을 절대 위치로 늘어놓고 트랙 하나를 translateX 로 움직인다 — 도는 중은 CSS 무한 애니, 멈춤·밀기는 transition.
 * 줄인 동작 설정이면 대회장 전체 규칙이 함께 끈다(최종 자리만 보인다).
 */
const CWT_REWARDS_CSS = `
.cwt-midnight .cwt-roulette-summary{display:flex;align-items:center;gap:12px;padding:10px 14px;margin:0 0 12px;border-radius:12px;border:1px solid #b49a5d66;background:#142137e8;font-family:'Pretendard','Malgun Gothic',sans-serif}
.cwt-midnight .cwt-roulette-summary>img{width:32px;height:40px;object-fit:contain}.cwt-midnight .cwt-roulette-summary>div{flex:1;min-width:0}.cwt-midnight .cwt-roulette-summary span{display:block;color:#bda885;font-size:10px;line-height:1.5}.cwt-midnight .cwt-roulette-summary b{font-size:16px;font-weight:650;color:#ffedcc}.cwt-midnight .cwt-roulette-summary .cwt-btn{min-height:34px;padding:0 10px;font-size:11px}
.cwt-root.cwt-midnight .cwt-roulette-audio{display:flex;justify-content:flex-end;margin:8px 0}.cwt-root.cwt-midnight .cwt-roulette-audio .cwt-music{display:block;grid-column:auto;margin:0;padding:0;border:0}.cwt-root.cwt-midnight .cwt-roulette-audio .cwt-music button{min-height:30px;font-size:11px;padding:5px 12px;border:1px solid #7b6783;background:#243047;color:#e4cddd;border-radius:7px;cursor:pointer}
${CWROUL_CSS}

/* 최종 순위표 */
.cwt-midnight .cwt-standings{margin:0 0 14px;padding:18px;border-radius:14px;border:1px solid #49536a;background:rgba(19,30,48,.95)}
.cwt-midnight .cwt-standings-head{display:flex;justify-content:space-between;align-items:flex-end;gap:10px;flex-wrap:wrap;margin-bottom:10px}
.cwt-midnight .cwt-standings-head h4{margin:6px 0 0;font-size:19px;color:#fff0cd}
.cwt-midnight .cwt-standings-note{font-size:12px;padding:5px 10px;border-radius:20px;border:1px solid #867655;color:#e9d3a1}
.cwt-midnight .cwt-standings-note.is-paid{background:#f3a7c3;border-color:#f3a7c3;color:#3a1a28;font-weight:700}
.cwt-midnight .cwt-standings-list{list-style:none;margin:0;padding:0;display:grid;gap:4px}
.cwt-midnight .cwt-standings-list li{display:grid;grid-template-columns:44px 30px 1fr auto auto;align-items:center;gap:10px;padding:7px 10px;border-radius:10px;background:#1b2840;border:1px solid transparent}
.cwt-midnight .cwt-standings-list li.is-champ{border-color:#e9c987}
.cwt-midnight .cwt-standings-list li.is-me{border-color:var(--cwl-pink,#f3a7c3);background:#2a2740}
.cwt-midnight .cwt-standings-list .cwt-ava{width:30px;height:30px}
.cwt-midnight .cwt-standings-rank{font-size:13px;color:#efdaa9;font-weight:700}
.cwt-midnight .cwt-standings-name{font-size:14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-midnight .cwt-standings-name small{font-size:10px;color:#f3a7c3;margin-left:4px}
.cwt-midnight .cwt-standings-wins{font-size:12px;color:#9aa6b8}
.cwt-midnight .cwt-standings-ribbons{font-size:13px;color:#ffd9e6;min-width:52px;text-align:right}
.cwt-midnight .cwt-standings-mine{margin:10px 0 0;font-size:13px;color:#c0cde0}
.cwt-midnight .cwt-standings-mine b{color:#ffd9e6;font-size:15px}
.cwt-midnight.is-narrow .cwt-standings-list li{grid-template-columns:38px 26px 1fr auto;gap:6px}
.cwt-midnight.is-narrow .cwt-standings-wins{display:none}

/* cherish ribbon shop — generated gift chests, private contents */
.cwt-midnight .cwl-shop,.cwt-midnight .cwt-shop-status{font-family:'Pretendard','Malgun Gothic',sans-serif;font-size:13px;font-weight:400;letter-spacing:-.02em;line-height:1.6;-webkit-font-smoothing:antialiased}
.cwt-midnight .cwl-shop button{font-family:inherit}
.cwt-midnight .cwt-shop-status{display:flex;align-items:center;gap:16px;padding:16px 22px;border:1px solid #66758a66;border-radius:14px;background:#142137d9;margin-bottom:18px}
.cwt-midnight .cwt-shop-status img{width:42px;height:52px;object-fit:contain}
.cwt-midnight .cwt-shop-status h3{margin:0 0 5px;font-size:16px;color:#f1dcae}
.cwt-midnight .cwt-shop-status p{margin:0;font-size:12px;line-height:1.7;color:#aebed5}
.cwt-midnight .cwl-shop{position:relative;margin:14px 0 0;padding:22px;border:1px solid #8c708361;border-radius:16px;background:radial-gradient(ellipse at 12% 0,#b97a9e17,transparent 55%),linear-gradient(145deg,#192438fa,#101b2cfb);box-shadow:0 16px 50px #0003;overflow:hidden}
.cwt-midnight .cwl-shop-head{display:flex;justify-content:space-between;align-items:center;gap:20px;flex-wrap:wrap;margin-bottom:12px}
.cwt-midnight .cwl-shop-head h4{margin:7px 0 5px;font-family:inherit;font-size:23px;font-weight:650;letter-spacing:-.04em;color:#fff0e0}
.cwt-midnight .cwl-shop .cwl-shop-tagline{margin:0;color:#c5adc1;font-size:13px;line-height:1.7}
.cwt-midnight .cwl-shop .cwl-shop-note{margin:0 0 16px;color:#8fa2bc;font-size:12px;line-height:1.8}
.cwt-midnight .cwl-wallet{display:flex;align-items:baseline;gap:7px;padding:14px 18px;border-radius:12px;background:#302539b3;border:1px solid #b88ba77a;flex-wrap:wrap}
.cwt-midnight .cwl-wallet small{font-size:11px;color:#ddadc8}.cwt-midnight .cwl-wallet b{font-size:24px;color:#fff0f7;font-variant-numeric:tabular-nums}.cwt-midnight .cwl-wallet span{font-size:12px;color:#d8c4cf}.cwt-midnight .cwl-wallet em{flex-basis:100%;font-style:normal;font-size:10px;color:#ad90a5}
.cwt-midnight .cwl-shop .cwl-boxes{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-top:12px}
.cwt-midnight .cwl-shop .cwl-box{--box-accent:#e0b3c5;position:relative;display:flex;flex-direction:column;align-items:stretch;gap:7px;padding:14px 14px 12px;border-radius:12px;min-width:0;border:1px solid #87637670;background:radial-gradient(ellipse at 50% 27%,#e0b3c51a,transparent 68%),linear-gradient(160deg,#29273a,#192437);box-shadow:inset 0 1px #ffffff08;overflow:hidden;text-align:center}
.cwt-midnight .cwl-shop .cwl-box.is-tier-2{--box-accent:#b9cbe1;border-color:#8096b166;background:radial-gradient(ellipse at 50% 27%,#b9cbe11c,transparent 68%),linear-gradient(160deg,#253247,#192437)}
.cwt-midnight .cwl-shop .cwl-box.is-tier-3{--box-accent:#e5ca8c;border-color:#b3915577;background:radial-gradient(ellipse at 50% 27%,#e5ca8c22,transparent 68%),linear-gradient(160deg,#34313b,#22283b)}
.cwt-midnight .cwl-shop .cwl-box-edition{font-size:9px;letter-spacing:3px;color:var(--box-accent);text-align:left}
.cwt-midnight .cwl-shop .cwl-box-art{position:relative;display:flex;align-items:center;justify-content:center;width:100%;height:140px;margin:0;background:none;border:0}
.cwt-midnight .cwl-shop .cwl-box-art::after{content:'';position:absolute;bottom:16px;left:12%;right:12%;height:12px;border-radius:50%;background:#050b1680;filter:blur(7px)}
.cwt-midnight .cwl-shop .cwl-box-art img{position:relative;z-index:1;width:100%;height:100%;max-width:170px;object-fit:contain;filter:drop-shadow(0 6px 12px #0004);transition:transform .3s}
.cwt-midnight .cwl-shop .cwl-box:hover .cwl-box-art img{transform:translateY(-4px)}
.cwt-midnight .cwl-shop .cwl-box h5{margin:0;font-family:inherit;font-size:15px;font-weight:600;line-height:1.5;color:#fff1e7;letter-spacing:-.03em}
.cwt-midnight .cwl-shop .cwl-box-caption{margin:0 0 7px;font-size:11px;line-height:1.7;color:#b1acc1}
.cwt-midnight .cwl-shop .cwl-open{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:auto;padding:11px 14px;min-height:44px;white-space:nowrap;border:1px solid #e3caa4;background:linear-gradient(115deg,#f2ddb5,#d6b573);color:#302a25;border-radius:8px;font-size:12px;font-weight:700}
.cwt-midnight .cwl-shop .cwl-open:disabled{opacity:.5;cursor:not-allowed}.cwt-midnight .cwl-shop .cwl-open:focus-visible{outline:3px solid #f4bad5;outline-offset:3px}.cwt-midnight .cwl-shop .cwl-open:not(:disabled):hover{filter:brightness(1.08)}
.cwt-midnight .cwl-shop .cwl-short{font-size:10px;line-height:1.6;color:#b19eb2;text-align:center;min-height:16px}
.cwt-midnight .cwl-box-result{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:14px;margin:10px 0 20px;padding:18px;border-radius:12px;background:linear-gradient(115deg,#493047,#263247);border:1px solid #d5a4bf;animation:cwt-in .45s ease-out both}
.cwt-midnight .cwl-box-result img{width:40px;height:54px;object-fit:contain}.cwt-midnight .cwl-box-result small{display:block;font-size:11px;color:#efbbd7}.cwt-midnight .cwl-box-result b{display:block;font-size:19px;color:#fff}.cwt-midnight .cwl-box-result span{display:block;font-size:12px;color:#d8c4cf;margin-top:2px}
@media(max-width:720px){.cwt-midnight .cwl-shop{padding:20px 14px}.cwt-midnight .cwl-shop-head{gap:16px}.cwt-midnight .cwl-shop-head h4{font-size:21px}.cwt-midnight .cwl-wallet{padding:10px 14px}.cwt-midnight .cwl-shop .cwl-boxes{grid-template-columns:1fr;gap:12px}.cwt-midnight .cwl-shop .cwl-box{display:grid;grid-template-columns:92px minmax(0,1fr);gap:5px 12px;padding:16px 12px;text-align:left;align-content:center}.cwt-midnight .cwl-shop .cwl-box-art{grid-column:1;grid-row:1 / 6;height:118px;align-self:center}.cwt-midnight .cwl-shop .cwl-box-edition,.cwt-midnight .cwl-shop .cwl-box h5,.cwt-midnight .cwl-shop .cwl-box-caption,.cwt-midnight .cwl-shop .cwl-open,.cwt-midnight .cwl-shop .cwl-short{grid-column:2}.cwt-midnight .cwl-shop .cwl-box-edition{font-size:8px;letter-spacing:2px}.cwt-midnight .cwl-shop .cwl-box h5{font-size:15px}.cwt-midnight .cwl-shop .cwl-box-caption{margin:0 0 5px;font-size:10px}.cwt-midnight .cwl-shop .cwl-open{padding:9px 10px;font-size:11px}.cwt-midnight .cwl-shop .cwl-short{text-align:left}.cwt-midnight .cwt-shop-status{padding:12px 16px}}
@media(max-width:360px){.cwt-midnight .cwl-shop .cwl-box{grid-template-columns:80px minmax(0,1fr);gap:5px 9px}.cwt-midnight .cwl-shop .cwl-box-art{height:110px}.cwt-midnight .cwl-shop .cwl-open{padding:9px 7px;font-size:10px}}
@media(prefers-reduced-motion:reduce){.cwt-midnight .cwl-shop .cwl-box-art img{transition:none;transform:none!important}}

.cwt-midnight .cwl-ledger{margin-top:12px;font-size:13px;color:#c9b6c1}
.cwt-midnight .cwl-ledger summary{cursor:pointer}
.cwt-midnight .cwl-ledger ul{list-style:none;margin:8px 0 0;padding:0;display:grid;gap:4px}
.cwt-midnight .cwl-ledger li{display:grid;grid-template-columns:84px 56px 1fr;gap:8px;font-size:12px}
.cwt-midnight .cwl-ledger b.is-plus{color:#ffd9e6}
.cwt-midnight .cwl-ledger b.is-minus{color:#9aa6b8}
.cwt-midnight.is-narrow .cwl{--cwl-w:78px}
.cwt-midnight.is-narrow .cwl-boxes{grid-template-columns:1fr}
.cwt-midnight.is-narrow .cwl-box-result{grid-template-columns:auto 1fr}
.cwt-midnight.is-narrow .cwl-box-result .cwt-btn{grid-column:1 / -1}
`;

const CWX_CSS = `
.cwt-midnight .cwt-badge.is-swiss{color:#d8f0ff;border-color:#6a9ab8;background:#1f3344}
.cwt-midnight .cwt-lobby.is-swiss{border-color:#6a9ab8}
.cwt-midnight .cwt-lobby.is-swiss .cwt-lobby-intro{background:radial-gradient(ellipse at 75% 100%,#6ab8e833,transparent 70%),linear-gradient(130deg,#1f3344,#121e30)}
.cwt-midnight .cwt-lobby.is-swiss .cwt-lobby-intro h4{color:#e3f3ff}
.cwt-midnight .cwt-guide.is-swiss{border-color:#4f7590}
.cwt-midnight .cwt-guide.is-swiss h4{color:#d8f0ff}

.cwt-midnight .cwx{display:grid;gap:18px}
.cwt-midnight .cwx-round{padding:16px 18px;border:1px solid #4a5a70;border-radius:14px;background:rgba(19,30,48,.9)}
.cwt-midnight .cwx-round-head{display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin-bottom:10px}
.cwt-midnight .cwx-round-head h4{margin:0;font-size:20px;color:#fff0cd}
.cwt-midnight .cwx-round-head small{font-size:12px;color:#b5c6c8}
.cwt-midnight .cwx .cwt-feed-list{display:grid;gap:8px;grid-template-columns:repeat(auto-fill,minmax(300px,1fr))}
.cwt-midnight .cwx.is-narrow .cwt-feed-list{grid-template-columns:1fr}

.cwt-midnight .cwx-table-wrap{padding:16px 18px;border:1px solid #4a5a70;border-radius:14px;background:rgba(19,30,48,.9);overflow-x:auto}
.cwt-midnight .cwx-table-head{margin-bottom:10px}
.cwt-midnight .cwx-table-head h4{margin:4px 0 2px;font-size:20px;color:#fff0cd}
.cwt-midnight .cwx-table-head small{display:block;font-size:12px;color:#b5c6c8;line-height:1.6}
.cwt-midnight .cwx-table{width:100%;border-collapse:collapse;font-size:14px;color:#edf1fc}
.cwt-midnight .cwx-table th{font-weight:600;font-size:11px;letter-spacing:.08em;color:#b5c6c8;text-align:left;padding:4px 8px;border-bottom:1px solid #3e4b60}
.cwt-midnight .cwx-table td{padding:7px 8px;border-bottom:1px solid #2a3648;vertical-align:middle;white-space:nowrap}
.cwt-midnight .cwx-row.is-mine td{background:#e9c98714}
.cwt-midnight .cwx-row.is-mine .cwt-name{color:#fff0cd}
.cwt-midnight .cwx-row.is-excluded td{opacity:.45}
.cwt-midnight .cwx-row.is-finalist .cwx-rank{color:#e9c987}
.cwt-midnight .cwx-rank{font-weight:700;font-variant-numeric:tabular-nums;width:44px}
.cwt-midnight .cwx-rank small{margin-right:2px;color:#b5c6c8;font-weight:400}
.cwt-midnight .cwx-row.is-top1 .cwx-rank{color:#ffd86b}
.cwt-midnight .cwx-row.is-top2 .cwx-rank{color:#d9dde7}
.cwt-midnight .cwx-row.is-top3 .cwx-rank{color:#e0a776}
.cwt-midnight .cwx-who{display:flex;align-items:center;gap:8px;min-width:0;max-width:260px}
.cwt-midnight .cwx-who .cwt-ava{width:28px;height:28px}
.cwt-midnight .cwx-who .cwt-name{max-width:150px}
.cwt-midnight .cwx-who .cwt-stamps{margin-left:4px}
.cwt-midnight .cwx-rec{font-variant-numeric:tabular-nums}
.cwt-midnight .cwx-rec b{color:#fff0cd}
.cwt-midnight .cwx-rec small{color:#b5c6c8}
.cwt-midnight .cwx-opp{font-variant-numeric:tabular-nums;color:#c4d3d6}
.cwt-midnight .cwx-st{display:inline-block;font-size:12px;padding:2px 9px;border-radius:10px;background:#ffffff14;color:#dfe6f2}
.cwt-midnight .cwx-st.is-live{background:#9ad9a5;color:#143022;font-weight:700;animation:cwt-pulse 1.6s ease-in-out infinite}
.cwt-midnight .cwx-st.is-calling{background:#e9c987;color:#2a1f33}
.cwt-midnight .cwx-st.is-won{background:#e9c987;color:#2a1f33;font-weight:700}
.cwt-midnight .cwx-st.is-lost{background:#5a5a66;color:#eee}
.cwt-midnight .cwx-st.is-bye{background:#ffffff22}
.cwt-midnight .cwx-st.is-out{background:#3a2430;color:#f0a0a0}
.cwt-midnight .cwx-st.is-dim{background:none;color:#5a6a7a}
.cwt-midnight .cwx.is-narrow .cwx-table th:nth-child(4),.cwt-midnight .cwx.is-narrow .cwx-table td:nth-child(4){display:none}
.cwt-midnight .cwx.is-narrow .cwx-table td{padding:6px 5px;font-size:13px}
.cwt-midnight .cwx.is-narrow .cwx-who{max-width:150px}
.cwt-midnight .cwx.is-narrow .cwx-who .cwt-name{max-width:90px}
.cwt-midnight .cwx-next{display:grid;gap:8px;justify-items:center;margin:12px auto 4px;padding:12px 16px;max-width:460px;border-radius:14px;
 border:1px solid #6a9ab8;background:linear-gradient(130deg,#1f3344cc,#121e30cc)}
.cwt-midnight .cwx-next.is-bye{border-style:dashed;text-align:center}
.cwt-midnight .cwx-next.is-bye b{color:#fff0cd;font-size:16px}
.cwt-midnight .cwx-next.is-bye small{color:#b5c6c8;font-size:12px}
.cwt-midnight .cwx-next-card{display:flex;align-items:center;gap:12px;width:100%;animation:cws-rise .4s both}
.cwt-midnight .cwx-next-card .cwt-ava{width:48px;height:48px;flex-shrink:0}
.cwt-midnight .cwx-next-who{display:grid;gap:3px;min-width:0;flex:1}
.cwt-midnight .cwx-next-who b{font-size:18px;color:#fff0cd;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cwt-midnight .cwx-next-who small{font-size:12px;color:#c4d3d6}
.cwt-midnight .cwx-next-vs{display:grid;justify-items:center;gap:2px;flex-shrink:0}
.cwt-midnight .cwx-next-vs em{font-style:normal;font-size:18px;letter-spacing:.1em;color:#e9c987}
.cwt-midnight .cwx-next-vs small{font-size:11px;color:#b5c6c8;white-space:nowrap}
.cwt-midnight .cwx-next-hint{font-size:12px;color:#b5c6c8;text-align:center}
.cwt-midnight.is-narrow .cwx-next-vs small{display:none}
.cwt-root .cwt-stale{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin:0 0 12px;padding:10px 14px;border-radius:12px;
 border:1px solid #e9c987;background:#39311f;color:#fff0cd;font-size:13px}
.cwt-root .cwt-stale b{color:#ffd86b}
.cwt-root .cwt-stale .cwt-btn{min-height:34px;background:#e9c987;color:#2a1f33;border-color:#e9c987}
.cwt-midnight .cwx-podium-line{display:flex;gap:14px;flex-wrap:wrap;justify-content:center;margin:6px 0 10px;font-size:13px}
.cwt-midnight .cwx-podium-line span{display:inline-flex;gap:6px;align-items:baseline}
.cwt-midnight .cwx-podium-line small{color:#b5c6c8}
.cwt-midnight .cwx-podium-line b{color:#fff0cd}
`;
